/**
 * Aspect — PlanEditor (deterministic 2D trace-to-edit over the uploaded render)
 * --------------------------------------------------------------
 * Opens a large editor that overlays the ORIGINAL 2D render with an editable
 * vector layer (walls = edges between corner nodes, doors/windows = markers on
 * an edge). The user traces the drawing 1:1 with Blender-like tools (move, add
 * On Apply, furniture/openings/topology patch the live scene in place. Full room
 * rebuild only happens when re-uploading a 2D render or using the AI prompt bar.
 * with the render; a calibrated meters-per-pixel converts to real meters.
 *
 * DOM lives only in the overlay layer (this module + overlay.js + minimap.js).
 */

import { fetchActivePlan, fetchActivePlanImage } from "../plan_analyzer.js";
import { loadSavedPlan, saveSavedPlan } from "../plan_store.js";
import { hasSessionTour } from "../session_tour_store.js";
import { itemThumb } from "./catalog_thumbs.js";
import { normalizeYaw } from "../scene/transforms.js";
import {
  constrainFurniturePlanPx,
  footprintPx,
  graphMeterBoundsFromGraph,
  isFloorOnlyModel,
  meterSpans,
  pxToGraphMeters,
  pxToWorld,
  worldToPx,
} from "./plan_mapping.js";
import { PlanHistory } from "./plan_history.js";
import {
  applyFloorDataToRoom,
  applyFurniturePoseToScene,
  sendFurnitureTransform,
  upsertFurnitureFromPlacement,
} from "./plan_scene_sync.js";
import { QERotationInput } from "../interaction/rotation_keys.js";
import { destroyOpeningMeshes, matchWallForSegment } from "../scene/opening_patch.js";
import { computeTopologyPatch } from "../scene/topology_patch.js";
import { rebuildWallSubset, refreshEnvelope, clearStructure } from "../scene/room_geometry.js";
import { planWallStrokes, planFloorStrokes, planFloorFillPoints, floorPolygonMeters } from "../scene/corner_fillet.js";
import {
  ensureFloorVertices,
  floorVertexRing,
  syncFloorLegacyFields,
  pickFloorBoundary,
  nearestFloorBoundary,
  mergeFloorExpansion,
  anchorsNear,
} from "../scene/floor_polygon.js";

const SNAP_SCREEN_PX = 10; // snap radius in screen pixels (converted to model units)
const FLOOR_CORNER_SNAP_SCREEN_PX = 5; // tighter — smooth drag, snap only on release
const FURNITURE_NET_THROTTLE_MS = 50;
const round2 = (v) => Math.round(Number(v) * 100) / 100;
/** localStorage fallback for the render thumbnail (shared with overlay.js). */
const PLAN_IMAGE_KEY = "aspect:plan-image";
/** sessionStorage cache so doors/windows survive close/reopen without Apply. */
const PLAN_OPENINGS_DRAFT_KEY = "aspect:plan-editor-openings-draft";

// Door leaf placement, mirrored on the server (room_factory.normHinge/normSwing).
// hinge: which jamb the leaf pivots on along the edge (start = "left", end = "right").
// swing: which side of the wall the leaf opens toward ("in" = inward normal).
const normHinge = (v) => (String(v ?? "").toLowerCase().startsWith("r") ? "right" : "left");
const normSwing = (v) => {
  const s = String(v ?? "").toLowerCase();
  return s === "out" || s === "outward" || s === "outside" || s === "exterior" ? "out" : "in";
};
/** Windows have curtains by default; only `curtain: false` turns them off. */
const windowHasCurtain = (op) => op?.curtain !== false;

// Room-type tagging. A "balcony" is rebuilt in 3D as an OPEN room: its exterior
// walls become a railing with a view that reaches `viewLength` meters out.
const isBalconyType = (t) => /balcon|terrace|loggia/.test(String(t ?? "").toLowerCase());
const DEFAULT_VIEW_LEN = 8; // meters of visible deck/ground beyond a balcony railing
const ROOM_TYPES = [
  { id: "room", label: "Room" },
  { id: "balcony", label: "Balcony" },
];

// Ceiling light fixtures the user can drop into a room. `kind` maps to a 3D
// fixture + light preset in the client LightSystem (intensity/spread/colour).
const LIGHT_TYPES = [
  { id: "ceiling", label: "Ceiling", hint: "Flush ceiling panel — broad, even room light" },
  { id: "pendant", label: "Pendant", hint: "Hanging pendant — warm pool of light" },
  { id: "spot", label: "Spot", hint: "Recessed downlight — tight focused beam" },
  { id: "chandelier", label: "Chandelier", hint: "Decorative multi-lamp — bright + warm" },
];
const DEFAULT_LIGHT = "ceiling";

/** Custom dropdown markup (native &lt;select&gt; option menus can't be themed on Windows). */
function pePickerHtml(key, title, extraClass = "") {
  return (
    `<div class="plan-editor__picker ${extraClass}" data-pe="${key}-picker">` +
    `<button type="button" class="plan-editor__picker-btn" data-pe="${key}-btn" ` +
    `aria-haspopup="listbox" aria-expanded="false" title="${title}">` +
    `<span data-pe="${key}-label"></span>` +
    `<svg class="plan-editor__picker-chev" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">` +
    `<path d="M4 6l4 4 4-4"/>` +
    `</svg></button>` +
    `<div class="plan-editor__picker-menu hidden" data-pe="${key}-menu" role="listbox"></div>` +
    `</div>`
  );
}

const TOOLS = [
  { id: "select", label: "Move", hint: "Drag corners (Shift = free); click a wall line to select it; click a door to flip hinge / swing" },
  { id: "floorExpand", label: "Expand", hint: "Click anywhere to start (snaps to nearest floor edge); place outward corners freely (auto H/V align when close · Shift = lock from previous); click outline to finish · right-click cancels", floorOnly: true },
  { id: "room", label: "Room", hint: "Click a room to move/rotate it, or set its type (Room/Balcony). Balconies open to a view." },
  { id: "wall", label: "Wall", hint: "Click to drop corners; double-click / Esc to finish" },
  { id: "door", label: "Door", hint: "Click a wall to add a door; then click it (Move tool) to set hinge / swing" },
  { id: "window", label: "Window", hint: "Click a wall to add a window; select it to toggle curtains" },
  { id: "light", label: "Lights", hint: "Pick a fixture type, then click inside a room to mount it on the ceiling" },
  { id: "switch", label: "Switch", hint: "Click inside a room to snap a wall switch (toggles that room's lights)" },
  { id: "delete", label: "Delete", hint: "Click a wall line, corner, door, window, light or switch to remove (Del / Backspace on selection)" },
  { id: "calibrate", label: "Scale", hint: "Draw a known length, then type its meters" },
];

const ICONS = {
  select:
    '<path d="M5 3l14 7-6 1.5L9 18z"/>',
  room:
    '<rect x="3" y="3" width="18" height="18" rx="1"/><path d="M16 7l3 3-3 3M19 10h-7"/>',
  wall:
    '<path d="M4 20V8l8-4 8 4v12"/><path d="M4 12h16"/>',
  door:
    '<rect x="6" y="4" width="12" height="16" rx="1"/><circle cx="14.5" cy="12" r="1"/>',
  window:
    '<rect x="4" y="5" width="16" height="14" rx="1"/><path d="M12 5v14M4 12h16"/>',
  light:
    '<path d="M9 18h6M10 21h4M12 2a6 6 0 0 1 4 10.5c-.8.7-1 1-1 2v.5H9v-.5c0-1-.2-1.3-1-2A6 6 0 0 1 12 2z"/>',
  switch:
    '<rect x="6" y="4" width="12" height="16" rx="2"/><rect x="8.5" y="7" width="7" height="9" rx="1.5"/><path d="M12 16v2"/>',
  delete:
    '<path d="M4 7h16M9 7V5h6v2M7 7l1 13h8l1-13"/>',
  calibrate:
    '<path d="M3 8h18v8H3z"/><path d="M7 8v4M11 8v4M15 8v4"/>',
  floorExpand:
    '<path d="M4 20V8l8-4 8 4v12"/><path d="M12 4v6M9 10h6"/><path d="M6 14h12" stroke-dasharray="3 2"/>',
};

export class PlanEditor {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {HTMLElement} mount  the overlay mount element
   * @param {{ getPlanPreviewImage?: () => string | null, getBuiltPlanImage?: () => string | null, scene?: import('../scene_manager.js').default, net?: import('../net_client.js').default, placement?: import('../interaction/placement_tool.js').PlacementTool }} [opts]
   */
  constructor(app, mount, opts = {}) {
    this.app = app;
    this.mount = mount;
    this.shell = mount.firstElementChild ?? mount;
    this._getBuiltPlanImage = opts.getBuiltPlanImage ?? null;
    this._getPlanPreviewImage = opts.getPlanPreviewImage ?? null;
    this.planAnalyzer = opts.planAnalyzer ?? null;
    this.doors = opts.doors ?? null;
    this.scene = opts.scene ?? null;
    this.net = opts.net ?? null;
    this.placement = opts.placement ?? null;
    this._placingCatalog = null; // { asset_id, meta, rotY }
    this._furnitureGhostPos = null;
    this._furnitureSendAt = 0;
    this._furnitureCommitting = false;
    /** Placements that arrived before the plan model finished loading. */
    this._pendingPlacements = new Map();
    this._furnitureSyncRaf = 0;
    this._topologyBaseline = null;
    this._lightsBaseline = null;
    this._openingsBaseline = null;
    this._closing = false;
    this._roomMetaBaseline = null;
    /** Serialized 2D model at editor open (Cancel / failed Apply restore target). */
    this._sessionModelBaseline = null;
    this._awaitingNewPlan = false;
    /** Deep copy of live room walls/openings/lights at editor open. */
    this._sceneSnapshot = null;
    this.open = false;
    this.tool = "select";

    /** @type {null | { naturalW:number, naturalH:number, metersPerPixel:number, height:number,
     *   nodes:Map<string,{id:number,px:number,py:number}>, edges:Array, openings:Array,
     *   nextNode:number, nextEdge:number, nextOpening:number }} */
    this.model = null;
    this.selected = null; // { kind, id }
    this.selectedRoom = null; // { key, nodes:[ids] }
    this._drag = null; // active drag state
    this._wallChain = null; // last node id while drawing walls
    this._floorExpand = null; // { start, points: [{px,py}] }
    this._floorExpandHover = null; // boundary pick for close preview
    this._floorExpandCursor = null; // live pointer while expanding
    this._floorExpandHvSnap = false; // Shift held during expand preview
    this._expandPreviewRaf = 0;
    this._lastExpandPointer = null;
    this._floorDragRaf = 0;
    this._applying = false;
    this._calib = null; // { a:{px,py}, b:{px,py}|null }
    this._showDims = true;

    this._buildDom();
    this._qeRotate = new QERotationInput({
      getYaw: () => this._getQERotateYaw(),
      setYaw: (y) => this._setQERotateYaw(y),
      onChange: () => this._onQERotateChanged(),
    });
    this.app.on("update", (dt) => {
      if (!this.open) return;
      if (!this._qeRotateActive() && !this._qeRotate.needsTick()) return;
      this._qeRotate.tick(dt);
    });
    this._boundQeKeyDown = (e) => this._handleQeKeyDown(e);
    this._boundQeKeyUp = (e) => this._handleQeKeyUp(e);
    window.addEventListener("keydown", this._boundQeKeyDown, true);
    window.addEventListener("keyup", this._boundQeKeyUp, true);
    this.app.on("ui:open-plan-editor", () => this.openEditor());
    this.app.on("ui:place-asset", ({ asset_id }) => {
      if (!this.open) return;
      this._startFurniturePlace(asset_id);
    });
    // SceneManager consumes net spawn/update frames and re-emits scene:* — listen once.
    this.app.on("scene:placement-added", (p) => this._onPlacementChanged(p));
    this.app.on("scene:placement-updated", (p) => this._onPlacementChanged(p));
    this.app.on("scene:placement-removed", ({ id }) => this._onPlacementRemoved(id));
    this.app.on("scene:room-loaded", () => this._scheduleFurnitureSync());
    this.app.on("net:scene:resync", () => this._scheduleFurnitureSync());
    this.app.on("ai:analysis", (e) => this._onAnalysisEvent(e));
    this.app.on("ui:home-reset", (e) => {
      if (e?.analyzing) {
        this._awaitingNewPlan = true;
        this._forgetEditorDrafts();
        if (this.open) this._clearEditorForNewUpload();
        return;
      }
      try {
        localStorage.removeItem(PLAN_IMAGE_KEY);
      } catch {
        /* ignore */
      }
      this._forgetEditorDrafts();
      if (!this.open) return;
      void this._showDefaultPlanBackground();
    });
  }

  // ---- DOM shell ------------------------------------------------------------

  _buildDom() {
    const root = document.createElement("div");
    root.className = "plan-editor hidden";
    root.innerHTML = `
      <div class="plan-editor__panel">
        <div class="plan-editor__head">
          <span class="plan-editor__title">Plan Editor</span>
          <div class="plan-editor__spacer"></div>
          <button type="button" class="plan-editor__btn plan-editor__btn--ghost" data-pe="undo" title="Undo last change (Ctrl+Z)" disabled>↶ Undo</button>
          <button type="button" class="plan-editor__btn plan-editor__btn--ghost" data-pe="redo" title="Redo (Ctrl+Y / Ctrl+Shift+Z)" disabled>Redo ↷</button>
          <div class="plan-editor__clear" data-pe="clear">
            <button type="button" class="plan-editor__btn plan-editor__btn--ghost plan-editor__clear-btn" data-pe="clear-btn" aria-haspopup="menu" aria-expanded="false">Clear ▾</button>
            <div class="plan-editor__clear-menu hidden" data-pe="clear-menu" role="menu">
              <button type="button" class="plan-editor__clear-item plan-editor__clear-item--all" role="menuitem" data-pe="clear-all">Clear whole apartment</button>
              <div class="plan-editor__clear-sep" role="separator" aria-hidden="true"></div>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="clear-doors">Remove all doors</button>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="clear-windows">Remove all windows</button>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="clear-curtains">Remove all curtains</button>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="add-curtains">Add curtains to all windows</button>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="clear-walls">Remove all walls &amp; rooms</button>
              <button type="button" class="plan-editor__clear-item" role="menuitem" data-pe="clear-interior">Remove all interior</button>
            </div>
          </div>
          <button type="button" class="plan-editor__btn plan-editor__btn--ghost" data-pe="cancel">Cancel</button>
          <button type="button" class="plan-editor__btn plan-editor__btn--accent" data-pe="apply">Apply</button>
        </div>
        <div class="plan-editor__body">
          <div class="plan-editor__tools" data-pe="tools"></div>
          <div class="plan-editor__stage-wrap">
            <div class="plan-editor__loading hidden" data-pe="loading" aria-live="polite">
              <div class="intro-screen__card plan-editor__loading-card">
                <div class="intro-screen__status intro-screen__status--loading">
                  <span class="intro-screen__spinner" aria-hidden="true"></span>
                  <span class="intro-screen__status-text">Loading plan…</span>
                </div>
              </div>
            </div>
            <div class="plan-editor__stage" data-pe="stage">
              <div class="plan-editor__canvas" data-pe="canvas">
                <img class="plan-editor__img" data-pe="img" alt="" decoding="async" />
                <svg class="plan-editor__svg" data-pe="svg" preserveAspectRatio="none"></svg>
              </div>
            </div>
            <div class="plan-editor__scroll-rail plan-editor__scroll-rail--y" data-pe="scroll-rail-y" aria-hidden="true">
              <div class="plan-editor__scroll-thumb" data-pe="scroll-thumb-y"></div>
            </div>
            <div class="plan-editor__scroll-rail plan-editor__scroll-rail--x" data-pe="scroll-rail-x" aria-hidden="true">
              <div class="plan-editor__scroll-thumb" data-pe="scroll-thumb-x"></div>
            </div>
          </div>
        </div>
        <div class="plan-editor__foot">
          <div class="plan-editor__zoom">
            <button type="button" class="plan-editor__btn plan-editor__btn--mini" data-pe="zoom-out">−</button>
            <span data-pe="zoom-label">100%</span>
            <button type="button" class="plan-editor__btn plan-editor__btn--mini" data-pe="zoom-in">+</button>
          </div>
          <label class="plan-editor__toggle" title="Show wall dimensions">
            <input type="checkbox" data-pe="dims" class="plan-editor__toggle-input" checked />
            <span class="plan-editor__toggle-switch" aria-hidden="true"></span>
            <span class="plan-editor__toggle-label">Dimensions</span>
          </label>
          <label class="plan-editor__slider" title="Background image opacity">
            <span class="plan-editor__slider-label">Image</span>
            <div class="plan-editor__slider-wrap" data-pe="img-op-wrap">
              <div class="plan-editor__slider-track" aria-hidden="true"></div>
              <input type="range" data-pe="img-op" class="plan-editor__range" min="0" max="1" step="0.05" value="1" aria-label="Image opacity" />
            </div>
            <span class="plan-editor__slider-value" data-pe="img-op-label">100%</span>
          </label>
          <div class="plan-editor__calib hidden" data-pe="calib">
            <span data-pe="calib-px">0 px</span>
            <input type="number" step="0.01" min="0.01" placeholder="meters" data-pe="calib-m" />
            <button type="button" class="plan-editor__btn plan-editor__btn--accent" data-pe="calib-set">Set scale</button>
          </div>
          <div class="plan-editor__roomctl hidden" data-pe="roomctl">
            <span>Room</span>
            <button type="button" class="plan-editor__btn plan-editor__btn--mini" data-pe="rot-ccw" title="Rotate −90°">↺ 90°</button>
            <button type="button" class="plan-editor__btn plan-editor__btn--mini" data-pe="rot-cw" title="Rotate +90°">↻ 90°</button>
            ${pePickerHtml("room-type", "Room type", "plan-editor__picker--dropup")}
            <label class="plan-editor__viewlen hidden" data-pe="room-viewwrap" title="How far the balcony view reaches">
              View
              <input type="number" step="1" min="1" max="40" data-pe="room-viewlen" />m
            </label>
          </div>
          <div class="plan-editor__lightctl hidden" data-pe="lightctl">
            <span>Fixture</span>
            ${pePickerHtml("light-type", "Light fixture type", "plan-editor__picker--wide plan-editor__picker--dropup")}
          </div>
          <div class="plan-editor__cornerctl hidden" data-pe="cornerctl">
            <span>Corner</span>
            <label class="plan-editor__slider plan-editor__slider--corner" title="Round this corner (interior fillet)">
              <span class="plan-editor__slider-label">Curve</span>
              <div class="plan-editor__slider-wrap" data-pe="corner-r-wrap">
                <div class="plan-editor__slider-track" aria-hidden="true"></div>
                <input type="range" data-pe="corner-r" class="plan-editor__range" min="0" max="1.5" step="0.05" value="0" aria-label="Corner curve radius" />
              </div>
              <span class="plan-editor__slider-value" data-pe="corner-r-label">0.00m</span>
            </label>
          </div>
          <div class="plan-editor__spacer"></div>
          <span class="plan-editor__status" data-pe="status"></span>
        </div>
      </div>`;
    this.shell.appendChild(root);
    this.root = root;
    this.elPanel = root.querySelector(".plan-editor__panel");
    this.$ = (k) => root.querySelector(`[data-pe="${k}"]`);

    this.elTools = this.$("tools");
    this.elStage = this.$("stage");
    this.elScrollRailY = this.$("scroll-rail-y");
    this.elScrollThumbY = this.$("scroll-thumb-y");
    this.elScrollRailX = this.$("scroll-rail-x");
    this.elScrollThumbX = this.$("scroll-thumb-x");
    this.elCanvas = this.$("canvas");
    this.elImg = this.$("img");
    this.elLoading = this.$("loading");
    this.elSvg = this.$("svg");
    this.elStatus = this.$("status");
    this.elZoomLabel = this.$("zoom-label");
    this.elCalib = this.$("calib");
    this.elCalibPx = this.$("calib-px");
    this.elCalibM = this.$("calib-m");
    this.elRoomCtl = this.$("roomctl");

    this.zoom = 1;
    this._userZoomed = false;
    this._viewBox = null;

    this._buildToolbar();

    this.$("cancel").addEventListener("click", () => this.closeEditor({ applied: false }));
    this.$("apply").addEventListener("click", () => void this._apply());
    this.$("undo").addEventListener("click", () => this._undo());
    this.$("redo").addEventListener("click", () => this._redo());
    this._initClearMenu();

    // Edit history (Undo/Redo). Each entry is a full serialized model snapshot;
    // stack management lives in plan_history.js.
    this._planHistory = new PlanHistory();
    this.$("zoom-in").addEventListener("click", () => {
      this._userZoomed = true;
      this._setZoom(this.zoom * 1.25);
    });
    this.$("zoom-out").addEventListener("click", () => {
      this._userZoomed = true;
      this._setZoom(this.zoom / 1.25);
    });
    this.$("dims").addEventListener("change", (e) => {
      this._showDims = e.target.checked;
      this._render();
    });
    this.elImgOp = this.$("img-op");
    this.elImgOpWrap = this.$("img-op-wrap");
    this.elImgOpLabel = this.$("img-op-label");
    this.elImgOp?.addEventListener("input", (e) => {
      const v = Number(e.target.value);
      this.elImg.style.opacity = String(v);
      this._syncImgOpUi(v);
    });
    this.$("calib-set").addEventListener("click", () => this._applyCalibration());
    this.$("rot-ccw").addEventListener("click", () => this._rotateRoom(-1));
    this.$("rot-cw").addEventListener("click", () => this._rotateRoom(1));
    this.elRoomViewWrap = this.$("room-viewwrap");
    this.elRoomViewLen = this.$("room-viewlen");
    this._openPePicker = null;
    this._onDocClickPePicker = (e) => {
      if (!this.open || !this._openPePicker) return;
      if (this._openPePicker.el?.contains(e.target)) return;
      this._openPePicker.close();
    };
    document.addEventListener("click", this._onDocClickPePicker);
    this._roomTypePicker = this._initPePicker("room-type", ROOM_TYPES, {
      title: "Room type",
      defaultId: "room",
      onSelect: (id) => this._setSelectedRoomType(id),
    });
    this.elRoomViewLen.addEventListener("change", (e) => this._setSelectedRoomViewLen(e.target.value));
    this.elLightCtl = this.$("lightctl");
    this.elCornerCtl = this.$("cornerctl");
    this.elCornerR = this.$("corner-r");
    this.elCornerRLabel = this.$("corner-r-label");
    this.elCornerRWrap = this.$("corner-r-wrap");
    this._lightTypePicker = this._initPePicker("light-type", LIGHT_TYPES, {
      title: "Light fixture type",
      defaultId: DEFAULT_LIGHT,
      showHint: true,
      onSelect: (id) => {
        this._lightFixture = id;
        // Retarget the selected light, if any, to the newly picked fixture.
        if (this.selected?.kind === "light") {
          const l = this.model?.lights.find((x) => x.id === this.selected.id);
          if (l) {
            l.fixture = this._lightFixture;
            this._render();
            this._record();
          }
        }
      },
    });
    this._lightFixture = DEFAULT_LIGHT;

    this.elCornerR?.addEventListener("input", (e) => {
      const v = Number(e.target.value);
      this._syncCornerSliderUi(v);
      if (this.selected?.kind === "floorCorner") {
        this._setFloorCornerFillet(this.selected.id, v);
        this._render();
        this._syncFloorLive();
        return;
      }
      if (this.selected?.kind !== "node") return;
      const n = this.model?.nodes.get(String(this.selected.id));
      if (!n) return;
      n.filletRadius = v;
      this._render();
      this._syncTopologyLive();
    });
    this.elCornerR?.addEventListener("change", () => this._record());

    // Keep typing in editor fields out of the PlayCanvas keyboard.
    for (const ev of ["keydown", "keyup", "keypress"]) {
      root.addEventListener(ev, (e) => e.stopPropagation());
    }
    document.addEventListener("keydown", (e) => {
      if (!this.open) return;
      if (e.key === "Escape") {
        if (this._openPePicker) {
          this._openPePicker.close();
          return;
        }
        if (this._placingCatalog) {
          this._cancelFurniturePlace();
          return;
        }
        if (this._wallChain != null) this._endWallChain();
        else if (this._floorExpand != null) this._cancelFloorExpand();
        else this.closeEditor({ applied: false });
        return;
      }
      // Undo / Redo. Ctrl+Z (undo), Ctrl+Y or Ctrl+Shift+Z (redo).
      if (e.ctrlKey || e.metaKey) {
        const k = e.key.toLowerCase();
        if (k === "z" && !e.shiftKey) {
          e.preventDefault();
          this._undo();
        } else if (k === "y" || (k === "z" && e.shiftKey)) {
          e.preventDefault();
          this._redo();
        }
        return;
      }
      const tag = e.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "select" || tag === "textarea") return;
      if (e.key === "Delete" || e.key === "Backspace") {
        if (this._deleteSelection()) e.preventDefault();
      }
    });

    // Pointer interaction on the SVG.
    this.elSvg.addEventListener("pointerdown", (e) => this._onPointerDown(e));
    // Floor expand: img/canvas gaps under transparent SVG still need to receive clicks.
    this.elCanvas?.addEventListener("pointerdown", (e) => this._onCanvasPointerDown(e));
    this.elSvg.addEventListener("contextmenu", (e) => this._onPlanContextMenu(e));
    this.elCanvas?.addEventListener("contextmenu", (e) => this._onPlanContextMenu(e));
    window.addEventListener("pointermove", (e) => this._onPointerMove(e));
    window.addEventListener("pointerup", (e) => this._onPointerUp(e));
    this.elStage.addEventListener("wheel", (e) => this._onWheel(e), { passive: false });
    this._bindStageScrollbar();
    this.elImg.addEventListener("load", () => {
      if (this.elImg?.src) this._render();
    });

    this._setPlanEditorLoading(false);
    window.addEventListener("resize", () => {
      if (this.open) {
        if (!this._userZoomed) this._fitToView();
        else this._centerStageScroll();
        this._render();
      }
    });
  }

  _buildToolbar() {
    this.elTools.innerHTML = "";
    for (const t of TOOLS) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "plan-editor__tool";
      btn.dataset.tool = t.id;
      if (t.floorOnly) btn.dataset.floorOnly = "1";
      btn.title = `${t.label} — ${t.hint}`;
      btn.innerHTML =
        `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">${ICONS[t.id] ?? ""}</svg>` +
        `<span>${t.label}</span>`;
      btn.addEventListener("click", () => this._setTool(t.id));
      this.elTools.appendChild(btn);
    }
    this._reflectTool();
  }

  /** Bulk-remove dropdown in the header (doors, windows, structure, interior). */
  _initClearMenu() {
    const btn = this.$("clear-btn");
    const menu = this.$("clear-menu");
    if (!btn || !menu) return;

    const close = () => {
      menu.classList.add("hidden");
      btn.setAttribute("aria-expanded", "false");
      if (this._openClearMenu) this._openClearMenu = null;
    };

    const open = () => {
      if (this._openPePicker) this._openPePicker.close();
      menu.classList.remove("hidden");
      btn.setAttribute("aria-expanded", "true");
      this._openClearMenu = { close };
    };

    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      if (menu.classList.contains("hidden")) open();
      else close();
    });

    this._onDocClickClearMenu = (e) => {
      if (!this.open || menu.classList.contains("hidden")) return;
      if (btn.contains(e.target) || menu.contains(e.target)) return;
      close();
    };
    document.addEventListener("click", this._onDocClickClearMenu);

    this.$("clear-all")?.addEventListener("click", () => {
      close();
      this._clearWholeApartment();
    });
    this.$("clear-doors")?.addEventListener("click", () => {
      close();
      this._removeAllOpeningsOfType("door");
    });
    this.$("clear-windows")?.addEventListener("click", () => {
      close();
      this._removeAllOpeningsOfType("window");
    });
    this.$("clear-curtains")?.addEventListener("click", () => {
      close();
      this._setAllWindowCurtains(false);
    });
    this.$("add-curtains")?.addEventListener("click", () => {
      close();
      this._setAllWindowCurtains(true);
    });
    this.$("clear-walls")?.addEventListener("click", () => {
      close();
      this._removeAllStructure();
    });
    this.$("clear-interior")?.addEventListener("click", () => {
      close();
      this._removeAllInterior();
    });
  }

  /** Remove walls, rooms, doors, windows, furniture, lights, and switches. */
  _clearWholeApartment() {
    if (!this.model) return;
    const hasAnything =
      this.model.edges.length > 0 ||
      this.model.nodes.size > 0 ||
      this.model.openings.length > 0 ||
      (this.model.furniture?.length ?? 0) > 0 ||
      (this.model.lights?.length ?? 0) > 0 ||
      (this.model.switches?.length ?? 0) > 0;
    if (!hasAnything) {
      if ((this.scene?.room?.walls?.length ?? 0) > 0) {
        this._initFloorRect();
        this._syncClearedStructureLive();
        this._render();
        this._record();
        this._status("Cleared whole apartment.");
      } else {
        this._status("Apartment is already empty.");
      }
      return;
    }
    for (const op of [...this.model.openings]) this._removeOpeningFromScene(op);
    for (const f of [...(this.model.furniture ?? [])]) {
      if (f.placementId) this.scene?.removePlacement(f.placementId);
    }
    this.model.nodes.clear();
    this.model.edges = [];
    this.model.openings = [];
    this.model.furniture = [];
    this.model.lights = [];
    this.model.switches = [];
    this.model.graphMeterBounds = null;
    this._roomMeta = [];
    this._initFloorRect();
    this.selected = null;
    this.selectedRoom = null;
    this._syncCornerCtl();
    this._syncClearedStructureLive();
    this._render();
    this._record();
    this._status("Cleared whole apartment.");
  }

  /** Push an empty traced graph to the live 3D scene (walls, doors, windows). */
  _syncClearedStructureLive() {
    if (!this.scene?.room || !this.model) return;
    this._ensureClearedFloorShell(true);
    this._commitLightsAndSwitches();
  }

  /**
   * Floor-only shell: sync floor rect → room data → strip walls → rebuild wood floor mesh.
   * Must run before reading the graph for Apply (room.walls must be empty).
   */
  _ensureClearedFloorShell(emitPatch = false) {
    if (!this.scene?.room || !this.model?.floor) return;
    this._initFloorRect();
    this.model.graphMeterBounds = null;
    this._refreshWorldMapping();
    // Sync floor data only — clearStructure clears walls then rebuilds the mesh once.
    this._syncFloorDataToRoom(false);
    clearStructure(this.app, this.scene.room, this.scene.roomRoot);
    if (emitPatch) {
      this.app?.fire("scene:topology-patched", { wallIds: [], removeWallIds: [], removedOpeningIds: [] });
    }
  }

  /** Remove every door or window opening from the plan and live 3D scene. */
  _removeAllOpeningsOfType(type) {
    if (!this.model) return;
    const doomed = this.model.openings.filter((o) => o.type === type);
    if (!doomed.length) {
      this._status(`No ${type === "door" ? "doors" : "windows"} to remove.`);
      return;
    }
    for (const op of doomed) this._removeOpeningFromScene(op);
    this.model.openings = this.model.openings.filter((o) => o.type !== type);
    if (this.selected?.kind === "opening") this.selected = null;
    this._render();
    this._record();
    this._status(`Removed ${doomed.length} ${type === "door" ? "door" : "window"}${doomed.length === 1 ? "" : "s"}.`);
  }

  /** Enable or disable curtains on every window opening (live 3D + plan model). */
  _setAllWindowCurtains(on) {
    if (!this.model) return;
    const wins = this.model.openings.filter((o) => o.type === "window");
    if (!wins.length) {
      this._status("No windows to update curtains on.");
      return;
    }
    let n = 0;
    for (const op of wins) {
      const next = on !== false;
      if (windowHasCurtain(op) === next) continue;
      op.curtain = next;
      this._syncOpeningById(op.id);
      n++;
    }
    this._render();
    this._record();
    this._status(
      n
        ? `${on ? "Added" : "Removed"} curtains on ${n} window${n === 1 ? "" : "s"}.`
        : on
          ? "All windows already have curtains."
          : "No curtains to remove.",
    );
  }

  /** Remove all traced walls, corners, rooms, and their openings. */
  _removeAllStructure() {
    if (!this.model) return;
    const hasStructure = this.model.edges.length > 0 || this.model.nodes.size > 0;
    if (!hasStructure) {
      this._status("No walls or rooms to remove.");
      return;
    }
    for (const op of [...this.model.openings]) this._removeOpeningFromScene(op);
    this.model.nodes.clear();
    this.model.edges = [];
    this.model.openings = [];
    this.model.graphMeterBounds = null;
    this._roomMeta = [];
    this._initFloorRect();
    this.selected = null;
    this.selectedRoom = null;
    this._syncCornerCtl();
    this._syncClearedStructureLive();
    this._render();
    this._record();
    this._status("All walls and rooms removed.");
  }

  /** Remove furniture, ceiling lights, and wall switches (keeps walls). */
  _removeAllInterior() {
    if (!this.model) return;
    const fCount = this.model.furniture?.length ?? 0;
    const lCount = this.model.lights?.length ?? 0;
    const sCount = this.model.switches?.length ?? 0;
    if (!fCount && !lCount && !sCount) {
      this._status("No interior items to remove.");
      return;
    }
    for (const f of [...(this.model.furniture ?? [])]) {
      if (f.placementId) this.scene?.removePlacement(f.placementId);
    }
    this.model.furniture = [];
    this.model.lights = [];
    this.model.switches = [];
    if (this.selected?.kind === "furniture" || this.selected?.kind === "light" || this.selected?.kind === "switch") {
      this.selected = null;
    }
    this._commitLightsAndSwitches();
    this._render();
    this._record();
    const parts = [];
    if (fCount) parts.push(`${fCount} furniture`);
    if (lCount) parts.push(`${lCount} light${lCount === 1 ? "" : "s"}`);
    if (sCount) parts.push(`${sCount} switch${sCount === 1 ? "" : "es"}`);
    this._status(`Removed ${parts.join(", ")}.`);
  }

  /**
   * Themed dropdown for the plan editor footer (replaces native &lt;select&gt;).
   * @returns {{ el: HTMLElement, close: () => void, value: string, disabled: boolean }}
   */
  _initPePicker(key, options, { title, showHint = false, defaultId = null, onSelect } = {}) {
    const el = this.$(`${key}-picker`);
    const btn = this.$(`${key}-btn`);
    const menu = this.$(`${key}-menu`);
    const label = this.$(`${key}-label`);
    let current = defaultId ?? options[0]?.id ?? "";
    let open = false;
    let disabled = false;

    menu.innerHTML = options
      .map((opt) => {
        const hint =
          showHint && opt.hint
            ? `<span class="plan-editor__picker-option-hint">${this._svgEsc(opt.hint)}</span>`
            : "";
        return (
          `<button type="button" class="plan-editor__picker-option" role="option" ` +
          `data-value="${opt.id}" aria-selected="false">` +
          `<span class="plan-editor__picker-option-label">${this._svgEsc(opt.label)}</span>${hint}` +
          `</button>`
        );
      })
      .join("");

    const refreshUi = () => {
      const opt = options.find((o) => o.id === current);
      if (label) label.textContent = opt?.label ?? current;
      for (const item of menu.querySelectorAll(".plan-editor__picker-option")) {
        const active = item.dataset.value === current;
        item.classList.toggle("plan-editor__picker-option--active", active);
        item.setAttribute("aria-selected", active ? "true" : "false");
      }
      btn.disabled = disabled;
      btn.classList.toggle("plan-editor__picker-btn--disabled", disabled);
    };

    const close = () => {
      open = false;
      menu.classList.add("hidden");
      btn.setAttribute("aria-expanded", "false");
      if (this._openPePicker?.el === el) this._openPePicker = null;
    };

    const openMenu = () => {
      if (disabled) return;
      if (this._openPePicker && this._openPePicker.el !== el) this._openPePicker.close();
      if (this._openClearMenu) this._openClearMenu.close();
      open = true;
      this._openPePicker = api;
      menu.classList.remove("hidden");
      btn.setAttribute("aria-expanded", "true");
    };

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (open) close();
      else openMenu();
    });

    for (const item of menu.querySelectorAll(".plan-editor__picker-option")) {
      item.addEventListener("click", (e) => {
        e.preventDefault();
        e.stopPropagation();
        const id = item.dataset.value;
        if (id !== current) {
          current = id;
          refreshUi();
          onSelect?.(id);
        }
        close();
      });
    }

    const api = {
      el,
      close,
      get value() {
        return current;
      },
      set value(id) {
        current = id;
        refreshUi();
      },
      get disabled() {
        return disabled;
      },
      set disabled(v) {
        disabled = Boolean(v);
        if (disabled) close();
        refreshUi();
      },
    };

    refreshUi();
    return api;
  }

  _setTool(id) {
    this.tool = id;
    this.selected = null;
    this.selectedRoom = null;
    if (this._wallChain != null) this._endWallChain();
    if (this._floorExpand != null) this._cancelFloorExpand();
    this._calib = null;
    this.elCalib.classList.toggle("hidden", id !== "calibrate");
    this.elRoomCtl?.classList.toggle("hidden", id !== "room");
    this.elLightCtl?.classList.toggle("hidden", id !== "light");
    this._syncRoomCtl();
    this._syncCornerCtl();
    const t = TOOLS.find((x) => x.id === id);
    if (this.open && t?.hint) this._status(t.hint);
    this._reflectTool();
    this._render();
  }

  _reflectTool() {
    for (const b of this.elTools.querySelectorAll(".plan-editor__tool")) {
      b.classList.toggle("plan-editor__tool--active", b.dataset.tool === this.tool);
      if (b.dataset.floorOnly === "1") {
        b.classList.toggle("hidden", !this._isFloorOnlyMode());
      }
    }
  }

  _status(msg, error = false) {
    if (!this.elStatus) return;
    this.elStatus.textContent = msg ?? "";
    this.elStatus.classList.toggle("plan-editor__status--err", Boolean(error));
  }

  _syncImgOpUi(value = Number(this.elImgOp?.value ?? 1)) {
    const v = Math.min(1, Math.max(0, value));
    this.elImgOpWrap?.style.setProperty("--range-pct", `${v * 100}%`);
    if (this.elImgOpLabel) this.elImgOpLabel.textContent = `${Math.round(v * 100)}%`;
  }

  // ---- open / close ---------------------------------------------------------

  _planEditorMotionMs(kind) {
    if (typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) {
      return 0;
    }
    return kind === "in" ? 460 : 340;
  }

  _setPlanEditorLoading(on) {
    this.elLoading?.classList.toggle("hidden", !on);
    this.elCanvas?.classList.toggle("plan-editor__canvas--loading", on);
  }

  /** Assign the background render and wait until the browser has decoded it. */
  _loadPlanImage(src) {
    return new Promise((resolve) => {
      if (!src || !this.elImg) {
        this._setPlanEditorLoading(false);
        resolve(false);
        return;
      }
      const finish = (ok) => {
        this.elImg.onload = null;
        this.elImg.onerror = null;
        if (ok) {
          const v = Number(this.elImgOp?.value ?? 1);
          this.elImg.style.opacity = String(v);
          this.elImg.classList.add("is-ready");
          const nw = this.elImg.naturalWidth || this.model?.naturalW;
          const nh = this.elImg.naturalHeight || this.model?.naturalH;
          if (this.model && nw > 0 && nh > 0 && (nw !== this.model.naturalW || nh !== this.model.naturalH)) {
            this._reconcilePlanNaturalSize(this.model, this.model.naturalW, this.model.naturalH, nw, nh);
            this.model.naturalW = nw;
            this.model.naturalH = nh;
            this._computePxOrigin();
            if (this._isFloorOnlyMode()) this._syncFloorLive();
            this._render();
          }
        } else {
          this.elImg.removeAttribute("src");
          this.elImg.style.opacity = "0";
          this.elImg.classList.remove("is-ready");
        }
        this._setPlanEditorLoading(false);
        resolve(ok);
      };
      if (this.elImg.src === src && this.elImg.complete && this.elImg.naturalWidth > 0) {
        finish(true);
        return;
      }
      this.elImg.onload = () => finish(true);
      this.elImg.onerror = () => finish(false);
      this.elImg.src = src;
    });
  }

  _playPlanEditorAnimation(kind) {
    const ms = this._planEditorMotionMs(kind);
    if (!this.root || ms <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const panel = this.elPanel;
      this.root.classList.remove("is-visible", "is-hiding");
      void this.root.offsetWidth;
      this.root.classList.add(kind === "in" ? "is-visible" : "is-hiding");
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        panel?.removeEventListener("animationend", onEnd);
        clearTimeout(timer);
        resolve();
      };
      const onEnd = (e) => {
        if (e.target === panel) finish();
      };
      panel?.addEventListener("animationend", onEnd);
      const timer = setTimeout(finish, ms);
    });
  }

  /** After Reset: drop the uploaded photo and show the default schematic map. */
  async _showDefaultPlanBackground() {
    const image = this._getBuiltPlanImage?.() ?? null;
    if (!image) {
      this.elImg?.removeAttribute("src");
      this.elImg?.classList.remove("is-ready");
      this._setPlanEditorLoading(false);
      this._render();
      return;
    }
    await this._loadPlanImage(image);
    this._render();
    requestAnimationFrame(() => this._fitToView());
  }

  _storedPlanImage() {
    try {
      return localStorage.getItem(PLAN_IMAGE_KEY);
    } catch {
      return null;
    }
  }

  /** True when this tab has a 2D upload / session tour — not the shared default. */
  _hasUploadedPlan() {
    return Boolean(
      this._getPlanPreviewImage?.() ||
      this._storedPlanImage() ||
      this.scene?.isSessionTourActive?.() ||
      hasSessionTour(),
    );
  }

  _specHasStructure(spec) {
    return (spec?.graph?.nodes?.length ?? 0) >= 3 || (spec?.rooms?.length ?? 0) > 0;
  }

  _forgetEditorDrafts() {
    this._sessionModelBaseline = null;
    try {
      sessionStorage.removeItem(PLAN_OPENINGS_DRAFT_KEY);
    } catch {
      /* private browsing */
    }
  }

  /** Drop the old default walls immediately when a new 2D render starts analyzing. */
  _clearEditorForNewUpload() {
    const w = this.model?.naturalW || 1000;
    const h = this.model?.naturalH || 1000;
    this.model = this._newModel(w, h, this.model?.metersPerPixel || 0.02, this.model?.height || 2.7);
    this.selected = null;
    this.selectedRoom = null;
    this._roomMeta = [];
    this._status("Replacing plan from the 2D render…");
    this._render();
  }

  _onAnalysisEvent(e) {
    if (e?.phase === "error" || e?.phase === "done") {
      if (e?.phase === "error") this._awaitingNewPlan = false;
      return;
    }
    if (e?.phase === "start" || e?.phase === "progress") {
      this._awaitingNewPlan = true;
      if (this.open && /analyz|prepar/i.test(String(e.text ?? ""))) {
        this._forgetEditorDrafts();
        this._clearEditorForNewUpload();
      }
      return;
    }
    if (e?.phase === "result" && e.result?.spec) {
      this._awaitingNewPlan = false;
      void this._reloadFromAnalyzedPlan(e.result.spec);
    }
  }

  async _reloadFromAnalyzedPlan(spec) {
    if (!this.open || !spec) return;
    this._forgetEditorDrafts();
    this._sessionModelBaseline = null;
    const image = await this._resolveBackgroundImage(null);
    const natural = await this._imageNatural(image);
    this._buildModel(spec, natural);
    if (image) await this._loadPlanImage(image);
    else this._setPlanEditorLoading(false);
    this._flushPendingPlacements();
    this._resetHistory();
    this._userZoomed = false;
    this._render();
    requestAnimationFrame(() => {
      this._fitToView();
      this._syncStageScrollbar();
    });
    this._status("Plan replaced from the 2D render.");
  }

  /** Uploaded 2D render first. Shared default image only when there is no session tour. */
  async _resolveBackgroundImage(serverImage) {
    const preview = this._getPlanPreviewImage?.();
    if (preview) return preview;
    const stored = this._storedPlanImage();
    if (stored) return stored;
    if (this._hasUploadedPlan()) return this._getBuiltPlanImage?.() ?? null;
    if (serverImage) return serverImage;
    return this._getBuiltPlanImage?.() ?? null;
  }

  _resolveEditorSpec(planResp) {
    const saved = loadSavedPlan()?.spec ?? null;
    if (this._hasUploadedPlan()) {
      if (this._awaitingNewPlan && !saved) {
        return { layoutMode: "cleared", graph: { nodes: [], edges: [] }, height: 2.7 };
      }
      if (saved && (this._specHasStructure(saved) || this._isClearedSpec(saved))) return saved;
      return this._specFromBuiltRoom() ?? saved ?? {
        layoutMode: "cleared",
        graph: { nodes: [], edges: [] },
        height: 2.7,
      };
    }
    let spec = planResp?.spec ?? saved ?? null;
    if (!this._isClearedSpec(spec) && !this._specHasStructure(spec)) {
      spec = this._specFromBuiltRoom() ?? spec;
    }
    return spec;
  }

  async openEditor() {
    if (this.open || this._closing) return;
    this.open = true;
    this.root.classList.remove("hidden", "is-hiding");
    document.body.classList.add("plan-editor-open");
    this._raiseUiChrome();
    this.app.fire("ui:cancel-placement", {});
    this._status("Loading plan…");
    this.app.fire("plan-editor:opened", {});
    this._setPlanEditorLoading(true);
    this.elImg?.removeAttribute("src");
    this.elImg?.classList.remove("is-ready");
    if (this.elImg) this.elImg.style.opacity = "0";
    void this._playPlanEditorAnimation("in");

    const useSharedDefault = !this._hasUploadedPlan();
    const [planResp, serverImage] = useSharedDefault
      ? await Promise.all([fetchActivePlan(), fetchActivePlanImage()])
      : [null, null];
    const image = await this._resolveBackgroundImage(serverImage);
    const fromUpload = this._hasUploadedPlan() || Boolean(this._getPlanPreviewImage?.());

    // Establish image natural dimensions (fallback to a square if no image).
    const natural = await this._imageNatural(image);
    const spec = this._resolveEditorSpec(planResp);
    this._buildModel(spec, natural);
    if (image) {
      await this._loadPlanImage(image);
    } else {
      this._setPlanEditorLoading(false);
    }
    if (this._isFloorOnlyMode()) {
      this._ensureClearedFloorShell(true);
    }
    if (this.elImgOp) this.elImgOp.value = "1";
    this._syncImgOpUi(1);
    this._flushPendingPlacements();
    this._resetHistory();
    this._setTool("select");
    this._userZoomed = false;
    this._render();
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this._fitToView();
        this._syncStageScrollbar();
      });
    });
    if (!this.model) {
      this._status("Could not load a plan.", true);
    } else if (this._isClearedSpec(spec)) {
      this._status("Apartment cleared — trace walls on the render to rebuild.");
    } else if (fromUpload) {
      this._status("Trace walls to match the render, or pick catalog items to place furniture.");
    } else if (image) {
      this._status("Edit geometry or pick catalog items to place furniture, then Apply.");
    } else {
      this._status("Edit geometry or pick catalog items to place furniture.");
    }
  }

  closeEditor(opts = {}) {
    if (!this.open || this._closing) return;
    const applied = opts.applied === true;
    if (!applied) {
      this._restoreSceneSnapshot();
      if (this._sessionModelBaseline) this._restoreModel(this._sessionModelBaseline);
      try {
        sessionStorage.removeItem(PLAN_OPENINGS_DRAFT_KEY);
      } catch {
        /* private browsing */
      }
    }
    this._closing = true;
    this._cancelFurniturePlace(false);
    this._pendingPlacements.clear();
    if (this._furnitureSyncRaf) {
      cancelAnimationFrame(this._furnitureSyncRaf);
      this._furnitureSyncRaf = 0;
    }
    if (applied) {
      try {
        if (this.model?.openings?.length) {
          sessionStorage.setItem(PLAN_OPENINGS_DRAFT_KEY, JSON.stringify(this.model.openings));
        } else {
          sessionStorage.removeItem(PLAN_OPENINGS_DRAFT_KEY);
        }
      } catch {
        /* private browsing */
      }
    }

    const finishClose = () => {
      this.open = false;
      this._closing = false;
      this._setPlanEditorLoading(false);
      this.elImg?.removeAttribute("src");
      this.elImg?.classList.remove("is-ready");
      if (this.elImg) this.elImg.style.opacity = "0";
      this.root.classList.add("hidden");
      this.root.classList.remove("is-visible", "is-hiding");
      document.body.classList.remove("plan-editor-open");
      this.app.fire("plan-editor:closed", {});
    };

    void this._playPlanEditorAnimation("out").then(finishClose);
  }

  _syncPlanEditorLayout() {
    if (!this._userZoomed) this._fitToView();
    else this._centerStageScroll();
  }

  /** Seed a rect spec from the live built room when no saved plan exists (default home). */
  _specFromBuiltRoom() {
    const room = this.scene?.room;
    const b = room?.bounds;
    if (!b?.min || !b?.max) return null;
    const width = Number(b.max[0]) - Number(b.min[0]);
    const depth = Number(b.max[2]) - Number(b.min[2]);
    if (!(width > 0) || !(depth > 0)) return null;
    const height = Number(b.max[1]) - Number(b.min[1]) || room?.dimensions?.height || 2.8;
    const rooms = Array.isArray(room?.rooms) && room.rooms.length
      ? room.rooms.map((r) => ({
          x: Number(r.x) || 0,
          z: Number(r.z) || 0,
          width: Number(r.width) || width,
          depth: Number(r.depth) || depth,
          ...(r.type ? { type: r.type } : {}),
          ...(r.name ? { name: r.name } : {}),
          ...(Array.isArray(r.polygon) ? { polygon: r.polygon } : {}),
        }))
      : [{ x: 0, z: 0, width, depth }];
    return {
      width,
      depth,
      height,
      rooms,
      ...(room?.finishes ? { finishes: room.finishes } : {}),
    };
  }

  /** Keep catalog + prompt above the plan-editor blur (same stacking context). */
  _raiseUiChrome() {
    const catalog = this.shell.querySelector(".catalog-stack");
    const prompt = this.shell.querySelector(".prompt-stack");
    if (catalog) this.shell.appendChild(catalog);
    if (prompt) this.shell.appendChild(prompt);
  }

  _imageNatural(dataUrl) {
    return new Promise((resolve) => {
      if (!dataUrl) return resolve({ w: 1000, h: 1000 });
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth || 1000, h: img.naturalHeight || 1000 });
      img.onerror = () => resolve({ w: 1000, h: 1000 });
      img.src = dataUrl;
    });
  }

  // ---- editable model -------------------------------------------------------

  _newModel(naturalW, naturalH, mpp, height) {
    return {
      naturalW,
      naturalH,
      metersPerPixel: mpp,
      height,
      nodes: new Map(),
      edges: [],
      openings: [],
      lights: [],
      switches: [],
      nextNode: 1,
      nextEdge: 1,
      nextOpening: 1,
      nextLight: 1,
      nextSwitch: 1,
      furniture: [],
      nextFurniture: 1,
      originPx: { px: 0, py: 0 },
      /** Meter envelope from the persisted graph (may differ from px*mpp after area scaling). */
      graphMeterBounds: null,
    };
  }

  _catalogMeta(asset_id) {
    return this.scene?.catalog?.get(asset_id) ?? null;
  }

  /**
   * Map between traced image pixels and built 3D floor coords (min-corner origin).
   * The server builds from graph node meter x/y (area-scaled), not px*mpp — so we
   * anchor the meter span to the graph and the pixel span to traced wall nodes.
   */
  _refreshWorldMapping() {
    if (!this.model) return;
    let minPx = Infinity;
    let minPy = Infinity;
    let maxPx = -Infinity;
    let maxPy = -Infinity;
    const f = this.model.floor;
    if (f) {
      minPx = Math.min(minPx, f.x0, f.x1);
      minPy = Math.min(minPy, f.y0, f.y1);
      maxPx = Math.max(maxPx, f.x0, f.x1);
      maxPy = Math.max(maxPy, f.y0, f.y1);
      for (const v of floorVertexRing(f)) {
        minPx = Math.min(minPx, v.px);
        minPy = Math.min(minPy, v.py);
        maxPx = Math.max(maxPx, v.px);
        maxPy = Math.max(maxPy, v.py);
      }
    }
    for (const [, n] of this.model.nodes) {
      minPx = Math.min(minPx, n.px);
      minPy = Math.min(minPy, n.py);
      maxPx = Math.max(maxPx, n.px);
      maxPy = Math.max(maxPy, n.py);
    }
    if (!Number.isFinite(minPx)) minPx = 0;
    if (!Number.isFinite(minPy)) minPy = 0;
    if (!Number.isFinite(maxPx)) maxPx = minPx + 1;
    if (!Number.isFinite(maxPy)) maxPy = minPy + 1;
    this.model.originPx = { px: minPx, py: minPy };
    this.model.pxBounds = { minPx, minPy, maxPx, maxPy };
  }

  _isFloorOnlyMode() {
    return isFloorOnlyModel(this.model);
  }

  _floorCorners() {
    const f = this.model?.floor;
    if (!f) return [];
    this._ensureFloorCorners();
    return floorVertexRing(f).map((v) => ({ id: v.id, px: v.px, py: v.py }));
  }

  /** @returns {Record<string, { px: number, py: number }> | null} */
  _floorCornerMap(m = this.model) {
    const f = m?.floor;
    if (!f) return null;
    this._ensureFloorCorners(m);
    const out = {};
    for (const v of f.vertices ?? []) out[v.id] = { px: v.px, py: v.py };
    // Expanded rings use v* ids on split edges; legacy nw/ne/se/sw live on floor.corners.
    for (const id of ["nw", "ne", "se", "sw"]) {
      const p = f.corners?.[id];
      if (p && Number.isFinite(Number(p.px)) && Number.isFinite(Number(p.py)) && !out[id]) {
        out[id] = { px: Number(p.px), py: Number(p.py) };
      }
    }
    return out;
  }

  /** Ensure ordered floor vertex ring (migrates legacy four corners). */
  _ensureFloorCorners(m = this.model) {
    const f = m?.floor;
    if (!f) return;
    ensureFloorVertices(f);
    syncFloorLegacyFields(f);
  }

  _nextFloorVertexId(m = this.model) {
    const f = m?.floor;
    if (!f) return "v0";
    if (!Number.isFinite(f.nextVertex)) f.nextVertex = (f.vertices?.length ?? 0) + 1;
    const id = `v${f.nextVertex++}`;
    return id;
  }

  _cancelFloorExpand() {
    this._floorExpand = null;
    this._floorExpandHover = null;
    this._floorExpandCursor = null;
    this._floorExpandHvSnap = false;
    this._lastExpandPointer = null;
    this._status("Floor expand cancelled.");
    this._render();
  }

  /** Floor ring as a polygon for inside/outside tests during expand. */
  _floorPolyPx(f = this.model?.floor) {
    if (!f) return [];
    return floorVertexRing(f).map((v) => [v.px, v.py]);
  }

  /** Close target on click — only when intentionally on the floor outline, not empty canvas. */
  _floorExpandCloseAtClick(f, p, snap) {
    const chain = this._floorExpand?.points;
    if (!chain?.length) return null;
    const closeSnap = snap * 0.85;
    const hit = pickFloorBoundary(f, p, closeSnap);
    if (!hit || anchorsNear(this._floorExpand.start, hit, snap)) return null;
    const poly = this._floorPolyPx(f);
    if (this._pointInPoly({ px: p.px, py: p.py }, poly)) return hit;
    // Outward clicks in empty margin must stay chain points — close only when on the outline.
    if (Math.hypot(p.px - hit.px, p.py - hit.py) <= snap * 0.45) return hit;
    return null;
  }

  /** Hover preview for closing back onto the floor outline. */
  _floorExpandCloseHover(f, p, snap) {
    const chain = this._floorExpand?.points;
    if (!chain?.length) return null;
    const hoverSnap = snap * 1.6;
    const hit = pickFloorBoundary(f, p, hoverSnap);
    if (!hit || anchorsNear(this._floorExpand.start, hit, snap)) return null;
    const poly = this._floorPolyPx(f);
    if (this._pointInPoly({ px: p.px, py: p.py }, poly)) return hit;
    if (Math.hypot(p.px - hit.px, p.py - hit.py) <= snap * 0.65) return hit;
    return null;
  }

  /**
   * Expand pin placement: free anywhere by default; when close to a floor corner's
   * row or column, snap onto that axis. Shift locks H/V from the previous point.
   */
  _snapFloorExpandPoint(px, py, prev, hvSnap = false) {
    const rawPx = px;
    const rawPy = py;
    if (hvSnap && prev) {
      if (Math.abs(px - prev.px) <= Math.abs(py - prev.py)) return { px: prev.px, py };
      return { px, py: prev.py };
    }
    const f = this.model?.floor;
    if (!f) return { px, py };
    const snap = SNAP_SCREEN_PX * this._unitPerScreen();
    const anchors = [];
    for (const v of floorVertexRing(f)) anchors.push(v);
    const ex = this._floorExpand;
    if (ex?.start) anchors.push({ px: ex.start.px, py: ex.start.py });
    for (const pt of ex?.points ?? []) anchors.push(pt);

    let bestX = null;
    let bestY = null;
    let nearX = snap;
    let nearY = snap;
    for (const a of anchors) {
      const dx = Math.abs(px - a.px);
      const dy = Math.abs(py - a.py);
      if (dx < nearX) {
        nearX = dx;
        bestX = a.px;
      }
      if (dy < nearY) {
        nearY = dy;
        bestY = a.py;
      }
    }
    let outPx = px;
    let outPy = py;
    if (bestX != null && nearX <= snap) outPx = bestX;
    if (bestY != null && nearY <= snap) outPy = bestY;

    // Avoid collapsing onto the previous pin when auto H/V snap pulls both axes back.
    if (prev) {
      const snapDist = Math.hypot(outPx - prev.px, outPy - prev.py);
      const rawDist = Math.hypot(rawPx - prev.px, rawPy - prev.py);
      if (snapDist < snap * 0.12 && rawDist >= snap * 0.12) {
        const horiz = Math.abs(rawPx - prev.px) >= Math.abs(rawPy - prev.py);
        if (horiz) {
          outPx = rawPx;
          if (bestY != null && nearY <= snap && bestY !== prev.py) outPy = bestY;
        } else {
          outPy = rawPy;
          if (bestX != null && nearX <= snap && bestX !== prev.px) outPx = bestX;
        }
        if (Math.hypot(outPx - prev.px, outPy - prev.py) < snap * 0.12) {
          return { px: rawPx, py: rawPy };
        }
      }
    }
    return { px: outPx, py: outPy };
  }

  _floorExpandClick(p, hvSnap = false) {
    if (!this.model?.floor) return;
    this._ensureFloorCorners();
    const snap = SNAP_SCREEN_PX * this._unitPerScreen();
    const f = this.model.floor;

    if (!this._floorExpand) {
      const hit = pickFloorBoundary(f, p, snap * 3) ?? nearestFloorBoundary(f, p);
      if (!hit) {
        this._status("No floor outline to expand.", true);
        return;
      }
      if (!this._viewBox) {
        this._viewBox = this._computeViewBox(this._renderPad(this._unitPerScreen()));
      }
      this._floorExpand = {
        start: hit,
        points: [],
        viewBox: { ...this._viewBox },
      };
      this._floorExpandHover = null;
      this._floorExpandCursor = null;
      this._status("Place outward corners anywhere · auto H/V align when close · Shift = lock from previous · click outline to finish.");
      this._render();
      return;
    }

    const chain = this._floorExpand.points;
    const closeHit = this._floorExpandCloseAtClick(f, p, snap);

    if (closeHit) {
      const verts = floorVertexRing(f);
      const merged = mergeFloorExpansion(
        verts,
        this._floorExpand.start,
        chain,
        closeHit,
        () => this._nextFloorVertexId(),
      );
      if (!merged) {
        this._status("Could not merge — close on a different floor point (avoid crossing the outline).", true);
        return;
      }
      f.vertices = merged;
      syncFloorLegacyFields(f);
      this._floorExpand = null;
      this._floorExpandHover = null;
      this._floorExpandCursor = null;
      this._floorExpandHvSnap = false;
      this._lastExpandPointer = null;
      this._syncFloorLive();
      this._record();
      this._status("Floor area added.");
      this._render();
      return;
    }

    const prev =
      chain.length > 0
        ? chain[chain.length - 1]
        : { px: this._floorExpand.start.px, py: this._floorExpand.start.py };
    const snapped = this._snapFloorExpandPoint(p.px, p.py, prev, hvSnap);
    let px = snapped.px;
    let py = snapped.py;
    const minStep = snap * 0.12;
    let step = Math.hypot(px - prev.px, py - prev.py);
    if (step < minStep) {
      const rawStep = Math.hypot(p.px - prev.px, p.py - prev.py);
      if (rawStep >= minStep) {
        px = p.px;
        py = p.py;
        step = rawStep;
      }
    }
    if (step < minStep) {
      this._status("Click farther from the previous point.", true);
      return;
    }
    const last = chain[chain.length - 1];
    if (last && Math.hypot(last.px - px, last.py - py) < snap * 0.08) return;
    chain.push({ px, py });
    this._floorExpandHover = null;
    this._floorExpandCursor = { px, py };
    this._refreshFloorExpandViewBox();
    this._status(
      chain.length === 1
        ? "Corner placed — add more or click the floor outline to finish."
        : `${chain.length} corners placed — keep going or click the floor outline to finish.`,
    );
    this._render();
  }

  /** Sync legacy x0/y0/x1/y1 bbox fields from independent corners. */
  _syncFloorRectFromCorners(m = this.model) {
    syncFloorLegacyFields(m?.floor);
  }

  /** Bounding box of the floor vertex ring in plan pixels. */
  _floorRectPx(m = this.model) {
    const f = m?.floor;
    if (!f) return null;
    this._ensureFloorCorners(m);
    const verts = floorVertexRing(f);
    if (verts.length >= 3) {
      const xs = verts.map((v) => v.px);
      const ys = verts.map((v) => v.py);
      return {
        x0: Math.min(...xs),
        y0: Math.min(...ys),
        x1: Math.max(...xs),
        y1: Math.max(...ys),
      };
    }
    const c = f.corners;
    if (!c?.nw || !c?.ne || !c?.se || !c?.sw) return null;
    const xs = [c.nw.px, c.ne.px, c.se.px, c.sw.px];
    const ys = [c.nw.py, c.ne.py, c.se.py, c.sw.py];
    return {
      x0: Math.min(...xs),
      y0: Math.min(...ys),
      x1: Math.max(...xs),
      y1: Math.max(...ys),
    };
  }

  /** Write axis-aligned floor bounds and refresh corner points. */
  _setFloorRectPx({ x0, y0, x1, y1 }, m = this.model) {
    if (!m?.floor) return;
    const fillet = { nw: 0, ne: 0, se: 0, sw: 0, ...(m.floor.fillet ?? {}) };
    m.floor = {
      x0,
      y0,
      x1,
      y1,
      fillet,
      corners: {
        nw: { px: x0, py: y0 },
        ne: { px: x1, py: y0 },
        se: { px: x1, py: y1 },
        sw: { px: x0, py: y1 },
      },
    };
  }

  _floorMeters(m = this.model) {
    const b = this._floorBoundsMeters(m);
    return { width: b.width, depth: b.depth };
  }

  /** Floor rect in absolute world meters (image px origin = world 0,0). */
  _floorBoundsMeters(m = this.model) {
    const f = m?.floor;
    const mpp = m?.metersPerPixel > 0 ? m.metersPerPixel : 0.02;
    if (!f) return { minX: 0, minZ: 0, maxX: 8, maxZ: 8, width: 8, depth: 8 };
    const x0 = Math.min(f.x0, f.x1);
    const y0 = Math.min(f.y0, f.y1);
    const x1 = Math.max(f.x0, f.x1);
    const y1 = Math.max(f.y0, f.y1);
    const minX = x0 * mpp;
    const minZ = y0 * mpp;
    const maxX = x1 * mpp;
    const maxZ = y1 * mpp;
    return { minX, minZ, maxX, maxZ, width: maxX - minX, depth: maxZ - minZ };
  }

  _ensureFloorRect(m = this.model) {
    if (!m?.floor) return;
    this._ensureFloorCorners(m);
    this._syncFloorRectFromCorners(m);
    const rect = this._floorRectPx(m);
    if (!rect) return;
    let { x0, y0, x1, y1 } = rect;
    const mpp = m.metersPerPixel > 0 ? m.metersPerPixel : 0.02;
    const minPx = 0.5 / mpp;
    if (x1 - x0 < minPx || y1 - y0 < minPx) return;
  }

  /**
   * Snap a dragged floor corner — only adjacent ring neighbours (smooth while dragging).
   * Full snap runs once on pointer-up.
   */
  _snapFloorCorner(cornerId, px, py, free = false) {
    if (free) return { px, py };
    const f = this.model?.floor;
    if (!f) return { px, py };
    const verts = floorVertexRing(f);
    const idx = verts.findIndex((v) => v.id === cornerId);
    if (idx < 0) return { px, py };
    const snap = FLOOR_CORNER_SNAP_SCREEN_PX * this._unitPerScreen();

    let best = null;
    let bestD = snap;
    for (const v of verts) {
      if (v.id === cornerId) continue;
      const d = Math.hypot(px - v.px, py - v.py);
      if (d < bestD) {
        bestD = d;
        best = v;
      }
    }
    if (best) return { px: best.px, py: best.py };

    const adj = [verts[(idx - 1 + verts.length) % verts.length], verts[(idx + 1) % verts.length]];
    for (const c of adj) {
      if (Math.abs(px - c.px) < snap) px = c.px;
      if (Math.abs(py - c.py) < snap) py = c.py;
    }

    return { px, py };
  }

  /** Move one floor corner; only that handle moves (wall-node style). */
  _moveFloorCorner(cornerId, p, free = false) {
    const f = this.model?.floor;
    if (!f) return;
    this._ensureFloorCorners();
    const vert = f.vertices?.find((v) => v.id === cornerId);
    if (!vert) return;
    const { px, py } = this._snapFloorCorner(cornerId, p.px, p.py, free);
    vert.px = px;
    vert.py = py;
    if (f.corners?.[cornerId]) {
      f.corners[cornerId].px = px;
      f.corners[cornerId].py = py;
    }
    syncFloorLegacyFields(f);
  }

  _getFloorCornerFillet(id) {
    return Number(this.model?.floor?.fillet?.[id]) || 0;
  }

  _setFloorCornerFillet(id, rM) {
    if (!this.model?.floor) return;
    if (!this.model.floor.fillet) this.model.floor.fillet = { nw: 0, ne: 0, se: 0, sw: 0 };
    this.model.floor.fillet[id] = Math.min(1.5, Math.max(0, Number(rM) || 0));
  }

  _beginFloorCornerDrag(id, p) {
    const c = this._floorCorners().find((v) => v.id === id);
    return {
      type: "floorCorner",
      id,
      grab: c ? { ox: p.px - c.px, oy: p.py - c.py } : { ox: 0, oy: 0 },
      anchor: c ? { px: c.px, py: c.py } : { px: p.px, py: p.py },
      last: { px: p.px, py: p.py },
      viewBox: this._viewBox ? { ...this._viewBox } : null,
    };
  }

  /** Grow the working view after a placed expand corner — not on every mouse move. */
  _refreshFloorExpandViewBox() {
    if (!this._floorExpand) return;
    const pad = this._renderPad(this._unitPerScreen());
    this._floorExpand.viewBox = this._computeViewBox(pad);
  }

  _pickFloorCorner(p) {
    const snap = SNAP_SCREEN_PX * this._unitPerScreen();
    let best = null;
    let bestD = snap;
    for (const c of this._floorCorners()) {
      const d = Math.hypot(c.px - p.px, c.py - p.py);
      if (d < bestD) {
        bestD = d;
        best = c;
      }
    }
    return best;
  }

  _initFloorRect() {
    if (!this.model) return;
    if (this.model.floor) {
      this._ensureFloorRect();
      this._refreshWorldMapping();
      return;
    }
    const envW = this.scene?.room?.dimensions?.width ?? 8;
    const envD = this.scene?.room?.dimensions?.depth ?? 8;
    const fit = this._fitMetersToImage(
      [
        [0, 0],
        [envW, envD],
      ],
      this.model.naturalW,
      this.model.naturalH,
    );
    const toPx = (x, z) => [fit.ox + x * fit.s, fit.oy + z * fit.s];
    const [x0, y0] = toPx(0, 0);
    const [x1, y1] = toPx(envW, envD);
    this.model.floor = { x0, y0, x1, y1, fillet: { nw: 0, ne: 0, se: 0, sw: 0 } };
    if (!(this.model.metersPerPixel > 0)) this.model.metersPerPixel = 1 / fit.s;
    this._ensureFloorRect();
    this._refreshWorldMapping();
  }

  _syncFloorLive(emitPatch = true) {
    if (!this._isFloorOnlyMode() || !this.scene?.room) return;
    this._refreshWorldMapping();
    this._syncFloorDataToRoom();
    if (emitPatch) {
      this.app?.fire("scene:topology-patched", { wallIds: [], removeWallIds: [], removedOpeningIds: [] });
    }
  }

  /** Snap traced wall nodes onto the floor vertex ring (keeps walls aligned with floor). */
  _snapWallGraphToFloorRing() {
    if (!this.model?.floor) return;
    const verts = floorVertexRing(this.model.floor);
    if (verts.length < 3) return;
    const mpp = this.model.metersPerPixel > 0 ? this.model.metersPerPixel : 0.02;
    const snapPx = Math.max(SNAP_SCREEN_PX * this._unitPerScreen(), mpp * 0.12);
    const nodes = [...this.model.nodes.values()];
    if (!nodes.length) return;
    const used = new Set();

    for (const v of verts) {
      let best = null;
      let bestD = snapPx;
      for (const n of nodes) {
        if (used.has(n.id)) continue;
        const d = Math.hypot(n.px - v.px, n.py - v.py);
        if (d < bestD) {
          bestD = d;
          best = n;
        }
      }
      if (best) {
        best.px = v.px;
        best.py = v.py;
        used.add(best.id);
      }
    }
  }

  /** Push floor polygon + bbox onto the live room (does not strip walls). */
  _syncFloorDataToRoom(patchEnvelope = true) {
    if (!this.model?.floor || !this.scene?.room) return;
    this._ensureFloorRect();
    const b = this._floorBoundsMeters();
    const room = this.scene.room;
    const mpp = this.model.metersPerPixel > 0 ? this.model.metersPerPixel : 0.02;
    const polygon = floorPolygonMeters(this.model.floor, (px, py) => this._pxToWorld(px, py), mpp);
    applyFloorDataToRoom(room, b, polygon, this.model.height ?? 2.7);
    if (patchEnvelope) refreshEnvelope(this.app, room, this.scene.roomRoot);
  }

  _computePxOrigin() {
    this._refreshWorldMapping();
  }

  // Coordinate mapping + constraint solving live in plan_mapping.js (pure,
  // Node-testable). These thin wrappers keep the existing call sites intact.

  /** @returns {{ spanMx: number, spanMz: number } | null} */
  _meterSpans() {
    return meterSpans(this.model);
  }

  _pxToWorld(px, py) {
    return pxToWorld(this.model, px, py);
  }

  _worldToPx(x, z) {
    return worldToPx(this.model, x, z);
  }

  /** Record graph node meter envelope (absolute, pre min-corner shift). */
  _setGraphMeterBoundsFromGraph(graph) {
    if (!this.model || !graph) return;
    const bounds = graphMeterBoundsFromGraph(graph);
    if (bounds) this.model.graphMeterBounds = bounds;
  }

  /** Absolute graph meters for a traced pixel (inverse of buildFromGraph shift). */
  _pxToGraphMeters(px, py) {
    return pxToGraphMeters(this.model, px, py);
  }

  /** Footprint width/depth in plan pixels (matches 3D catalog meters). */
  _footprintPx(fp) {
    return footprintPx(this.model, fp);
  }

  /** Keep a furniture center in valid floor space (same rules as 3D placement). */
  _constrainFurniturePlanPx(px, py, footprint, rotY = 0, scale = 1, excludePlacementId = null) {
    if (!this.scene?.room) return { px, py };
    const placements = this.scene.placements ? [...this.scene.placements.values()] : [];
    return constrainFurniturePlanPx(this.model, this.scene.room, placements, px, py, footprint, rotY, scale, excludePlacementId);
  }

  _syncFurnitureFromScene() {
    if (!this.model || !this.scene) return;
    this.model.furniture = [];
    this.model.nextFurniture = 1;
    for (const p of this.scene.list()) this._upsertFurnitureFromPlacement(p);
    for (const p of this._pendingPlacements.values()) this._upsertFurnitureFromPlacement(p);
  }

  _flushPendingPlacements() {
    if (!this.model || !this._pendingPlacements.size) return;
    for (const p of this._pendingPlacements.values()) this._upsertFurnitureFromPlacement(p);
    this._pendingPlacements.clear();
  }

  /** Coalesce rapid agent spawns / resync into one furniture refresh. */
  _scheduleFurnitureSync() {
    if (!this.open) return;
    if (this._furnitureSyncRaf) return;
    this._furnitureSyncRaf = requestAnimationFrame(() => {
      this._furnitureSyncRaf = 0;
      if (!this.open) return;
      if (this.model) {
        this._syncFurnitureFromScene();
        this._render();
      }
    });
  }

  _onPlacementRemoved(id) {
    if (!this.open || !id) return;
    this._pendingPlacements.delete(id);
    if (!this.model?.furniture) return;
    this.model.furniture = this.model.furniture.filter((f) => f.placementId !== id);
    if (this.selected?.kind === "furniture") this.selected = null;
    this._render();
  }

  _upsertFurnitureFromPlacement(p) {
    upsertFurnitureFromPlacement(this.model, p);
  }

  _onPlacementChanged(p) {
    if (!this.open || !p?.id) return;
    if (!this.model) {
      this._pendingPlacements.set(p.id, p);
      return;
    }
    if (this._drag?.type === "furniture" || this._drag?.type === "frotate") {
      const f = this.model.furniture?.find((x) => x.placementId === p.id);
      if (f && f.id === this._drag.id) return;
    }
    this._upsertFurnitureFromPlacement(p);
    this._pendingPlacements.delete(p.id);
    this._render();
  }

  _startFurniturePlace(asset_id) {
    if (this._furnitureCommitting) {
      this._status("Still placing the previous item…", true);
      return;
    }
    if (!this.model) {
      this._status("Plan still loading…", true);
      return;
    }
    const meta = this._catalogMeta(asset_id);
    if (!meta) {
      this._status("Unknown catalog item.", true);
      return;
    }
    if (this._placingCatalog) {
      this.placement?.cancel(false);
      this._qeRotate.reset();
    }
    this._placingCatalog = { asset_id, meta, rotY: 0 };
    this._furnitureGhostPos = null;
    void this.placement?.startPlanPreview(asset_id);
    this.app.fire("ui:placement-started", { asset_id, name: meta.name });
    this.app.fire("ui:placement-hint", {
      text: `Placing ${meta.name} on the plan — click to place · Q/E rotate · Esc cancel`,
    });
    this._status(`Placing ${meta.name} — click on the plan.`);
    this._render();
  }

  _cancelFurniturePlace(fireEvent = true) {
    if (!this._placingCatalog) return;
    this._placingCatalog = null;
    this._furnitureGhostPos = null;
    this._qeRotate.reset();
    this.placement?.cancel(false);
    if (fireEvent) this.app.fire("ui:placement-ended", {});
    this._render();
  }

  /** Mirror the 2D placement ghost into the blurred 3D scene behind the editor. */
  _sync3dPlacementGhost() {
    if (!this.placement || !this._placingCatalog || !this._furnitureGhostPos) return;
    const { x, z } = this._pxToWorld(this._furnitureGhostPos.px, this._furnitureGhostPos.py);
    this.placement.updatePlanPreview(x, z, this._placingCatalog.rotY ?? 0);
  }

  async _commitFurniturePlace(p) {
    if (this._furnitureCommitting || !this._placingCatalog || !this.net) return;
    this._furnitureCommitting = true;

    const placing = this._placingCatalog;
    const asset_id = placing.asset_id;
    const name = placing.meta?.name ?? "Item";
    const fp = placing.meta?.footprint ?? { w: 1, d: 1 };
    const rotation_y = placing.rotY ?? 0;
    const anchor = this._furnitureGhostPos ?? p;
    const constrained = this._constrainFurniturePlanPx(anchor.px, anchor.py, fp, rotation_y, 1);
    const { x, z } = this._pxToWorld(constrained.px, constrained.py);
    const floorY = this.scene?.room?.floorY ?? 0;

    // End placing mode before the network round-trip so a second click cannot re-commit.
    this._placingCatalog = null;
    this._furnitureGhostPos = null;
    this._qeRotate.reset();
    this.placement?.cancel(false);
    this.app.fire("ui:placement-ended", { asset_id });

    try {
      await this.net.request(
        "viewport:place_furniture",
        { asset_id, position: [x, floorY, z], rotation_y, scale: 1 },
        12_000,
      );
      this._status(`${name} placed — pick another item from the catalog.`);
      this.app.fire("ui:placement-hint", { text: `${name} placed — pick from the catalog to place another.` });
    } catch (err) {
      this._status(err?.message ?? "Could not place here — pick the item again to retry.", true);
      this.app.fire("ui:placement-hint", { text: err?.message ?? "Could not place here.", error: true });
    } finally {
      this._furnitureCommitting = false;
      this.placement?.cancel(false);
      this._render();
    }
  }

  _removeFurniture(localId) {
    const f = this.model?.furniture?.find((x) => x.id === localId);
    if (!f) return;
    if (f.placementId) this.scene?.removePlacement(f.placementId);
    this.model.furniture = this.model.furniture.filter((x) => x.id !== localId);
    if (this.selected?.kind === "furniture" && this.selected.id === localId) this.selected = null;
    this._status("Furniture removed.");
    this._render();
  }

  _furnitureItem(localId) {
    return this.model?.furniture?.find((x) => x.id === localId) ?? null;
  }

  _qeRotateActive() {
    return Boolean(this._placingCatalog) || this.selected?.kind === "furniture";
  }

  /** Capture-phase Q/E — runs before plan-editor stopPropagation blocks bubbling. */
  _handleQeKeyDown(e) {
    if (!this.open || !this._qeRotateActive()) return;
    if (e.ctrlKey || e.metaKey) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === "input" || tag === "select" || tag === "textarea") return;
    if (e.key === "q" || e.key === "Q") {
      this._qeRotate.onKeyDown("q", e.repeat);
      e.preventDefault();
      e.stopPropagation();
    } else if (e.key === "e" || e.key === "E") {
      this._qeRotate.onKeyDown("e", e.repeat);
      e.preventDefault();
      e.stopPropagation();
    }
  }

  _handleQeKeyUp(e) {
    if (!this.open) return;
    const tag = e.target?.tagName?.toLowerCase();
    if (tag === "input" || tag === "select" || tag === "textarea") return;
    if (e.key === "q" || e.key === "Q" || e.key === "e" || e.key === "E") {
      this._qeRotate.onKeyUp(e.key);
      e.preventDefault();
      e.stopPropagation();
    }
  }

  _getQERotateYaw() {
    if (this._placingCatalog) return this._placingCatalog.rotY ?? 0;
    if (this.selected?.kind === "furniture") return this._furnitureItem(this.selected.id)?.rotY ?? 0;
    return 0;
  }

  _setQERotateYaw(y) {
    if (this._placingCatalog) this._placingCatalog.rotY = y;
    else if (this.selected?.kind === "furniture") {
      const f = this._furnitureItem(this.selected.id);
      if (f) f.rotY = y;
    }
  }

  _onQERotateChanged() {
    if (this._placingCatalog) {
      if (this._furnitureGhostPos) {
        const fp = this._placingCatalog.meta?.footprint ?? { w: 1, d: 1 };
        this._furnitureGhostPos = this._constrainFurniturePlanPx(
          this._furnitureGhostPos.px,
          this._furnitureGhostPos.py,
          fp,
          this._placingCatalog.rotY ?? 0,
          1,
        );
      }
      this._sync3dPlacementGhost();
      this._render();
      return;
    }
    if (this.selected?.kind === "furniture") {
      const f = this._furnitureItem(this.selected.id);
      if (!f) return;
      const meta = this._catalogMeta(f.asset_id);
      const fp = meta?.footprint ?? { w: 1, d: 1 };
      const c = this._constrainFurniturePlanPx(f.px, f.py, fp, f.rotY ?? 0, 1, f.placementId ?? null);
      f.px = c.px;
      f.py = c.py;
      const payload = this._applyFurnitureToScene(f);
      this._throttledFurnitureNet(payload);
      this._render();
    }
  }

  /** Push furniture pose from plan px/py + rotY into the live 3D scene. */
  _applyFurnitureToScene(f) {
    return applyFurniturePoseToScene(this.model, this.scene, f);
  }

  _throttledFurnitureNet(payload) {
    if (!payload || !this.net) return;
    const now = performance.now();
    if (now - this._furnitureSendAt < FURNITURE_NET_THROTTLE_MS) return;
    this._furnitureSendAt = now;
    sendFurnitureTransform(this.net, payload);
  }

  _sendFurnitureNet(payload) {
    if (!payload || !this.net) return;
    this._furnitureSendAt = performance.now();
    sendFurnitureTransform(this.net, payload);
  }

  _moveFurniture(localId, p, render = true) {
    const f = this._furnitureItem(localId);
    if (!f) return;
    const meta = this._catalogMeta(f.asset_id);
    const fp = meta?.footprint ?? { w: 1, d: 1 };
    const c = this._constrainFurniturePlanPx(p.px, p.py, fp, f.rotY ?? 0, 1, f.placementId ?? null);
    f.px = c.px;
    f.py = c.py;
    const payload = this._applyFurnitureToScene(f);
    this._throttledFurnitureNet(payload);
    if (render) this._render();
  }

  _rotateFurniture(localId, deltaDeg) {
    const f = this._furnitureItem(localId);
    if (!f) return;
    const meta = this._catalogMeta(f.asset_id);
    const fp = meta?.footprint ?? { w: 1, d: 1 };
    f.rotY = normalizeYaw((f.rotY ?? 0) + deltaDeg);
    const c = this._constrainFurniturePlanPx(f.px, f.py, fp, f.rotY, 1, f.placementId ?? null);
    f.px = c.px;
    f.py = c.py;
    const payload = this._applyFurnitureToScene(f);
    this._sendFurnitureNet(payload);
    this._render();
  }

  _furniturePointerAngle(f, p) {
    return (Math.atan2(p.py - f.py, p.px - f.px) * 180) / Math.PI;
  }

  _svgEsc(s) {
    return String(s ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/"/g, "&quot;");
  }

  // ---- edit history (undo / redo) -------------------------------------------

  /** Serialize the current model to a snapshot string (Map -> array of nodes). */
  _serializeModel() {
    const m = this.model;
    if (!m) return null;
    return JSON.stringify({
      naturalW: m.naturalW,
      naturalH: m.naturalH,
      metersPerPixel: m.metersPerPixel,
      height: m.height,
      nextNode: m.nextNode,
      nextEdge: m.nextEdge,
      nextOpening: m.nextOpening,
      nextLight: m.nextLight ?? 1,
      nextSwitch: m.nextSwitch ?? 1,
      nodes: [...m.nodes.values()].map((n) => ({ ...n })),
      edges: m.edges.map((e) => ({ ...e })),
      openings: m.openings.map((o) => ({ ...o })),
      lights: (m.lights ?? []).map((l) => ({ ...l })),
      switches: (m.switches ?? []).map((s) => ({ ...s })),
      roomMeta: (this._roomMeta ?? []).map((r) => ({ ...r })),
      graphMeterBounds: m.graphMeterBounds ?? null,
      floor: m.floor ? { ...m.floor } : null,
    });
  }

  /** Replace the live model from a snapshot string and re-render. */
  _restoreModel(json) {
    if (!json) return;
    const s = JSON.parse(json);
    const m = this._newModel(s.naturalW, s.naturalH, s.metersPerPixel, s.height);
    m.nextNode = s.nextNode;
    m.nextEdge = s.nextEdge;
    m.nextOpening = s.nextOpening;
    m.nextLight = s.nextLight ?? 1;
    m.nextSwitch = s.nextSwitch ?? 1;
    for (const n of s.nodes ?? []) m.nodes.set(String(n.id), { ...n });
    m.edges = (s.edges ?? []).map((e) => ({ ...e }));
    m.openings = (s.openings ?? []).map((o) => ({ ...o }));
    m.lights = (s.lights ?? []).map((l) => ({ ...l }));
    m.switches = (s.switches ?? []).map((sw) => ({ ...sw }));
    this.model = m;
    this._roomMeta = (s.roomMeta ?? []).map((r) => ({ ...r }));
    m.graphMeterBounds = s.graphMeterBounds ?? null;
    m.floor = s.floor ? { ...s.floor, fillet: { nw: 0, ne: 0, se: 0, sw: 0, ...(s.floor.fillet ?? {}) } } : null;
    if (m.floor) this._ensureFloorCorners(m);
    // Drop transient interaction state that may now reference gone ids.
    this.selected = null;
    this.selectedRoom = null;
    this._wallChain = null;
    this._drag = null;
    this._syncRoomCtl();
    this._computePxOrigin();
    this._syncFurnitureFromScene();
    if (this._isFloorOnlyMode()) this._syncFloorLive();
    this._render();
  }

  /** Start a fresh history with the freshly built model as the baseline. */
  _resetHistory() {
    const snap = this._serializeModel();
    this._planHistory.reset(snap);
    this._sessionModelBaseline = snap;
    this._topologyBaseline = this._wallsStructuralFingerprint();
    this._lightsBaseline = this._lightsFingerprint();
    this._openingsBaseline = this._openingsSnapshot();
    this._roomMetaBaseline = JSON.stringify(this._roomMeta ?? []);
    this._bindEdgesToSceneWalls();
    if (this._roomMeta?.some((m) => isBalconyType(m.type))) this._syncBalconyMetaLive();
    this._captureSceneSnapshot();
    this._updateHistButtons();
  }

  _captureSceneSnapshot() {
    const room = this.scene?.room;
    if (!room) {
      this._sceneSnapshot = null;
      return;
    }
    this._sceneSnapshot = {
      openings: JSON.parse(JSON.stringify(room.openings ?? [])),
      walls: JSON.parse(JSON.stringify(room.walls ?? [])),
      lights: JSON.parse(JSON.stringify(room.lights ?? [])),
      switches: JSON.parse(JSON.stringify(room.switches ?? [])),
    };
  }

  _restoreSceneSnapshot() {
    if (!this._sceneSnapshot || !this.scene?.room) return;
    const room = this.scene.room;
    const snap = this._sceneSnapshot;

    const snapWallIds = new Set(snap.walls.map((w) => w.id));
    const curWallIds = new Set((room.walls ?? []).map((w) => w.id));
    const wallRemove = [...curWallIds].filter((id) => !snapWallIds.has(id));
    const wallUpsert = snap.walls.filter((w) => {
      if (!curWallIds.has(w.id)) return true;
      const cur = room.walls.find((x) => x.id === w.id);
      return JSON.stringify(cur) !== JSON.stringify(w);
    });
    if (wallRemove.length || wallUpsert.length) {
      this._applyTopologyPatchClient({ upsert: wallUpsert, remove: wallRemove }, { quiet: true });
    }

    const curOpenings = room.openings ?? [];
    const snapOpenings = snap.openings ?? [];
    const snapOpeningIds = new Set(snapOpenings.map((o) => o.id));
    const curOpeningIds = new Set(curOpenings.map((o) => o.id));
    const openingRemove = [...curOpeningIds].filter((id) => !snapOpeningIds.has(id));
    const openingUpsert = snapOpenings.filter((o) => {
      if (!curOpeningIds.has(o.id)) return true;
      const cur = curOpenings.find((x) => x.id === o.id);
      return JSON.stringify(cur) !== JSON.stringify(o);
    });
    if (openingRemove.length || openingUpsert.length) {
      this._commitOpeningVisuals({ upsert: openingUpsert, remove: openingRemove }, { quiet: true });
    }

    if (snap.lights !== undefined && this.scene.applyLightsPatch) {
      this.scene.applyLightsPatch({
        lights: JSON.parse(JSON.stringify(snap.lights ?? [])),
        switches: JSON.parse(JSON.stringify(snap.switches ?? [])),
      });
    }
  }

  /** Pre-bind plan edges to live wall ids so opening edits don't spuriously dirty topology. */
  _bindEdgesToSceneWalls() {
    const room = this.scene?.room;
    if (!room || !this.model?.edges?.length) return;
    for (const edge of this.model.edges) {
      const A = this.model.nodes.get(String(edge.a));
      const B = this.model.nodes.get(String(edge.b));
      if (!A || !B) continue;
      const wa = this._pxToWorld(A.px, A.py);
      const wb = this._pxToWorld(B.px, B.py);
      const wall = edge.sceneWallId
        ? room.walls?.find((w) => w.id === edge.sceneWallId)
        : matchWallForSegment(room, wa.x, wa.z, wb.x, wb.z);
      if (wall) edge.sceneWallId = wall.id;
    }
  }

  _openingsSnapshot() {
    return JSON.stringify(
      (this.model?.openings ?? []).map((o) => ({
        id: o.id,
        edge: o.edge,
        type: o.type,
        t: o.t,
        w: o.w,
        sill: o.sill,
        hinge: o.hinge,
        swing: o.swing,
        curtain: o.curtain,
        sceneId: o.sceneId,
      })),
    );
  }

  /** Wall/node fingerprint — excludes lights/switches and edge.sceneWallId. */
  _wallsStructuralFingerprint() {
    const m = this.model;
    if (!m) return null;
    return JSON.stringify({
      naturalW: m.naturalW,
      naturalH: m.naturalH,
      metersPerPixel: m.metersPerPixel,
      height: m.height,
      nextNode: m.nextNode,
      nextEdge: m.nextEdge,
      nodes: [...m.nodes.values()].map((n) => ({ id: n.id, px: round2(n.px), py: round2(n.py), filletRadius: round2(n.filletRadius ?? 0) })),
      edges: m.edges.map((e) => ({ id: e.id, a: e.a, b: e.b })),
      graphMeterBounds: m.graphMeterBounds ?? null,
      floor: m.floor
        ? (() => {
            this._ensureFloorCorners(m);
            const c = m.floor.corners ?? {};
            const verts = floorVertexRing(m.floor);
            const corner = (id, fbPx, fbPy) => ({
              px: round2(c[id]?.px ?? fbPx),
              py: round2(c[id]?.py ?? fbPy),
            });
            return {
              x0: round2(m.floor.x0),
              y0: round2(m.floor.y0),
              x1: round2(m.floor.x1),
              y1: round2(m.floor.y1),
              vertices: verts.map((v) => ({ id: v.id, px: round2(v.px), py: round2(v.py) })),
              corners: {
                nw: corner("nw", m.floor.x0, m.floor.y0),
                ne: corner("ne", m.floor.x1, m.floor.y0),
                se: corner("se", m.floor.x1, m.floor.y1),
                sw: corner("sw", m.floor.x0, m.floor.y1),
              },
              fillet: {
                nw: round2(m.floor.fillet?.nw ?? 0),
                ne: round2(m.floor.fillet?.ne ?? 0),
                se: round2(m.floor.fillet?.se ?? 0),
                sw: round2(m.floor.fillet?.sw ?? 0),
              },
            };
          })()
        : null,
    });
  }

  _lightsFingerprint() {
    const m = this.model;
    if (!m) return null;
    return JSON.stringify({
      nextLight: m.nextLight ?? 1,
      nextSwitch: m.nextSwitch ?? 1,
      lights: (m.lights ?? []).map((l) => ({ ...l, px: round2(l.px), py: round2(l.py) })),
      switches: (m.switches ?? []).map((s) => ({ ...s, px: round2(s.px), py: round2(s.py) })),
    });
  }

  _wallsDirty() {
    const snap = this._wallsStructuralFingerprint();
    if (!snap || !this._topologyBaseline) return true;
    return snap !== this._topologyBaseline;
  }

  _lightsDirty() {
    const snap = this._lightsFingerprint();
    if (!snap || !this._lightsBaseline) return true;
    return snap !== this._lightsBaseline;
  }

  /** @deprecated use _wallsDirty */
  _topologyDirty() {
    return this._wallsDirty();
  }

  _openingsDirty() {
    return this._openingsSnapshot() !== this._openingsBaseline;
  }

  _roomMetaDirty() {
    return JSON.stringify(this._roomMeta ?? []) !== this._roomMetaBaseline;
  }

  /** Convert a plan-editor opening to a live scene opening record. */
  _planOpeningToWorld(op) {
    if (!op || !this.model || !this.scene?.room) return null;
    const edge = this.model.edges.find((e) => e.id === op.edge);
    if (!edge) return null;
    const A = this.model.nodes.get(String(edge.a));
    const B = this.model.nodes.get(String(edge.b));
    if (!A || !B) return null;
    const wa = this._pxToWorld(A.px, A.py);
    const wb = this._pxToWorld(B.px, B.py);
    const room = this.scene.room;
    let wall = edge.sceneWallId ? room.walls?.find((w) => w.id === edge.sceneWallId) : null;
    if (!wall) wall = matchWallForSegment(room, wa.x, wa.z, wb.x, wb.z);
    if (!wall) return null;
    edge.sceneWallId = wall.id;

    const lenPx = Math.hypot(B.px - A.px, B.py - A.py) || 1;
    const tCenter = (op.t + op.w / 2) / lenPx;
    const cx = wa.x + (wb.x - wa.x) * tCenter;
    const cz = wa.z + (wb.z - wa.z) * tCenter;
    const mpp = this.model.metersPerPixel > 0 ? this.model.metersPerPixel : 0.02;
    const width = Math.max(0.4, op.w * mpp);
    const floorY = this.scene.room.floorY ?? 0;
    const isWindow = op.type === "window";
    const height = isWindow ? Math.max(0.6, Number(op.height) || 1.46) : 2.1;
    const sill = isWindow ? Number(op.sill) || 0.9 : 0;
    const cy = isWindow ? floorY + sill + height / 2 : floorY + height / 2;
    const sceneId = op.sceneId ?? `pe_op_${op.id}`;
    op.sceneId = sceneId;

    return {
      id: sceneId,
      type: op.type === "window" ? "window" : "door",
      wall: wall.id,
      center: [cx, cy, cz],
      width,
      height,
      ...(isWindow
        ? { sill, curtain: windowHasCurtain(op) }
        : { hinge: normHinge(op.hinge), swing: normSwing(op.swing) }),
    };
  }

  /** Schema-safe opening for server persistence (floor-relative center, no viewport extras). */
  _openingForServer(world, planOp) {
    if (!world?.id || !world?.wall) return null;
    const floorY = this.scene?.room?.floorY ?? 0;
    const out = {
      id: world.id,
      type: world.type === "window" ? "window" : "door",
      wall: world.wall,
      center: [world.center[0], (world.center[1] ?? 0) - floorY, world.center[2]],
      width: world.width,
      height: world.height,
    };
    if (planOp?.edge != null) out.edgeId = String(planOp.edge);
    if (planOp?.id != null) out.sourceId = String(planOp.id);
    if (out.type === "window") {
      out.sill = world.sill ?? 0.9;
      out.curtain = world.curtain !== false;
    } else {
      out.hinge = world.hinge ?? "left";
      out.swing = world.swing ?? "in";
    }
    return out;
  }

  _commitOpeningVisuals({ upsert = [], remove = [] }, opts = {}) {
    if (!this.scene) return;
    const patch = { upsert: upsert.filter(Boolean), remove: remove.filter(Boolean) };
    if (!patch.upsert.length && !patch.remove.length) return;
    if (patch.remove.length && this.scene.roomRoot) {
      destroyOpeningMeshes(this.scene.roomRoot, patch.remove);
    }
    const wallIds = this.scene.applyOpeningPatch(patch);
    for (const id of patch.remove) this.doors?.removeOpening(id);
    for (const op of patch.upsert) {
      if (op.type === "door") this.doors?.patchOpening(op);
    }
    const rebuildIds = [
      ...new Set([...(wallIds ?? []), ...patch.upsert.map((o) => o.wall).filter(Boolean)]),
    ];
    if (rebuildIds.length && this.scene.roomRoot) {
      rebuildWallSubset(this.app, this.scene.room, this.scene.roomRoot, rebuildIds, {
        skyTexture: this.scene.skyTexture,
      });
    }
    if (!opts.quiet) this.net?.notify("viewport:opening_patch", patch);
  }

  _applyOpeningPatchClient(patch, opts = {}) {
    this._commitOpeningVisuals(patch, opts);
  }

  _syncOpeningById(localId) {
    this._bindEdgesToSceneWalls();
    const op = this.model?.openings?.find((o) => o.id === localId);
    if (!op) return;
    const world = this._planOpeningToWorld(op);
    if (!world) return;
    this._applyOpeningPatchClient({ upsert: [world], remove: [] });
  }

  _removeOpeningFromScene(op) {
    const ids = new Set();
    if (op?.sceneId) ids.add(String(op.sceneId));
    if (op?.id != null) ids.add(`pe_op_${op.id}`);
    const world = this._planOpeningToWorld(op);
    if (world?.id) ids.add(String(world.id));
    if (world?.center) {
      const type = op?.type === "window" ? "window" : "door";
      for (const so of this.scene?.room?.openings ?? []) {
        if ((so.type === "window" ? "window" : "door") !== type) continue;
        const sx = Number(so.center?.[0]);
        const sz = Number(so.center?.[2]);
        if (!Number.isFinite(sx) || !Number.isFinite(sz)) continue;
        if (Math.hypot(sx - world.center[0], sz - world.center[2]) < 0.75) ids.add(String(so.id));
      }
    }
    this._applyOpeningPatchClient({ upsert: [], remove: [...ids] });
  }

  async _persistOpenings(graph = null) {
    const built = graph ?? this._buildGraph();
    const empty = this._graphIsEmpty(built);
    this._bindEdgesToSceneWalls();
    this._refreshWorldMapping();

    const upsertVisual = [];
    const upsertServer = [];
    for (const op of this.model?.openings ?? []) {
      const world = this._planOpeningToWorld(op);
      if (!world) continue;
      upsertVisual.push(world);
      const srv = this._openingForServer(world, op);
      if (srv) upsertServer.push(srv);
    }
    const remove = [];
    const liveIds = new Set(upsertVisual.map((o) => o.id));
    const liveSourceIds = new Set(
      (this.model?.openings ?? []).map((o) => String(o.sceneId ?? `pe_op_${o.id}`)).filter(Boolean),
    );
    for (const op of this.scene?.room?.openings ?? []) {
      if (op.type !== "door" && op.type !== "window") continue;
      if (!liveIds.has(op.id) && !liveSourceIds.has(op.id)) remove.push(op.id);
    }

    // Update live 3D immediately — do not wait on the orchestrator round-trip.
    this._commitOpeningVisuals({ upsert: upsertVisual, remove }, { quiet: true });

    const graphOut = this._canSyncGraph(built) || empty ? built : null;
    let persisted = true;
    if (this.planAnalyzer) {
      persisted = await this.planAnalyzer.patchOpenings({ upsert: upsertServer, remove, graph: graphOut });
      if (!persisted && this.net) {
        this.net.notify("viewport:opening_patch", { upsert: upsertServer, remove });
        persisted = true;
        this._status("Openings saved in scene — start the orchestrator to persist to disk.", false);
      }
      if (!persisted && graphOut && this._persistGraphToLocalStore(graphOut, upsertServer)) {
        persisted = true;
        this._status("Saved locally — start the orchestrator to persist to disk.", false);
      }
    }
    if (!persisted) return false;
    this._openingsBaseline = this._openingsSnapshot();
    return true;
  }

  _computeTopologyPatch() {
    const room = this.scene?.room;
    if (!room || !this.model) return { upsert: [], remove: [] };
    return computeTopologyPatch(this.model, room, (px, py) => this._pxToWorld(px, py));
  }

  /** Persist patch — after live clear, room.walls is empty so recover ids from the session snapshot. */
  _topologyPatchForPersist(empty = false) {
    const patch = this._computeTopologyPatch();
    if (empty && !patch.remove.length) {
      const ids = (this._sceneSnapshot?.walls ?? []).map((w) => w.id).filter(Boolean);
      if (ids.length) patch.remove = ids;
    }
    return patch;
  }

  _applyTopologyPatchClient(patch, opts = {}) {
    if (!this.scene) return;
    const upsert = patch?.upsert ?? [];
    const remove = patch?.remove ?? [];
    if (!upsert.length && !remove.length) return;
    const result = this.scene.applyTopologyPatch({ upsert, remove });
    if (!opts.quiet) this.net?.notify("viewport:topology_patch", { upsert, remove });
    return result;
  }

  _syncTopologyLive() {
    if (!this.scene?.room || !this.model) return;
    this._refreshWorldMapping();
    if (this.model.floor) {
      this._snapWallGraphToFloorRing();
      this._syncFloorDataToRoom();
    }
    const patch = this._computeTopologyPatch();
    if (!patch.upsert.length && !patch.remove.length) return;
    this._applyTopologyPatchClient(patch);
    const touched = new Set([...patch.upsert.map((w) => w.id), ...patch.remove]);
    for (const op of this.model.openings ?? []) {
      const edge = this.model.edges.find((e) => e.id === op.edge);
      if (edge?.sceneWallId && touched.has(edge.sceneWallId)) this._syncOpeningById(op.id);
    }
  }

  /** Room face index for a light/switch — wall switches use their snapped edge. */
  _groupAt(px, py, edgeId = null) {
    const faces = this._computeFaces();
    if (edgeId != null) {
      for (let i = 0; i < faces.length; i++) {
        if (faces[i].edges.includes(edgeId)) return i;
      }
    }
    let best = -1;
    let bestArea = Infinity;
    for (let i = 0; i < faces.length; i++) {
      if (this._pointInPoly({ px, py }, faces[i].polygon) && faces[i].areaPx < bestArea) {
        bestArea = faces[i].areaPx;
        best = i;
      }
    }
    return best;
  }

  /** Map planner light/switch pixels to live room records (meters, room-origin). */
  _buildRoomLightsAndSwitches() {
    const m = this.model;
    if (!m) return { lights: [], switches: [] };
    const round3 = (v) => Math.round(v * 1000) / 1000;
    const lights = (m.lights ?? []).map((l) => {
      const { x, z } = this._pxToWorld(l.px, l.py);
      return {
        id: `L${l.id}`,
        fixture: l.fixture || DEFAULT_LIGHT,
        group: this._groupAt(l.px, l.py),
        position: [round3(x), round3(z)],
        on: true,
      };
    });
    const switches = (m.switches ?? []).map((s) => {
      this._ensureSwitchPlacement(s);
      const { x, z } = this._pxToWorld(s.px, s.py);
      const wn = this._pxNormalToWorld(s.nx, s.ny, s.px, s.py);
      return {
        id: `S${s.id}`,
        group: this._groupAt(s.px, s.py, s.edge),
        position: [round3(x), round3(z)],
        normal: [round3(wn.x), round3(wn.z)],
        on: s.on !== false,
      };
    });
    return { lights, switches };
  }

  _commitLightsAndSwitches() {
    if (!this.scene?.applyLightsPatch) return;
    const { lights, switches } = this._buildRoomLightsAndSwitches();
    this.scene.applyLightsPatch({ lights, switches });
  }

  /** Persist ceiling lights / switches when walls did not change. */
  async _persistLightsAndSwitchesOnly() {
    const graph = this._buildGraph();
    if (!this._canSyncGraph(graph) && !this._graphIsEmpty(graph)) return false;
    const ok = await this._persistGraphRemote(graph);
    if (!ok) return false;
    this._commitLightsAndSwitches();
    this._lightsBaseline = this._lightsFingerprint();
    return true;
  }

  async _persistTopology() {
    const empty = this.model && this.model.edges.length === 0 && this.model.nodes.size === 0;
    if (empty) {
      this._ensureClearedFloorShell(false);
    } else {
      if (this.model.floor) this._snapWallGraphToFloorRing();
      const patch = this._topologyPatchForPersist(false);
      const graph = this._buildGraph();
      if (!this._canSyncGraph(graph)) return false;
      const ok = await this._persistGraphRemote(graph, patch);
      if (!ok) {
        // Orchestrator offline — patch the live scene locally so Apply still updates 3D.
        this._applyTopologyPatchClient(patch);
        for (const op of this.model?.openings ?? []) this._syncOpeningById(op.id);
        return false;
      }
      this._commitLightsAndSwitches();
      this._topologyBaseline = this._wallsStructuralFingerprint();
      this._lightsBaseline = this._lightsFingerprint();
      return true;
    }
    const graph = this._buildGraph();
    if (!this._graphIsEmpty(graph)) return false;
    const patch = this._topologyPatchForPersist(true);
    const ok = await this._persistGraphRemote(graph, patch);
    if (!ok) return false;
    this._commitLightsAndSwitches();
    this._topologyBaseline = this._wallsStructuralFingerprint();
    this._lightsBaseline = this._lightsFingerprint();
    return true;
  }

  /**
   * Record the current model as a new history step (called AFTER a discrete edit).
   * Truncates any redo tail and caps the stack so a long session can't grow
   * unbounded. No-op if nothing actually changed since the last snapshot.
   */
  _record() {
    const snap = this._serializeModel();
    if (!snap) return;
    if (this._planHistory.record(snap)) this._updateHistButtons();
  }

  _undo() {
    const snap = this._planHistory.undo();
    if (!snap) return;
    this._restoreModel(snap);
    this._updateHistButtons();
    this._status("Undid last change.");
  }

  _redo() {
    const snap = this._planHistory.redo();
    if (!snap) return;
    this._restoreModel(snap);
    this._updateHistButtons();
    this._status("Redid change.");
  }

  _updateHistButtons() {
    const undo = this.$?.("undo");
    const redo = this.$?.("redo");
    if (undo) undo.disabled = !this._planHistory.canUndo;
    if (redo) redo.disabled = !this._planHistory.canRedo;
  }

  /** Persisted plan was cleared (empty graph) — 2D editor should show floor only. */
  _isClearedSpec(spec) {
    const g = spec?.graph;
    const empty =
      !g ||
      (Array.isArray(g.nodes) &&
        Array.isArray(g.edges) &&
        g.nodes.length === 0 &&
        g.edges.length === 0);
    if (spec?.layoutMode === "cleared" && empty) return true;
    return Boolean(g && Array.isArray(g.nodes) && Array.isArray(g.edges) && empty);
  }

  _buildModel(spec, natural) {
    // Drop any stale state from a previous plan.
    this.selectedRoom = null;
    this._roomMeta = []; // room-type tags (centroid-keyed), reseeded from the graph below

    const W = natural.w;
    const H = natural.h;
    const height = Number(spec?.height) || Number(spec?.graph?.height) || 2.7;

    if (this._isClearedSpec(spec)) {
      this.model = this._modelFromCleared(spec, W, H, height);
      this._computePxOrigin();
      this._syncFurnitureFromScene();
      return;
    }

    const graph = spec?.graph;
    const useGraph =
      graph && Array.isArray(graph.nodes) && graph.nodes.length >= 3 && spec?.layoutMode !== "synthesis";

    if (useGraph) {
      this.model = this._modelFromGraph(graph, W, H, height);
      if (this.model) {
        this._normalizeTopology();
        this._computePxOrigin();
        this._mergeAllOpenings(spec);
        this._bindEdgesToSceneWalls();
        this._syncFurnitureFromScene();
        this._mergeAllLightsAndSwitches(spec);
        return;
      }
    }
    // Fallback: seed an editable px-graph by fitting the room rectangles onto the
    // image. Geometry won't match the drawing yet — the user drags to align it.
    this.model = this._modelFromRooms(spec, W, H, height);
    this._normalizeTopology();
    this._computePxOrigin();
    this._mergeAllOpenings(spec);
    this._bindEdgesToSceneWalls();
    this._syncFurnitureFromScene();
    this._mergeAllLightsAndSwitches(spec);
  }

  /**
   * After the model is built, pull in any doors/windows that exist in the live 3D
   * scene, saved builtOpenings, or the last editor session — without duplicating
   * openings already loaded from the persisted graph.
   */
  _mergeAllOpenings(spec) {
    const graphOpeningsInSpec = (spec?.graph?.openings?.length ?? 0) > 0;
    const graphOpeningsLoaded = graphOpeningsInSpec && (this.model?.openings?.length ?? 0) > 0;
    const hasBuiltInSpec = (spec?.builtOpenings?.length ?? 0) > 0;

    if (!graphOpeningsLoaded) {
      this._mergeBuiltOpeningsPx(spec);
      // builtOpenings are derived from the live 3D room — skip scene re-import.
      if (!hasBuiltInSpec) this._mergeSceneOpeningsPx();
    } else if (this.model.openings.length === 0) {
      this._mergeBuiltOpeningsPx(spec);
      this._mergeSceneOpeningsPx();
    }

    this._mergeDraftOpenings();
    this._dedupeOpenings(this.model);
    this._reconcileRoomMetaFaceKeys();
  }

  /** Attach stable face keys to balcony tags loaded without them (older saves). */
  _reconcileRoomMetaFaceKeys() {
    if (!this.model || !this._roomMeta?.length) return;
    const faces = this._computeFaces();
    for (const m of this._roomMeta) {
      if (m.faceKey) continue;
      let best = null;
      let bestD = this._roomTagTol();
      for (const face of faces) {
        if (!this._pointInPoly({ px: m.cx, py: m.cy }, face.polygon)) continue;
        const d = Math.hypot(m.cx - face.centroid[0], m.cy - face.centroid[1]);
        if (d < bestD) {
          bestD = d;
          best = face;
        }
      }
      if (best) m.faceKey = best.key;
    }
  }

  /** Convert a persisted graph light/switch record to plan-editor pixel coords. */
  _graphMarkerToPx(item) {
    if (Number.isFinite(Number(item?.px)) && Number.isFinite(Number(item?.py))) {
      return { px: Number(item.px), py: Number(item.py) };
    }
    const gx = Number(item?.x);
    const gy = Number(item?.y);
    if (!Number.isFinite(gx) || !Number.isFinite(gy)) return null;
    const gb = this.model?.graphMeterBounds;
    const pb = this.model?.pxBounds;
    const spans = this._meterSpans();
    if (gb && pb && spans) {
      return this._worldToPx(gx - gb.minMx, gy - gb.minMz);
    }
    const mpp = this.model?.metersPerPixel > 0 ? this.model.metersPerPixel : 0.02;
    return { px: gx / mpp, py: gy / mpp };
  }

  _markerPosKey(px, py) {
    return `${Math.round(px)},${Math.round(py)}`;
  }

  /** Pull lights/switches from the persisted graph when the model missed them. */
  _mergeGraphLightsAndSwitches(spec) {
    const m = this.model;
    const graph = spec?.graph;
    if (!m || !graph) return;

    const lightKeys = new Set((m.lights ?? []).map((l) => this._markerPosKey(l.px, l.py)));
    const switchKeys = new Set((m.switches ?? []).map((s) => this._markerPosKey(s.px, s.py)));

    for (const l of graph.lights ?? []) {
      const pos = this._graphMarkerToPx(l);
      if (!pos) continue;
      const key = this._markerPosKey(pos.px, pos.py);
      if (lightKeys.has(key)) continue;
      if (!m.lights) m.lights = [];
      m.lights.push({ id: m.nextLight++, px: pos.px, py: pos.py, fixture: l.fixture || DEFAULT_LIGHT });
      lightKeys.add(key);
    }

    for (const s of graph.switches ?? []) {
      const pos = this._graphMarkerToPx(s);
      if (!pos) continue;
      const key = this._markerPosKey(pos.px, pos.py);
      if (switchKeys.has(key)) continue;
      if (!m.switches) m.switches = [];
      m.switches.push({
        id: m.nextSwitch++,
        px: pos.px,
        py: pos.py,
        on: s.on !== false,
        ...(Number.isFinite(Number(s.nx)) && Number.isFinite(Number(s.ny)) ? { nx: Number(s.nx), ny: Number(s.ny) } : {}),
      });
      switchKeys.add(key);
    }
    for (const sw of m.switches ?? []) this._ensureSwitchPlacement(sw);
  }

  /** Pull lights/switches from the live 3D room (source of truth after Apply). */
  _mergeSceneLightsAndSwitches() {
    const m = this.model;
    if (!m || !this.scene?.room) return;
    this._refreshWorldMapping();

    const lightKeys = new Set((m.lights ?? []).map((l) => this._markerPosKey(l.px, l.py)));
    const switchKeys = new Set((m.switches ?? []).map((s) => this._markerPosKey(s.px, s.py)));

    for (const l of this.scene.room.lights ?? []) {
      const pos = l.position;
      if (!Array.isArray(pos) || pos.length < 2) continue;
      const { px, py } = this._worldToPx(pos[0], pos[1]);
      const key = this._markerPosKey(px, py);
      if (lightKeys.has(key)) continue;
      if (!m.lights) m.lights = [];
      m.lights.push({ id: m.nextLight++, px, py, fixture: l.fixture || DEFAULT_LIGHT });
      lightKeys.add(key);
    }

    for (const sw of this.scene.room.switches ?? []) {
      const pos = sw.position;
      if (!Array.isArray(pos) || pos.length < 2) continue;
      const { px, py } = this._worldToPx(pos[0], pos[1]);
      const key = this._markerPosKey(px, py);
      if (switchKeys.has(key)) continue;
      if (!m.switches) m.switches = [];
      m.switches.push({ id: m.nextSwitch++, px, py, on: sw.on !== false });
      switchKeys.add(key);
    }
    for (const sw of m.switches ?? []) this._ensureSwitchPlacement(sw);
  }

  _mergeAllLightsAndSwitches(spec) {
    this._mergeGraphLightsAndSwitches(spec);
    this._mergeSceneLightsAndSwitches();
  }

  /** Project a built/scene opening (meters) onto the nearest model edge. */
  _projectOpeningRecordToModel(op, m) {
    const cx = Number(op.center?.[0]);
    const cz = Number(op.center?.[2]);
    if (!Number.isFinite(cx) || !Number.isFinite(cz)) return false;

    this._refreshWorldMapping();
    const { px: pcx, py: pcy } = this._worldToPx(cx, cz);
    let best = null;
    let bestD = Infinity;

    if (op.wall) {
      const wallEdge = m.edges.find((e) => e.sceneWallId === op.wall);
      if (wallEdge) best = wallEdge;
    }
    if (!best) {
      for (const e of m.edges) {
        const A = m.nodes.get(String(e.a));
        const B = m.nodes.get(String(e.b));
        const d = this._pointSegDist(pcx, pcy, A.px, A.py, B.px, B.py);
        if (d < bestD) {
          bestD = d;
          best = e;
        }
      }
    }
    if (!best) return false;

    const A = m.nodes.get(String(best.a));
    const B = m.nodes.get(String(best.b));
    const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
    const ux = (B.px - A.px) / len;
    const uy = (B.py - A.py) / len;
    const tc = (pcx - A.px) * ux + (pcy - A.py) * uy;
    const type = op.type === "window" ? "window" : "door";
    const mpp = m.metersPerPixel > 0 ? m.metersPerPixel : 0.02;
    const wPx = (Number(op.width) || (type === "window" ? 1.4 : 0.9)) / mpp;
    let t = tc - wPx / 2;
    if (t < 0) t = 0;
    if (t > len - wPx) t = Math.max(0, len - wPx);
    if (this._openingOverlapsExisting(m, best.id, t, wPx, type)) return false;

    let sceneId = op.id ?? op.sceneId;
    if (!sceneId && this.scene?.room?.openings?.length) {
      const hit = this.scene.room.openings.find((so) => {
        const st = so.type === "window" ? "window" : "door";
        if (st !== type) return false;
        const sx = Number(so.center?.[0]);
        const sz = Number(so.center?.[2]);
        return Number.isFinite(sx) && Number.isFinite(sz) && Math.hypot(sx - cx, sz - cz) < 0.6;
      });
      if (hit?.id) sceneId = hit.id;
    }
    const localId = m.nextOpening++;
    return this._pushOpening(m, {
      id: localId,
      sceneId: sceneId ?? `pe_op_${localId}`,
      edge: best.id,
      type,
      t,
      w: wPx,
      sill:
        type === "window"
          ? op.sill != null
            ? Number(op.sill)
            : Math.max(0, (Number(op.center?.[1]) || 0.9) - (Number(op.height) || 1.2) / 2)
          : 0,
      ...(type === "door"
        ? { hinge: normHinge(op.hinge), swing: normSwing(op.swing) }
        : { curtain: windowHasCurtain(op) }),
    });
  }

  _mergeBuiltOpeningsPx(spec) {
    const m = this.model;
    if (!m?.edges?.length) return;
    const existing = new Set(m.openings.map((o) => o.sceneId).filter(Boolean));
    for (const op of spec?.builtOpenings ?? []) {
      if (op.id && existing.has(op.id)) continue;
      if (this._projectOpeningRecordToModel(op, m)) {
        const added = m.openings[m.openings.length - 1];
        if (added?.sceneId) existing.add(added.sceneId);
      }
    }
  }

  _mergeSceneOpeningsPx() {
    const m = this.model;
    if (!m?.edges?.length) return;
    const existing = new Set(m.openings.map((o) => o.sceneId).filter(Boolean));
    for (const op of this.scene?.room?.openings ?? []) {
      if (op.type !== "door" && op.type !== "window") continue;
      if (existing.has(op.id)) continue;
      if (this._projectOpeningRecordToModel({ ...op, wall: op.wall }, m)) {
        const added = m.openings[m.openings.length - 1];
        if (added?.sceneId) existing.add(added.sceneId);
      }
    }
  }

  /** Restore openings from the last editor session (close without Apply). */
  _mergeDraftOpenings() {
    const m = this.model;
    if (!m?.edges?.length) return;
    let draft = [];
    try {
      const raw = sessionStorage.getItem(PLAN_OPENINGS_DRAFT_KEY);
      if (raw) draft = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(draft)) return;

    const existingScene = new Set(m.openings.map((o) => o.sceneId).filter(Boolean));
    const usedLocal = new Set(m.openings.map((o) => o.id));
    for (const o of draft) {
      if (o.sceneId && existingScene.has(o.sceneId)) continue;
      if (!m.edges.some((e) => e.id === o.edge)) continue;
      if (this._openingOverlapsExisting(m, o.edge, o.t, o.w, o.type ?? "door")) continue;
      const copy = { ...o };
      if (copy.id == null || usedLocal.has(copy.id)) copy.id = m.nextOpening++;
      else m.nextOpening = Math.max(m.nextOpening, copy.id + 1);
      usedLocal.add(copy.id);
      if (!this._pushOpening(m, copy)) continue;
      if (copy.sceneId) existingScene.add(copy.sceneId);
    }
  }

  _modelFromGraph(graph, W, H, height) {
    const m = this._newModel(W, H, Number(graph.metersPerPixel) || 0, height);
    const idMap = new Map();
    let pxValid = true;
    for (const n of graph.nodes ?? []) {
      const hasPx = Number.isFinite(Number(n.px)) && Number.isFinite(Number(n.py));
      if (!hasPx) pxValid = false;
    }
    // If nodes have no pixel coords, derive them from meters using metersPerPixel
    // (or a unit fit) so they still render somewhere editable.
    const mpp = Number(graph.metersPerPixel) > 0 ? Number(graph.metersPerPixel) : 0;
    let fit = null;
    if (!pxValid) fit = this._fitMetersToImage(graph.nodes.map((n) => [Number(n.x) || 0, Number(n.y) || 0]), W, H);

    for (const n of graph.nodes ?? []) {
      const id = m.nextNode++;
      idMap.set(String(n.id), id);
      let px;
      let py;
      if (Number.isFinite(Number(n.px)) && Number.isFinite(Number(n.py))) {
        px = Number(n.px);
        py = Number(n.py);
      } else if (fit) {
        px = fit.ox + (Number(n.x) || 0) * fit.s;
        py = fit.oy + (Number(n.y) || 0) * fit.s;
      } else {
        px = W / 2;
        py = H / 2;
      }
      m.nodes.set(String(id), { id, px, py, filletRadius: Number(n.fillet_radius) || 0 });
    }
    if (!(m.metersPerPixel > 0)) {
      m.metersPerPixel = mpp > 0 ? mpp : fit ? 1 / fit.s : 0.02;
    }

    const edgeKeyToId = new Map();
    for (const e of graph.edges ?? []) {
      const a = idMap.get(String(e.node_start));
      const b = idMap.get(String(e.node_end));
      if (a == null || b == null || a === b) continue;
      const id = m.nextEdge++;
      m.edges.push({ id, a, b });
      edgeKeyToId.set(String(e.id), id);
    }

    for (const o of graph.openings ?? []) {
      const eid = edgeKeyToId.get(String(o.edge_id));
      if (eid == null) continue;
      const edge = m.edges.find((x) => x.id === eid);
      if (!edge) continue;
      const A = m.nodes.get(String(edge.a));
      const B = m.nodes.get(String(edge.b));
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const tPx = Number.isFinite(Number(o.distance_from_start_node_pixels))
        ? Number(o.distance_from_start_node_pixels)
        : Number.isFinite(Number(o.distance_from_start_node_meters)) && m.metersPerPixel > 0
          ? Number(o.distance_from_start_node_meters) / m.metersPerPixel
          : len * 0.4;
      const wPx = Number.isFinite(Number(o.width_pixels))
        ? Number(o.width_pixels)
        : Number.isFinite(Number(o.width_meters)) && m.metersPerPixel > 0
          ? Number(o.width_meters) / m.metersPerPixel
          : len * 0.2;
      const type = String(o.type ?? "door").includes("window") ? "window" : "door";
      const localId = m.nextOpening++;
      this._pushOpening(m, {
        id: localId,
        sceneId: o.id != null ? String(o.id) : `pe_op_${localId}`,
        edge: eid,
        type,
        t: tPx,
        w: wPx,
        sill: type === "window" ? Number(o.sill_meters) || 0.9 : 0,
        ...(type === "door"
          ? { hinge: normHinge(o.hinge ?? o.hinge_side), swing: normSwing(o.swing) }
          : { curtain: o.curtain !== false }),
      });
    }
    // Seed room-type tags (balconies) from the persisted graph so toggles survive
    // reloads. Each room's centroid is computed from its edge endpoints in px.
    if (!this._roomMeta) this._roomMeta = [];
    for (const r of graph.rooms ?? []) {
      if (!isBalconyType(r.type)) continue;
      let cx = 0;
      let cy = 0;
      let n = 0;
      const seen = new Set();
      for (const ge of r.edges ?? []) {
        const eid = edgeKeyToId.get(String(ge));
        const edge = eid != null && m.edges.find((x) => x.id === eid);
        if (!edge) continue;
        for (const nid of [edge.a, edge.b]) {
          if (seen.has(nid)) continue;
          seen.add(nid);
          const nd = m.nodes.get(String(nid));
          if (!nd) continue;
          cx += nd.px;
          cy += nd.py;
          n++;
        }
      }
      if (n) {
        const plannerEdgeIds = (r.edges ?? [])
          .map((ge) => edgeKeyToId.get(String(ge)))
          .filter((eid) => eid != null);
        this._roomMeta.push({
          faceKey: [...plannerEdgeIds].sort((a, b) => a - b).join(","),
          cx: cx / n,
          cy: cy / n,
          type: "balcony",
          viewLen: Number(r.viewLength) || DEFAULT_VIEW_LEN,
        });
      }
    }

    this.model = m;
    this._setGraphMeterBoundsFromGraph(graph);

    // Lights + switches survive reloads via the persisted graph (px/py preferred,
    // meters fallback through graph bounds / mpp).
    for (const l of graph.lights ?? []) {
      const pos = this._graphMarkerToPx(l);
      if (!pos) continue;
      m.lights.push({ id: m.nextLight++, px: pos.px, py: pos.py, fixture: l.fixture || DEFAULT_LIGHT });
    }
    for (const s of graph.switches ?? []) {
      const pos = this._graphMarkerToPx(s);
      if (!pos) continue;
      m.switches.push({
        id: m.nextSwitch++,
        px: pos.px,
        py: pos.py,
        on: s.on !== false,
        ...(Number.isFinite(Number(s.nx)) && Number.isFinite(Number(s.ny)) ? { nx: Number(s.nx), ny: Number(s.ny) } : {}),
      });
    }
    for (const sw of m.switches ?? []) this._ensureSwitchPlacement(sw);

    if (m.nodes.size >= 3 && m.edges.length >= 3) {
      const envW = Number(graph.envelope?.width) || 8;
      const envD = Number(graph.envelope?.depth) || 8;
      this._applyGraphFloorToModel(m, graph, W, H, envW, envD);
      return m;
    }
    return null;
  }

  _fitMetersToImage(pts, W, H) {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const [x, y] of pts) {
      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
    const pw = Math.max(0.1, maxX - minX);
    const ph = Math.max(0.1, maxY - minY);
    const s = Math.min(W / pw, H / ph) * 0.86;
    const ox = (W - pw * s) / 2 - minX * s;
    const oy = (H - ph * s) / 2 - minY * s;
    return { s, ox, oy };
  }

  /** Rescale traced px coords when the plan image natural size changes between sessions. */
  _reconcilePlanNaturalSize(m, fromW, fromH, toW, toH) {
    if (!m || !(fromW > 0) || !(fromH > 0) || !(toW > 0) || !(toH > 0)) return;
    if (fromW === toW && fromH === toH) return;
    const sx = toW / fromW;
    const sy = toH / fromH;
    const scalePx = (px, py) => ({ px: px * sx, py: py * sy });
    if (m.floor) {
      const f = m.floor;
      m.floor = { ...f, x0: f.x0 * sx, y0: f.y0 * sy, x1: f.x1 * sx, y1: f.y1 * sy };
      if (f.corners) {
        for (const id of ["nw", "ne", "se", "sw"]) {
          const p = f.corners[id];
          if (p) {
            m.floor.corners[id] = { px: p.px * sx, py: p.py * sy };
          }
        }
      }
      if (Array.isArray(f.vertices)) {
        for (const v of f.vertices) {
          v.px *= sx;
          v.py *= sy;
        }
      }
    }
    if (m.metersPerPixel > 0) m.metersPerPixel = m.metersPerPixel / sx;
    for (const [, n] of m.nodes ?? []) {
      const p = scalePx(n.px, n.py);
      n.px = p.px;
      n.py = p.py;
    }
    for (const l of m.lights ?? []) {
      const p = scalePx(l.px, l.py);
      l.px = p.px;
      l.py = p.py;
    }
    for (const s of m.switches ?? []) {
      const p = scalePx(s.px, s.py);
      s.px = p.px;
      s.py = p.py;
    }
    for (const f of m.furniture ?? []) {
      const p = scalePx(f.px, f.py);
      f.px = p.px;
      f.py = p.py;
    }
  }

  /** Restore floor rect / polygon from persisted graph fields. */
  _applyGraphFloorToModel(m, graph, W, H, envW, envD) {
    if (!m || !graph) return;
    const fp = graph.floor_px;
    const ff = graph.floor_fillet;
    const fillet = {
      nw: Number(ff?.nw) || 0,
      ne: Number(ff?.ne) || 0,
      se: Number(ff?.se) || 0,
      sw: Number(ff?.sw) || 0,
    };
    const env = graph.envelope;
    const envMinX = Number(env?.minX);
    const envMinZ = Number(env?.minZ);
    const envBoxW = Number(env?.width) || envW;
    const envBoxD = Number(env?.depth) || envD;
    const mpp = m.metersPerPixel > 0 ? m.metersPerPixel : 0.02;
    const fc = graph.floor_corners;
    const fv = graph.floor_vertices;
    const cornerValid = (p) => p && Number.isFinite(Number(p.px)) && Number.isFinite(Number(p.py));
    const hasSavedVertices = Array.isArray(fv) && fv.length >= 3 && fv.every((v) => cornerValid(v));
    const hasSavedCorners =
      cornerValid(fc?.nw) && cornerValid(fc?.ne) && cornerValid(fc?.se) && cornerValid(fc?.sw);
    if (hasSavedVertices) {
      m.floor = {
        x0: 0,
        y0: 0,
        x1: 1,
        y1: 1,
        fillet,
        vertices: fv.map((v) => ({ id: String(v.id), px: Number(v.px), py: Number(v.py) })),
        nextVertex: fv.length + 1,
      };
      syncFloorLegacyFields(m.floor);
    } else if (hasSavedCorners) {
      m.floor = {
        x0: 0,
        y0: 0,
        x1: 1,
        y1: 1,
        fillet,
        corners: {
          nw: { px: Number(fc.nw.px), py: Number(fc.nw.py) },
          ne: { px: Number(fc.ne.px), py: Number(fc.ne.py) },
          se: { px: Number(fc.se.px), py: Number(fc.se.py) },
          sw: { px: Number(fc.sw.px), py: Number(fc.sw.py) },
        },
      };
      this._syncFloorRectFromCorners(m);
    } else if (fp && [fp.x0, fp.y0, fp.x1, fp.y1].every((v) => Number.isFinite(Number(v)))) {
      m.floor = { x0: Number(fp.x0), y0: Number(fp.y0), x1: Number(fp.x1), y1: Number(fp.y1), fillet };
      this._ensureFloorCorners(m);
    } else if (Number.isFinite(envMinX) && Number.isFinite(envMinZ) && mpp > 0) {
      m.floor = {
        x0: envMinX / mpp,
        y0: envMinZ / mpp,
        x1: (envMinX + envBoxW) / mpp,
        y1: (envMinZ + envBoxD) / mpp,
        fillet,
      };
      this._ensureFloorCorners(m);
    }
    const savedNW = Number(graph.planNaturalW);
    const savedNH = Number(graph.planNaturalH);
    if (savedNW > 0 && savedNH > 0 && (savedNW !== W || savedNH !== H)) {
      this._reconcilePlanNaturalSize(m, savedNW, savedNH, W, H);
    }
    if (m.floor) this._ensureFloorRect(m);
  }

  /** Empty editable model for a cleared apartment (scale preserved, no walls). */
  _modelFromCleared(spec, W, H, height) {
    const graph = spec?.graph;
    const envW = Number(spec?.width) || Number(graph?.envelope?.width) || 8;
    const envD = Number(spec?.depth) || Number(graph?.envelope?.depth) || 8;
    let mpp = Number(graph?.metersPerPixel);
    let fit = null;
    if (!(mpp > 0)) {
      fit = this._fitMetersToImage(
        [
          [0, 0],
          [envW, envD],
        ],
        W,
        H,
      );
      mpp = 1 / fit.s;
    }
    const m = this._newModel(W, H, mpp, height);
    this._applyGraphFloorToModel(m, graph, W, H, envW, envD);
    if (!m.floor) {
      const fillet = {
        nw: Number(graph?.floor_fillet?.nw) || 0,
        ne: Number(graph?.floor_fillet?.ne) || 0,
        se: Number(graph?.floor_fillet?.se) || 0,
        sw: Number(graph?.floor_fillet?.sw) || 0,
      };
      if (!fit) {
        fit = this._fitMetersToImage(
          [
            [0, 0],
            [envW, envD],
          ],
          W,
          H,
        );
      }
      const toPx = (x, z) => [fit.ox + x * fit.s, fit.oy + z * fit.s];
      const [x0, y0] = toPx(0, 0);
      const [x1, y1] = toPx(envW, envD);
      m.floor = { x0, y0, x1, y1, fillet };
      this._ensureFloorCorners(m);
      this._ensureFloorRect(m);
    }
    m.graphMeterBounds = null;
    return m;
  }

  _modelFromRooms(spec, W, H, height) {
    const rooms = Array.isArray(spec?.rooms) ? spec.rooms : [];
    const envW = Number(spec?.width) || rooms.reduce((mx, r) => Math.max(mx, (r.x || 0) + (r.width || 0)), 0) || 8;
    const envD = Number(spec?.depth) || rooms.reduce((mx, r) => Math.max(mx, (r.z || 0) + (r.depth || 0)), 0) || 8;
    const fit = this._fitMetersToImage(
      [
        [0, 0],
        [envW, envD],
      ],
      W,
      H,
    );
    const m = this._newModel(W, H, 1 / fit.s, height);
    const toPx = (x, z) => [fit.ox + x * fit.s, fit.oy + z * fit.s];

    // Dedup nodes by quantized coordinate; dedup edges by node-pair.
    const nodeKey = (x, z) => `${Math.round(x * 1000)},${Math.round(z * 1000)}`;
    const nodeByKey = new Map();
    const nodeAt = (x, z) => {
      const k = nodeKey(x, z);
      if (nodeByKey.has(k)) return nodeByKey.get(k);
      const [px, py] = toPx(x, z);
      const id = m.nextNode++;
      m.nodes.set(String(id), { id, px, py });
      nodeByKey.set(k, id);
      return id;
    };
    const edgeByKey = new Map();
    const edgeAt = (aId, bId) => {
      const k = aId < bId ? `${aId}-${bId}` : `${bId}-${aId}`;
      if (edgeByKey.has(k)) return edgeByKey.get(k);
      const id = m.nextEdge++;
      m.edges.push({ id, a: aId, b: bId });
      edgeByKey.set(k, id);
      return id;
    };

    const sideEdge = (r, side) => {
      const x0 = r.x || 0;
      const z0 = r.z || 0;
      const x1 = x0 + (r.width || 0);
      const z1 = z0 + (r.depth || 0);
      if (side === "north") return edgeAt(nodeAt(x0, z0), nodeAt(x1, z0));
      if (side === "south") return edgeAt(nodeAt(x0, z1), nodeAt(x1, z1));
      if (side === "west") return edgeAt(nodeAt(x0, z0), nodeAt(x0, z1));
      return edgeAt(nodeAt(x1, z0), nodeAt(x1, z1)); // east
    };

    for (const r of rooms) {
      for (const side of ["north", "south", "west", "east"]) sideEdge(r, side);
    }

    // Seed balcony tags from the rect spec so synthesized balconies open to a view
    // and stay tagged when re-opened in the editor.
    if (!this._roomMeta) this._roomMeta = [];
    for (const r of rooms) {
      if (!isBalconyType(r.type)) continue;
      const [cx, cy] = toPx((r.x || 0) + (r.width || 0) / 2, (r.z || 0) + (r.depth || 0) / 2);
      this._roomMeta.push({ cx, cy, type: "balcony", viewLen: Number(r.viewLength) || DEFAULT_VIEW_LEN });
    }

    // Seed openings from each room's per-room openings (best-effort).
    for (const r of rooms) {
      for (const op of r.openings ?? []) {
        try {
          const side = String(op.wall ?? "").toLowerCase();
          if (!["north", "south", "west", "east"].includes(side)) continue;
          const eid = sideEdge(r, side);
          const edge = m.edges.find((x) => x.id === eid);
          const A = m.nodes.get(String(edge.a));
          const B = m.nodes.get(String(edge.b));
          const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
          // World start corner of the side + along-direction.
          const x0 = r.x || 0;
          const z0 = r.z || 0;
          let startWx;
          let startWz;
          let dirx;
          let dirz;
          if (side === "north") ((startWx = x0), (startWz = z0), (dirx = 1), (dirz = 0));
          else if (side === "south") ((startWx = x0), (startWz = z0 + (r.depth || 0)), (dirx = 1), (dirz = 0));
          else if (side === "west") ((startWx = x0), (startWz = z0), (dirx = 0), (dirz = 1));
          else ((startWx = x0 + (r.width || 0)), (startWz = z0), (dirx = 0), (dirz = 1));
          const dist = Number(op.distance) || 0;
          const [spx, spy] = toPx(startWx + dirx * dist, startWz + dirz * dist);
          // t = distance from edge.node_start (A) to the opening start point.
          let t = Math.hypot(spx - A.px, spy - A.py);
          if (t > len) t = len * 0.5;
          const type = op.type === "window" ? "window" : "door";
          const wPx = (Number(op.width) || (type === "window" ? 1.4 : 0.9)) / m.metersPerPixel;
          this._pushOpening(m, {
            id: m.nextOpening++,
            edge: eid,
            type,
            t,
            w: wPx,
            sill: type === "window" ? Number(op.sill) || 0.9 : 0,
            ...(type === "door"
              ? { hinge: normHinge(op.hinge), swing: normSwing(op.swing) }
              : { curtain: op.curtain !== false }),
          });
        } catch {
          /* skip an opening we can't place */
        }
      }
    }
    m.graphMeterBounds = { minMx: 0, minMz: 0, maxMx: envW, maxMz: envD };
    return m;
  }

  /** Perpendicular distance from point (px,py) to segment A-B (image px). */
  _pointSegDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax;
    const dy = by - ay;
    const L2 = dx * dx + dy * dy || 1;
    let t = ((px - ax) * dx + (py - ay) * dy) / L2;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }

  _openingWorldCenter(m, o) {
    const edge = m.edges.find((e) => e.id === o.edge);
    if (!edge) return null;
    const A = m.nodes.get(String(edge.a));
    const B = m.nodes.get(String(edge.b));
    if (!A || !B) return null;
    const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
    const tMid = o.t + o.w / 2;
    const u = tMid / len;
    const px = A.px + (B.px - A.px) * u;
    const py = A.py + (B.py - A.py) * u;
    return this._pxToWorld(px, py);
  }

  /** Same opening on one edge (interval overlap or nearly coincident midpoints). */
  _openingSpansOverlap(a, b) {
    if (a.edge !== b.edge) return false;
    const a0 = a.t;
    const a1 = a.t + a.w;
    const b0 = b.t;
    const b1 = b.t + b.w;
    if (a0 < b1 && b0 < a1) return true;
    const am = a.t + a.w / 2;
    const bm = b.t + b.w / 2;
    return Math.abs(am - bm) < Math.min(a.w, b.w) * 0.55;
  }

  /** Duplicate when spans overlap on one edge or world centers are within ~1 m. */
  _openingsAreDuplicates(m, a, b) {
    if (this._openingSpansOverlap(a, b)) return true;
    if (a.type !== b.type) return false;
    const ca = this._openingWorldCenter(m, a);
    const cb = this._openingWorldCenter(m, b);
    if (!ca || !cb) return false;
    const d = Math.hypot(ca.x - cb.x, ca.z - cb.z);
    const mpp = m.metersPerPixel > 0 ? m.metersPerPixel : 0.02;
    const mergeDist = Math.max(1.0, Math.max(a.w, b.w) * mpp * 0.65);
    return d < mergeDist;
  }

  _openingOverlapsList(m, list, candidate) {
    return (list ?? []).some((o) => this._openingsAreDuplicates(m, o, candidate));
  }

  _openingOverlapsExisting(m, edgeId, t, w, type = "door") {
    return this._openingOverlapsList(m, m.openings, { edge: edgeId, t, w, type });
  }

  /** Drop duplicate doors/windows on the same wall span (graph + built + scene merge). */
  _dedupeOpenings(m) {
    if (!m?.openings?.length) return;
    const sceneIds = new Set((this.scene?.room?.openings ?? []).map((o) => String(o.id)));
    const score = (o) => {
      let s = 0;
      const sid = String(o.sceneId ?? "");
      if (sid && sceneIds.has(sid)) s += 10;
      if (sid && !sid.startsWith("pe_op_")) s += 5;
      return s;
    };
    const kept = [];
    for (const o of [...m.openings].sort((a, b) => score(b) - score(a))) {
      if (this._openingOverlapsList(m, kept, o)) continue;
      kept.push(o);
    }
    m.openings = kept;
  }

  _pushOpening(m, opening) {
    if (this._openingOverlapsExisting(m, opening.edge, opening.t, opening.w, opening.type ?? "door")) return false;
    m.openings.push(opening);
    return true;
  }

  // ---- coordinate mapping ---------------------------------------------------

  /** Model units (image px) per on-screen pixel — for constant-size handles. */
  _unitPerScreen() {
    const vb = this._viewBox;
    const r = this.elSvg?.getBoundingClientRect();
    if (vb?.w > 0 && r?.width) return vb.w / r.width;
    const img = this.elImg?.getBoundingClientRect();
    if (!img?.width || !this.model) return 1;
    return this.model.naturalW / img.width;
  }

  /** Bounds of all editable geometry in model pixel space. */
  _modelContentBounds() {
    const m = this.model;
    if (!m) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
    let minX = 0;
    let minY = 0;
    let maxX = m.naturalW;
    let maxY = m.naturalH;
    const include = (px, py) => {
      if (Number.isFinite(px)) {
        minX = Math.min(minX, px);
        maxX = Math.max(maxX, px);
      }
      if (Number.isFinite(py)) {
        minY = Math.min(minY, py);
        maxY = Math.max(maxY, py);
      }
    };
    if (m.floor) {
      for (const c of this._floorCorners()) include(c.px, c.py);
    }
    for (const [, n] of m.nodes) include(n.px, n.py);
    for (const o of m.openings ?? []) {
      const edge = m.edges.find((e) => e.id === o.edge);
      if (!edge) continue;
      const A = m.nodes.get(String(edge.a));
      const B = m.nodes.get(String(edge.b));
      if (!A || !B) continue;
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      include(A.px + ux * o.t, A.py + uy * o.t);
      include(A.px + ux * (o.t + o.w), A.py + uy * (o.t + o.w));
    }
    for (const f of m.furniture ?? []) include(f.px, f.py);
    if (this._floorExpand) {
      include(this._floorExpand.start?.px, this._floorExpand.start?.py);
      for (const pt of this._floorExpand.points ?? []) include(pt.px, pt.py);
    }
    return { minX, minY, maxX, maxY };
  }

  /** Expand the SVG viewBox so floor/wall handles stay visible outside the traced image. */
  _computeViewBox(pad) {
    const b = this._modelContentBounds();
    const minX = b.minX - pad;
    const minY = b.minY - pad;
    const maxX = b.maxX + pad;
    const maxY = b.maxY + pad;
    return { x: minX, y: minY, w: Math.max(1, maxX - minX), h: Math.max(1, maxY - minY) };
  }

  /** Convert a pointer event to model (image-pixel) coordinates. */
  _eventToModel(e, viewBoxOverride = null) {
    const vb = viewBoxOverride ?? this._viewBox;
    const r = this.elSvg?.getBoundingClientRect();
    if (vb?.w > 0 && r?.width && r?.height) {
      return {
        px: vb.x + ((e.clientX - r.left) / r.width) * vb.w,
        py: vb.y + ((e.clientY - r.top) / r.height) * vb.h,
      };
    }
    const img = this.elImg?.getBoundingClientRect();
    if (!img?.width || !img?.height || !this.model) return { px: 0, py: 0 };
    return {
      px: ((e.clientX - img.left) / img.width) * this.model.naturalW,
      py: ((e.clientY - img.top) / img.height) * this.model.naturalH,
    };
  }

  // ---- zoom -----------------------------------------------------------------

  /** Scale the canvas so the full plan (or traced geometry) fits the stage. */
  _fitToView() {
    if (!this.model || !this.elStage) return;
    const stageW = this.elStage.clientWidth;
    const stageH = this.elStage.clientHeight;
    const estU = this._unitPerScreen();
    const vb = this._computeViewBox(this._renderPad(estU));
    const W = vb.w;
    const H = vb.h;
    if (!stageW || !stageH || !W || !H) return;

    const imageAspect = H / W;
    let fitZ = Math.min(1, stageH / (stageW * imageAspect));

    const pb = this.model.pxBounds;
    if (pb) {
      const cw = pb.maxPx - pb.minPx;
      const ch = pb.maxPy - pb.minPy;
      if (cw > 2 && ch > 2) {
        const margin = 1.12;
        const zContent = Math.min(vb.w / (cw * margin), vb.h / (ch * margin), 1);
        fitZ = Math.min(fitZ, zContent);
      }
    }

    this._setZoom(Math.max(0.25, fitZ));
    requestAnimationFrame(() => this._centerStageScroll());
  }

  _centerStageScroll() {
    if (!this.elStage || !this.elCanvas) return;
    const sw = this.elStage.clientWidth;
    const sh = this.elStage.clientHeight;
    const cw = this.elCanvas.offsetWidth;
    const ch = this.elCanvas.offsetHeight;
    const vb = this._viewBox;
    const pb = this.model?.pxBounds;
    if (pb && cw && ch && vb?.w && vb?.h) {
      const cx = (pb.minPx + pb.maxPx) / 2;
      const cy = (pb.minPy + pb.maxPy) / 2;
      const contentCenterX = ((cx - vb.x) / vb.w) * cw;
      const contentCenterY = ((cy - vb.y) / vb.h) * ch;
      this.elStage.scrollLeft = Math.max(0, contentCenterX - sw / 2);
      this.elStage.scrollTop = Math.max(0, contentCenterY - sh / 2);
    } else {
      this.elStage.scrollLeft = Math.max(0, (cw - sw) / 2);
      this.elStage.scrollTop = Math.max(0, (ch - sh) / 2);
    }
    requestAnimationFrame(() => this._syncStageScrollbar());
  }

  _setZoom(z) {
    this.zoom = Math.max(0.25, Math.min(6, z));
    if (this.elCanvas) this.elCanvas.style.width = `${this.zoom * 100}%`;
    if (this.elZoomLabel) this.elZoomLabel.textContent = `${Math.round(this.zoom * 100)}%`;
    this._render();
    requestAnimationFrame(() => this._syncStageScrollbar());
  }

  /** Apple-style overlay scrollbars (native OS bars bypass custom cursor). */
  _bindStageScrollbar() {
    if (!this.elStage) return;
    this._scrollFadeTimer = null;
    this.elStage.addEventListener(
      "scroll",
      () => {
        this._syncStageScrollbar();
        this._pulseStageScrollbar();
      },
      { passive: true },
    );
    this._bindScrollbarDrag(this.elScrollRailY, this.elScrollThumbY, "y");
    this._bindScrollbarDrag(this.elScrollRailX, this.elScrollThumbX, "x");
  }

  _pulseStageScrollbar() {
    for (const rail of [this.elScrollRailY, this.elScrollRailX]) {
      rail?.classList.add("is-scrolling");
    }
    clearTimeout(this._scrollFadeTimer);
    this._scrollFadeTimer = setTimeout(() => {
      for (const rail of [this.elScrollRailY, this.elScrollRailX]) {
        rail?.classList.remove("is-scrolling");
      }
    }, 900);
  }

  _syncStageScrollbar() {
    if (!this.open || !this.elStage) return;
    this._syncAxisScrollbar("y", this.elScrollRailY, this.elScrollThumbY);
    this._syncAxisScrollbar("x", this.elScrollRailX, this.elScrollThumbX);
  }

  _syncAxisScrollbar(axis, rail, thumb) {
    if (!rail || !thumb || !this.elStage) return;
    const isY = axis === "y";
    const scrollSize = isY ? this.elStage.scrollHeight : this.elStage.scrollWidth;
    const clientSize = isY ? this.elStage.clientHeight : this.elStage.clientWidth;
    const scrollPos = isY ? this.elStage.scrollTop : this.elStage.scrollLeft;

    if (scrollSize <= clientSize + 1) {
      rail.classList.remove("is-active");
      rail.hidden = true;
      return;
    }

    rail.hidden = false;
    rail.classList.add("is-active");
    const railSize = isY ? rail.clientHeight : rail.clientWidth;
    const minThumb = 32;
    const thumbSize = Math.max(minThumb, (clientSize / scrollSize) * railSize);
    const maxScroll = scrollSize - clientSize;
    const maxThumbTravel = Math.max(1, railSize - thumbSize);
    const thumbPos = maxScroll > 0 ? (scrollPos / maxScroll) * maxThumbTravel : 0;

    if (isY) {
      thumb.style.height = `${thumbSize}px`;
      thumb.style.width = "100%";
      thumb.style.top = `${thumbPos}px`;
      thumb.style.left = "0";
    } else {
      thumb.style.width = `${thumbSize}px`;
      thumb.style.height = "100%";
      thumb.style.left = `${thumbPos}px`;
      thumb.style.top = "0";
    }
  }

  _bindScrollbarDrag(rail, thumb, axis) {
    if (!rail || !thumb || !this.elStage) return;
    const isY = axis === "y";

    const scrollFromPointer = (clientPos) => {
      const railRect = rail.getBoundingClientRect();
      const railSize = isY ? railRect.height : railRect.width;
      const thumbSize = isY ? thumb.offsetHeight : thumb.offsetWidth;
      const scrollSize = isY ? this.elStage.scrollHeight : this.elStage.scrollWidth;
      const clientSize = isY ? this.elStage.clientHeight : this.elStage.clientWidth;
      const maxScroll = Math.max(0, scrollSize - clientSize);
      const maxThumbTravel = Math.max(1, railSize - thumbSize);
      const local = isY ? clientPos - railRect.top - thumbSize / 2 : clientPos - railRect.left - thumbSize / 2;
      const ratio = local / maxThumbTravel;
      const next = ratio * maxScroll;
      if (isY) this.elStage.scrollTop = Math.max(0, Math.min(maxScroll, next));
      else this.elStage.scrollLeft = Math.max(0, Math.min(maxScroll, next));
    };

    thumb.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      rail.classList.add("is-dragging");
      const startPointer = isY ? e.clientY : e.clientX;
      const startScroll = isY ? this.elStage.scrollTop : this.elStage.scrollLeft;
      const railSize = isY ? rail.clientHeight : rail.clientWidth;
      const thumbSize = isY ? thumb.offsetHeight : thumb.offsetWidth;
      const scrollSize = isY ? this.elStage.scrollHeight : this.elStage.scrollWidth;
      const clientSize = isY ? this.elStage.clientHeight : this.elStage.clientWidth;
      const maxScroll = Math.max(0, scrollSize - clientSize);
      const maxThumbTravel = Math.max(1, railSize - thumbSize);
      const ratio = maxScroll / maxThumbTravel;

      const onMove = (ev) => {
        const delta = (isY ? ev.clientY : ev.clientX) - startPointer;
        const next = startScroll + delta * ratio;
        if (isY) this.elStage.scrollTop = Math.max(0, Math.min(maxScroll, next));
        else this.elStage.scrollLeft = Math.max(0, Math.min(maxScroll, next));
      };
      const onUp = () => {
        rail.classList.remove("is-dragging");
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    });

    rail.addEventListener("mousedown", (e) => {
      if (e.button !== 0 || e.target === thumb) return;
      e.preventDefault();
      scrollFromPointer(isY ? e.clientY : e.clientX);
    });
  }

  _onWheel(e) {
    if (!this.open) return;
    e.preventDefault();
    this._userZoomed = true;
    const factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    this._setZoom(this.zoom * factor);
  }

  // ---- pointer interaction --------------------------------------------------

  /** Prefer delete + door controls even when overlapping pick layers. */
  _pointerHit(e) {
    const stack = typeof e.composedPath === "function" ? e.composedPath() : [e.target];
    for (const el of stack) {
      const kind = el?.dataset?.kind;
      if (!kind) continue;
      if (
        kind === "odelete" ||
        kind === "edelete" ||
        kind === "ldelete" ||
        kind === "swdelete" ||
        kind === "fdelete" ||
        kind === "frotate" ||
        kind === "dhinge" ||
        kind === "dswing" ||
        kind === "wcurtain" ||
        kind === "floor-corner"
      ) {
        return {
          kind,
          id: kind === "floor-corner" ? el.dataset.id : el.dataset.id != null ? Number(el.dataset.id) : null,
          role: null,
        };
      }
    }
    const p = this._eventToModel(e);
    for (const el of stack) {
      if (el?.dataset?.kind === "node" && el.dataset.id != null) {
        const id = Number(el.dataset.id);
        const node = this.model?.nodes?.get(String(id));
        if (node) {
          const u = this._unitPerScreen();
          const cornerSnap = SNAP_SCREEN_PX * u * 2.5;
          if (Math.hypot(node.px - p.px, node.py - p.py) <= cornerSnap) {
            return { kind: "node", id, role: null };
          }
        }
      }
    }
    const t = e.target;
    const kind = t?.dataset?.kind;
    return {
      kind,
      id:
        kind === "floor-corner"
          ? t.dataset.id
          : t?.dataset?.id != null
            ? Number(t.dataset.id)
            : null,
      role: t?.dataset?.role ?? null,
    };
  }

  _onPlanRightClick(e) {
    if (!this.open || this.tool !== "floorExpand" || !this._floorExpand) return false;
    e.preventDefault();
    e.stopPropagation();
    this._cancelFloorExpand();
    return true;
  }

  _onPlanContextMenu(e) {
    this._onPlanRightClick(e);
  }

  _onPointerDown(e) {
    if (!this.model) return;
    if (e.button === 2) {
      this._onPlanRightClick(e);
      return;
    }
    if (e.button !== 0) return;
    this._handlePlanPointerDown(e, { fromSvg: true });
  }

  /** Catch clicks on the image / canvas margin when the SVG has no painted pick target. */
  _onCanvasPointerDown(e) {
    if (!this.model) return;
    if (e.button === 2) {
      this._onPlanRightClick(e);
      return;
    }
    if (e.button !== 0) return;
    if (this.tool !== "floorExpand" && this.tool !== "calibrate" && this.tool !== "wall") return;
    if (this.elSvg?.contains(e.target)) return;
    e.preventDefault();
    this._handlePlanPointerDown(e, { fromCanvas: true });
  }

  _handlePlanPointerDown(e, _source = {}) {
    const p = this._eventToModel(e);

    if (this._placingCatalog) {
      e.preventDefault();
      e.stopPropagation();
      void this._commitFurniturePlace(p);
      return;
    }

    let { kind, id, role } = this._pointerHit(e);

    // Red ✕ on a door/window removes it, regardless of the active tool.
    if (kind === "odelete" && id != null) {
      const doomed = this.model.openings.find((o) => o.id === id);
      if (doomed) this._removeOpeningFromScene(doomed);
      this.model.openings = this.model.openings.filter((o) => o.id !== id);
      if (this.selected?.kind === "opening" && this.selected.id === id) this.selected = null;
      this._status("Opening removed.");
      this._render();
      this._record();
      return;
    }

    // Red ✕ on a light / switch removes it, regardless of the active tool.
    if (kind === "ldelete" && id != null) {
      this.model.lights = this.model.lights.filter((l) => l.id !== id);
      if (this.selected?.kind === "light" && this.selected.id === id) this.selected = null;
      this._status("Light removed.");
      this._render();
      this._record();
      return;
    }
    if (kind === "swdelete" && id != null) {
      this.model.switches = this.model.switches.filter((s) => s.id !== id);
      if (this.selected?.kind === "switch" && this.selected.id === id) this.selected = null;
      this._status("Switch removed.");
      this._render();
      this._record();
      return;
    }
    if (kind === "fdelete" && id != null) {
      this._removeFurniture(id);
      return;
    }
    if (kind === "frotate" && id != null) {
      const f = this._furnitureItem(id);
      if (f) {
        this.selected = { kind: "furniture", id };
        this._drag = {
          type: "frotate",
          id,
          startAng: this._furniturePointerAngle(f, p),
          startRotY: f.rotY ?? 0,
        };
        this._status("Drag to rotate · Q/E fine-tune · release to commit.");
      }
      return;
    }
    if (kind === "edelete" && id != null) {
      this._removeEdge(id);
      return;
    }

    // Door swing controls: flip the hinge jamb (left/right) or the side the leaf
    // opens toward (in/out), regardless of the active tool.
    if ((kind === "dhinge" || kind === "dswing") && id != null) {
      const op = this.model.openings.find((o) => o.id === id && o.type === "door");
      if (op) {
        if (kind === "dhinge") {
          op.hinge = normHinge(op.hinge) === "left" ? "right" : "left";
          this._status(`Door hinge: ${op.hinge === "left" ? "left jamb" : "right jamb"}.`);
        } else {
          op.swing = normSwing(op.swing) === "in" ? "out" : "in";
          this._status(`Door swings ${op.swing === "in" ? "inward" : "outward"}.`);
        }
        this.selected = { kind: "opening", id };
        this._render();
        this._syncOpeningById(id);
        this._record();
      }
      return;
    }

    // Window curtain toggle — add/remove Unity curtains on this opening.
    if (kind === "wcurtain" && id != null) {
      const op = this.model.openings.find((o) => o.id === id && o.type === "window");
      if (op) {
        op.curtain = !windowHasCurtain(op);
        this.selected = { kind: "opening", id };
        this._status(op.curtain ? "Curtains added." : "Curtains removed.");
        this._render();
        this._syncOpeningById(id);
        this._record();
      }
      return;
    }

    if (this.tool === "calibrate") {
      this._calibrateClick(p);
      return;
    }
    if (this.tool === "delete") {
      ({ kind, id } = this._pickOpeningOverEdge(kind, id, p));
      this._deleteAt(kind, id, p);
      return;
    }
    if (this.tool === "wall") {
      this._wallClick(p);
      return;
    }
    if (this.tool === "floorExpand") {
      const now = performance.now();
      if (this._lastExpandPointer?.id === e.pointerId && now - this._lastExpandPointer.t < 400) return;
      this._lastExpandPointer = { id: e.pointerId, t: now };
      e.preventDefault();
      e.stopPropagation();
      this._floorExpandClick(p, e.shiftKey);
      return;
    }
    if (this.tool === "door" || this.tool === "window") {
      this._addOpeningAt(p, this.tool);
      return;
    }
    if (this.tool === "light") {
      this._addLightAt(p);
      return;
    }
    if (this.tool === "switch") {
      this._addSwitchAt(p);
      return;
    }
    if (this.tool === "room") {
      const face = this._faceAtPoint(p);
      if (face) {
        // Detach into an independent block so move/rotate is rigid and neighbours
        // are not dragged into diagonals. Walls re-weld on Apply.
        this.selectedRoom = this._detachRoom(face);
        // Remember where the room's tag sits so it follows the block when moved.
        this._dragRoomTagFrom = this._roomCentroidPx(this._selectedRoomNodeIds());
        this._drag = { type: "room", last: p };
        this._status(`Room selected — set its type (Room/Balcony), drag to move, or rotate.`);
      } else {
        this.selectedRoom = null;
      }
      this._syncRoomCtl();
      this._render();
      return;
    }

    // select / move
    ({ kind, id } = this._pickOpeningOverEdge(kind, id, p));
    if (kind === "edge" && id != null) {
      this.selected = { kind: "edge", id };
      this.selectedRoom = null;
      this._syncCornerCtl();
    } else if (kind === "node") {
      this._drag = { type: "node", id };
      this.selected = { kind: "node", id };
      this.selectedRoom = null;
      this._syncCornerCtl();
    } else if (kind === "floor-corner" && id) {
      this._drag = this._beginFloorCornerDrag(id, p);
      this.selected = { kind: "floorCorner", id };
      this.selectedRoom = null;
      this._status("Drag corner smoothly — release to snap · Shift = axis lock");
      this._syncCornerCtl();
    } else if (kind === "ohandle") {
      this._drag = { type: "ohandle", id, role };
      this.selected = { kind: "opening", id };
    } else if (kind === "opening") {
      this._drag = { type: "opening", id };
      this.selected = { kind: "opening", id };
    } else if (kind === "light") {
      this._drag = { type: "light", id };
      this.selected = { kind: "light", id };
      const l = this.model.lights.find((x) => x.id === id);
      if (l && this._lightTypePicker) {
        this._lightTypePicker.value = l.fixture || DEFAULT_LIGHT;
        this._lightFixture = this._lightTypePicker.value;
      }
    } else if (kind === "switch") {
      this._drag = { type: "switch", id };
      this.selected = { kind: "switch", id };
    } else if (kind === "furniture" && id != null) {
      this.selected = { kind: "furniture", id };
      this.selectedRoom = null;
      if (this.tool === "select") {
        this._drag = { type: "furniture", id };
      }
      const meta = this._catalogMeta(this._furnitureItem(id)?.asset_id);
      this._status(meta ? `${meta.name} — drag to move · Q/E rotate · Del remove` : "Drag to move · Q/E rotate");
    } else if (this.tool === "select") {
      const floorPick = this._pickFloorCorner(p);
      if (floorPick) {
        this.selected = { kind: "floorCorner", id: floorPick.id };
        this.selectedRoom = null;
        this._drag = this._beginFloorCornerDrag(floorPick.id, p);
        this._status("Drag corner smoothly — release to snap · Shift = axis lock");
        this._syncCornerCtl();
      } else {
        const pick = this._pickEdgeOrNode(p);
        if (pick?.kind === "edge") {
          this.selected = { kind: "edge", id: pick.id };
          this.selectedRoom = null;
        } else {
          this.selected = null;
        }
      }
    } else {
      this.selected = null;
    }
    this._syncCornerCtl();
    this._render();
  }

  _onPointerMove(e) {
    if (!this.open || !this.model) return;
    if (this._placingCatalog) {
      const fp = this._placingCatalog.meta?.footprint ?? { w: 1, d: 1 };
      const raw = this._eventToModel(e);
      this._furnitureGhostPos = this._constrainFurniturePlanPx(
        raw.px,
        raw.py,
        fp,
        this._placingCatalog.rotY ?? 0,
        1,
      );
      this._sync3dPlacementGhost();
      this._render();
      return;
    }
    if (this.tool === "floorExpand" && this._floorExpand && !this._drag) {
      const p = this._eventToModel(e);
      const snap = SNAP_SCREEN_PX * this._unitPerScreen();
      this._floorExpandHvSnap = e.shiftKey;
      const prev =
        this._floorExpand.points.length > 0
          ? this._floorExpand.points[this._floorExpand.points.length - 1]
          : { px: this._floorExpand.start.px, py: this._floorExpand.start.py };
      const cursor = this._snapFloorExpandPoint(p.px, p.py, prev, e.shiftKey);
      const nextHover = this._floorExpandCloseHover(this.model.floor, p, snap);
      const cursorMoved =
        cursor.px !== this._floorExpandCursor?.px || cursor.py !== this._floorExpandCursor?.py;
      const hoverMoved =
        nextHover?.px !== this._floorExpandHover?.px || nextHover?.py !== this._floorExpandHover?.py;
      if (cursorMoved || (hoverMoved && (nextHover || this._floorExpandHover))) {
        this._floorExpandCursor = cursor;
        this._floorExpandHover = nextHover;
        if (!this._expandPreviewRaf) {
          this._expandPreviewRaf = requestAnimationFrame(() => {
            this._expandPreviewRaf = 0;
            this._render();
          });
        }
      }
      return;
    }
    if (!this._drag) return;
    const p = this._eventToModel(e);
    if (this._drag.type === "node") this._moveNode(this._drag.id, p, e.shiftKey);
    else if (this._drag.type === "floorCorner") {
      const vb = this._drag.viewBox ?? this._viewBox;
      const mp = this._eventToModel(e, vb);
      const gp = {
        px: mp.px - (this._drag.grab?.ox ?? 0),
        py: mp.py - (this._drag.grab?.oy ?? 0),
      };
      if (e.shiftKey && this._drag.anchor) {
        const a = this._drag.anchor;
        if (Math.abs(gp.px - a.px) <= Math.abs(gp.py - a.py)) gp.py = a.py;
        else gp.px = a.px;
      }
      this._moveFloorCorner(this._drag.id, gp, true);
      if (!this._floorDragRaf) {
        this._floorDragRaf = requestAnimationFrame(() => {
          this._floorDragRaf = 0;
          this._render();
        });
      }
      return;
    } else if (this._drag.type === "opening") this._slideOpening(this._drag.id, p);
    else if (this._drag.type === "ohandle") this._resizeOpening(this._drag.id, this._drag.role, p);
    else if (this._drag.type === "room") this._dragRoom(p);
    else if (this._drag.type === "light") this._moveMarker(this.model.lights, this._drag.id, p);
    else if (this._drag.type === "switch") this._moveMarker(this.model.switches, this._drag.id, p);
    else if (this._drag.type === "furniture") this._moveFurniture(this._drag.id, p, false);
    else if (this._drag.type === "frotate") {
      const f = this._furnitureItem(this._drag.id);
      if (f) {
        const meta = this._catalogMeta(f.asset_id);
        const fp = meta?.footprint ?? { w: 1, d: 1 };
        const ang = this._furniturePointerAngle(f, p);
        f.rotY = normalizeYaw(this._drag.startRotY - (ang - this._drag.startAng));
        const c = this._constrainFurniturePlanPx(f.px, f.py, fp, f.rotY, 1, f.placementId ?? null);
        f.px = c.px;
        f.py = c.py;
        const payload = this._applyFurnitureToScene(f);
        this._throttledFurnitureNet(payload);
      }
    }
    this._render();
  }

  /** Nudge the stage scroll so a model point stays on screen while dragging. */
  _keepModelPointVisible(p, viewBoxOverride = null) {
    if (!this.elStage || !this.elSvg) return;
    const vb = viewBoxOverride ?? this._viewBox;
    if (!vb) return;
    const r = this.elSvg.getBoundingClientRect();
    if (!r.width || !r.height) return;
    const sx = r.left + ((p.px - vb.x) / vb.w) * r.width;
    const sy = r.top + ((p.py - vb.y) / vb.h) * r.height;
    const margin = 56;
    const sr = this.elStage.getBoundingClientRect();
    if (sx < sr.left + margin) this.elStage.scrollLeft -= sr.left + margin - sx;
    else if (sx > sr.right - margin) this.elStage.scrollLeft += sx - (sr.right - margin);
    if (sy < sr.top + margin) this.elStage.scrollTop -= sr.top + margin - sy;
    else if (sy > sr.bottom - margin) this.elStage.scrollTop += sy - (sr.bottom - margin);
  }

  _onPointerUp() {
    const t = this._drag?.type;
    if (t === "furniture" || t === "frotate") {
      const f = this._furnitureItem(this._drag.id);
      if (f) {
        const payload = this._applyFurnitureToScene(f);
        this._sendFurnitureNet(payload);
      }
    } else if (t === "opening" || t === "ohandle") {
      this._syncOpeningById(this._drag.id);
    }
    // Room blocks stay free (rigid) while editing; they re-weld at Apply. Only
    // corner/node drags trigger live T-junction normalization.
    if (t === "room") {
      this._snapRoom();
      // Keep the room's type tag attached after a move (centroid shifted).
      const from = this._dragRoomTagFrom;
      const meta = this._findRoomMeta(from);
      const to = this._roomCentroidPx(this._selectedRoomNodeIds());
      if (meta && to) {
        meta.cx = to[0];
        meta.cy = to[1];
        const face = this._faceAtPoint({ px: to[0], py: to[1] });
        if (face) meta.faceKey = face.key;
      }
      this._dragRoomTagFrom = null;
      this._syncRoomCtl();
    } else if (t === "node") {
      this._normalizeTopology();
      this._syncTopologyLive();
    } else if (t === "floorCorner") {
      const f = this.model?.floor;
      const id = this._drag?.id;
      const vert = f?.vertices?.find((v) => v.id === id);
      if (vert) {
        const snapped = this._snapFloorCorner(id, vert.px, vert.py, false);
        vert.px = snapped.px;
        vert.py = snapped.py;
        if (f.corners?.[id]) {
          f.corners[id].px = snapped.px;
          f.corners[id].py = snapped.py;
        }
        syncFloorLegacyFields(f);
      }
      this._syncFloorLive();
    }
    this._drag = null;
    this._syncCornerCtl();
    this._render();
    // A completed drag (corner move, opening slide/resize, room move) is one undoable step.
    if (t && t !== "furniture" && t !== "frotate") this._record();
  }

  // ---- editing operations ---------------------------------------------------

  /**
   * Drag a corner freely in any direction. By default the attached walls keep
   * straight via soft H/V snapping to the corner's own neighbours (and exact
   * snapping onto another corner), but pulling off-axis moves it diagonally so
   * walls get longer/shorter at an angle. Hold Shift (`free`) for no snapping.
   */
  _moveNode(id, p, free = false) {
    const node = this.model.nodes.get(String(id));
    if (!node) return;
    let px = p.px;
    let py = p.py;

    if (!free) {
      const snap = SNAP_SCREEN_PX * this._unitPerScreen();
      // 1) Coincidence snap: drop exactly onto a nearby corner (merge alignment).
      let best = null;
      let bestD = snap;
      for (const [, n] of this.model.nodes) {
        if (n.id === id) continue;
        const d = Math.hypot(px - n.px, py - n.py);
        if (d < bestD) {
          bestD = d;
          best = n;
        }
      }
      if (best) {
        px = best.px;
        py = best.py;
      } else {
        // 2) Keep the two walls at this corner axis-aligned when close to it.
        for (const e of this.model.edges) {
          const otherId = e.a === id ? e.b : e.b === id ? e.a : null;
          if (otherId == null) continue;
          const n = this.model.nodes.get(String(otherId));
          if (!n) continue;
          if (Math.abs(px - n.px) < snap) px = n.px;
          if (Math.abs(py - n.py) < snap) py = n.py;
        }
      }
    }

    node.px = Math.max(0, Math.min(this.model.naturalW, px));
    node.py = Math.max(0, Math.min(this.model.naturalH, py));
  }

  // ---- room (group) transforms ---------------------------------------------

  /** Point-in-polygon (ray casting) on a face polygon. */
  _pointInPoly(p, poly) {
    let inside = false;
    for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
      const xi = poly[i][0];
      const yi = poly[i][1];
      const xj = poly[j][0];
      const yj = poly[j][1];
      const hit = yi > p.py !== yj > p.py && p.px < ((xj - xi) * (p.py - yi)) / (yj - yi) + xi;
      if (hit) inside = !inside;
    }
    return inside;
  }

  /** The smallest-area room face whose polygon contains the point. */
  _faceAtPoint(p) {
    const faces = this._computeFaces().filter((f) => this._pointInPoly(p, f.polygon));
    if (!faces.length) return null;
    faces.sort((a, b) => a.areaPx - b.areaPx);
    return faces[0];
  }

  /**
   * Turn the clicked room into an independent block: duplicate its corner nodes
   * and walls so transforms are rigid and never distort neighbours. Walls shared
   * with another room keep their original (so the neighbour stays intact); the
   * room's exterior walls move with the room. Returns { nodes, edges, loop }.
   */
  _detachRoom(face) {
    const faces = this._computeFaces();
    const useCount = new Map();
    for (const f of faces) for (const eid of f.edges) useCount.set(eid, (useCount.get(eid) || 0) + 1);

    // Already independent (no shared walls): edit it in place.
    if (!face.edges.some((eid) => (useCount.get(eid) || 0) > 1)) {
      return { nodes: [...new Set(face.nodes)], edges: [...face.edges], loop: [...face.nodes] };
    }

    const oldToNew = new Map();
    const dupNode = (oldId) => {
      if (oldToNew.has(oldId)) return oldToNew.get(oldId);
      const o = this.model.nodes.get(String(oldId));
      const id = this.model.nextNode++;
      this.model.nodes.set(String(id), { id, px: o.px, py: o.py });
      oldToNew.set(oldId, id);
      return id;
    };

    const newEdges = [];
    for (const eid of face.edges) {
      const e = this.model.edges.find((x) => x.id === eid);
      if (!e) continue;
      const ne = { id: this.model.nextEdge++, a: dupNode(e.a), b: dupNode(e.b) };
      this.model.edges.push(ne);
      newEdges.push(ne.id);
      if ((useCount.get(eid) || 0) <= 1) {
        // Exterior wall — the room takes it (with its openings).
        for (const o of this.model.openings) if (o.edge === eid) o.edge = ne.id;
        this.model.edges = this.model.edges.filter((x) => x.id !== eid);
      }
    }
    this._dropOrphanNodes();
    const loop = face.nodes.map((n) => oldToNew.get(n)).filter((v) => v != null);
    return { nodes: [...oldToNew.values()], edges: newEdges, loop };
  }

  /** Remove nodes no longer referenced by any wall. */
  _dropOrphanNodes() {
    const used = new Set();
    for (const e of this.model.edges) {
      used.add(e.a);
      used.add(e.b);
    }
    for (const [k, n] of [...this.model.nodes]) if (!used.has(n.id)) this.model.nodes.delete(k);
  }

  /** Unique node ids of the currently selected room. */
  _selectedRoomNodeIds() {
    return this.selectedRoom ? [...new Set(this.selectedRoom.nodes)] : [];
  }

  /** Translate the selected room block rigidly. */
  _dragRoom(p) {
    if (!this.selectedRoom || !this._drag?.last) return;
    const dx = p.px - this._drag.last.px;
    const dy = p.py - this._drag.last.py;
    for (const nid of this._selectedRoomNodeIds()) {
      const n = this.model.nodes.get(String(nid));
      if (!n) continue;
      n.px = Math.max(0, Math.min(this.model.naturalW, n.px + dx));
      n.py = Math.max(0, Math.min(this.model.naturalH, n.py + dy));
    }
    this._drag.last = p;
  }

  /**
   * Snap the whole room block by a single offset so it aligns to nearby outside
   * corners while staying rigid (no per-corner distortion).
   */
  _snapRoom() {
    if (!this.selectedRoom) return;
    const snap = SNAP_SCREEN_PX * this._unitPerScreen() * 1.5;
    const roomIds = new Set(this._selectedRoomNodeIds());
    let dx = 0;
    let dy = 0;
    let bestX = snap;
    let bestY = snap;
    for (const nid of roomIds) {
      const n = this.model.nodes.get(String(nid));
      if (!n) continue;
      for (const [, o] of this.model.nodes) {
        if (roomIds.has(o.id)) continue;
        const ex = Math.abs(n.px - o.px);
        const ey = Math.abs(n.py - o.py);
        if (ex < bestX) {
          bestX = ex;
          dx = o.px - n.px;
        }
        if (ey < bestY) {
          bestY = ey;
          dy = o.py - n.py;
        }
      }
    }
    for (const nid of roomIds) {
      const n = this.model.nodes.get(String(nid));
      if (!n) continue;
      n.px = Math.round(n.px + dx);
      n.py = Math.round(n.py + dy);
    }
  }

  /** Rotate the selected room block ±90° about its centroid (rigid). */
  _rotateRoom(dir) {
    if (!this.selectedRoom) {
      this._status("Pick a room first (Room tool, click a room).", true);
      return;
    }
    const ids = this._selectedRoomNodeIds();
    let cx = 0;
    let cy = 0;
    let count = 0;
    for (const nid of ids) {
      const n = this.model.nodes.get(String(nid));
      if (!n) continue;
      cx += n.px;
      cy += n.py;
      count++;
    }
    if (!count) return;
    cx /= count;
    cy /= count;
    const s = dir >= 0 ? 1 : -1;
    for (const nid of ids) {
      const n = this.model.nodes.get(String(nid));
      if (!n) continue;
      const rx = n.px - cx;
      const ry = n.py - cy;
      n.px = Math.round(cx - s * ry);
      n.py = Math.round(cy + s * rx);
    }
    this._status(`Rotated room ${dir >= 0 ? "+" : "−"}90°. Place it, then Apply to re-join walls.`);
    this._render();
    this._record();
  }

  /**
   * Split any wall that has a node sitting on its interior (a "T-junction") into
   * two walls at that node. Without this, planar face tracing jumps across the
   * mid-edge node and merges two rooms into one face (so a click selects both).
   * Returns true if the topology changed.
   */
  _normalizeTopology() {
    if (!this.model) return false;
    const tol = 2.5; // px; seed coordinates are exact, so a small tolerance is safe
    let changed = false;
    let guard = 0;
    let again = true;
    while (again && guard++ < 64) {
      again = false;
      for (const e of [...this.model.edges]) {
        const A = this.model.nodes.get(String(e.a));
        const B = this.model.nodes.get(String(e.b));
        if (!A || !B) continue;
        const vx = B.px - A.px;
        const vy = B.py - A.py;
        const len2 = vx * vx + vy * vy;
        if (len2 < 1) continue;
        let hit = null;
        let hitDpx = 0;
        for (const [, n] of this.model.nodes) {
          if (n.id === e.a || n.id === e.b) continue;
          const t = ((n.px - A.px) * vx + (n.py - A.py) * vy) / len2;
          if (t <= 0.002 || t >= 0.998) continue;
          const cx = A.px + vx * t;
          const cy = A.py + vy * t;
          if (Math.hypot(n.px - cx, n.py - cy) <= tol) {
            hit = n;
            hitDpx = Math.sqrt(len2) * t;
            break;
          }
        }
        if (hit) {
          this._splitEdgeAt(e, hit.id, hitDpx);
          changed = true;
          again = true;
          break; // edges array mutated — restart the scan
        }
      }
    }
    return changed;
  }

  /**
   * Merge nodes that sit on top of each other into one (used at commit to re-join
   * room blocks the user positioned edge-to-edge). Re-points walls, drops zero-
   * length and duplicate walls, and keeps openings on the surviving wall.
   */
  _weldCoincidentNodes(tol = 6) {
    if (!this.model) return false;
    const list = [...this.model.nodes.values()];
    const remap = new Map(); // welded id -> survivor id
    for (let i = 0; i < list.length; i++) {
      const a = list[i];
      if (remap.has(a.id)) continue;
      for (let j = i + 1; j < list.length; j++) {
        const b = list[j];
        if (remap.has(b.id)) continue;
        if (Math.hypot(a.px - b.px, a.py - b.py) <= tol) remap.set(b.id, a.id);
      }
    }
    if (!remap.size) return false;
    for (const e of this.model.edges) {
      if (remap.has(e.a)) e.a = remap.get(e.a);
      if (remap.has(e.b)) e.b = remap.get(e.b);
    }
    const seen = new Map();
    const keep = [];
    for (const e of this.model.edges) {
      if (e.a === e.b) {
        this.model.openings = this.model.openings.filter((o) => o.edge !== e.id);
        continue;
      }
      const key = e.a < e.b ? `${e.a}-${e.b}` : `${e.b}-${e.a}`;
      const prev = seen.get(key);
      if (prev) {
        for (const o of this.model.openings) if (o.edge === e.id) o.edge = prev.id;
        continue;
      }
      seen.set(key, e);
      keep.push(e);
    }
    this.model.edges = keep;
    for (const [from] of remap) this.model.nodes.delete(String(from));
    return true;
  }

  /** Get the edge for an unordered node pair, creating it if absent. */
  _ensureEdge(a, b) {
    const found = this.model.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
    if (found) return found;
    const e = { id: this.model.nextEdge++, a, b };
    this.model.edges.push(e);
    return e;
  }

  /**
   * Replace edge `e` (a→b) with a→node and node→b. Each sub-segment merges onto
   * an existing edge if one already covers that node pair (so a wall spanning two
   * rooms collapses onto the rooms' shared partitions). Openings are reassigned by
   * geometry so they keep their real position regardless of edge orientation.
   */
  _splitEdgeAt(e, nodeId, dpx) {
    const A = this.model.nodes.get(String(e.a));
    const B = this.model.nodes.get(String(e.b));
    if (!A || !B) return;
    const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
    const ux = (B.px - A.px) / len;
    const uy = (B.py - A.py) / len;
    const ops = this.model.openings
      .filter((o) => o.edge === e.id)
      .map((o) => ({ o, sx: A.px + ux * o.t, sy: A.py + uy * o.t }));

    this.model.edges = this.model.edges.filter((x) => x.id !== e.id);
    const seg1 = this._ensureEdge(e.a, nodeId);
    const seg2 = this._ensureEdge(nodeId, e.b);
    if (e.sceneWallId) seg1.sceneWallId = e.sceneWallId;

    for (const { o, sx, sy } of ops) {
      const mid = o.t + o.w / 2;
      const target = mid <= dpx ? seg1 : seg2;
      const Ta = this.model.nodes.get(String(target.a));
      const Tb = this.model.nodes.get(String(target.b));
      const tlen = Math.hypot(Tb.px - Ta.px, Tb.py - Ta.py) || 1;
      const tux = (Tb.px - Ta.px) / tlen;
      const tuy = (Tb.py - Ta.py) / tlen;
      o.edge = target.id;
      o.t = Math.max(0, Math.min(tlen - o.w, (sx - Ta.px) * tux + (sy - Ta.py) * tuy));
    }
  }

  /** Nearest node within snap radius (model units), or null. */
  _nodeNear(p, snap) {
    let best = null;
    let bestD = snap;
    for (const [, n] of this.model.nodes) {
      const d = Math.hypot(n.px - p.px, n.py - p.py);
      if (d < bestD) {
        bestD = d;
        best = n;
      }
    }
    return best;
  }

  _addNode(px, py) {
    const id = this.model.nextNode++;
    this.model.nodes.set(String(id), {
      id,
      px: Math.max(0, Math.min(this.model.naturalW, px)),
      py: Math.max(0, Math.min(this.model.naturalH, py)),
    });
    return id;
  }

  _addEdge(a, b) {
    if (a === b) return null;
    const exists = this.model.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
    if (exists) return exists.id;
    const id = this.model.nextEdge++;
    this.model.edges.push({ id, a, b });
    return id;
  }

  _wallClick(p) {
    const snap = SNAP_SCREEN_PX * this._unitPerScreen();
    const near = this._nodeNear(p, snap);
    let target;
    if (near) {
      target = near.id;
    } else {
      let px = p.px;
      let py = p.py;
      const f = this.model?.floor;
      if (f) {
        const hit = pickFloorBoundary(f, p, snap * 3) ?? nearestFloorBoundary(f, p);
        if (hit) {
          px = hit.px;
          py = hit.py;
        }
      }
      // Orthogonal lock against the chain's previous node.
      if (this._wallChain != null) {
        const prev = this.model.nodes.get(String(this._wallChain));
        if (prev) {
          if (Math.abs(px - prev.px) <= Math.abs(py - prev.py)) px = prev.px;
          else py = prev.py;
        }
      }
      target = this._addNode(px, py);
    }
    if (this._wallChain != null && target !== this._wallChain) {
      this._addEdge(this._wallChain, target);
    }
    // Close the loop if we clicked back onto the chain's start area handled by near.
    this._wallChain = target;
    this.selected = { kind: "node", id: target };
    this._render();
    this._record();
    this._syncTopologyLive();
  }

  _endWallChain() {
    this._wallChain = null;
    this._normalizeTopology();
    this._render();
  }

  _edgeNear(p, snap) {
    let best = null;
    let bestD = snap;
    for (const e of this.model.edges) {
      const A = this.model.nodes.get(String(e.a));
      const B = this.model.nodes.get(String(e.b));
      if (!A || !B) continue;
      const d = this._distToSegment(p, A, B);
      if (d.dist < bestD) {
        bestD = d.dist;
        best = { edge: e, t: d.t, len: d.len, dist: d.dist };
      }
    }
    return best;
  }

  /** Pick a wall segment or corner near a click (corners win when closer than the wall). */
  _pickEdgeOrNode(p) {
    const u = this._unitPerScreen();
    const snap = SNAP_SCREEN_PX * u * 1.8;
    const cornerSnap = SNAP_SCREEN_PX * u * 2.5;
    const node = this._nodeNear(p, cornerSnap);
    const edge = this._edgeNear(p, snap);
    if (node && !edge) return { kind: "node", id: node.id };
    if (edge && !node) return { kind: "edge", id: edge.edge.id };
    if (node && edge) {
      const nd = Math.hypot(node.px - p.px, node.py - p.py);
      if (nd <= edge.dist) return { kind: "node", id: node.id };
      return { kind: "edge", id: edge.edge.id };
    }
    return null;
  }

  /** Nearest door/window opening within snap radius (model units), or null. */
  _openingNear(p, snap) {
    let best = null;
    let bestD = snap;
    for (const o of this.model.openings) {
      const edge = this.model.edges.find((e) => e.id === o.edge);
      if (!edge) continue;
      const A = this.model.nodes.get(String(edge.a));
      const B = this.model.nodes.get(String(edge.b));
      if (!A || !B) continue;
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      const t0 = Math.max(0, Math.min(len, o.t));
      const t1 = Math.max(0, Math.min(len, o.t + o.w));
      const s = { px: A.px + ux * t0, py: A.py + uy * t0 };
      const en = { px: A.px + ux * t1, py: A.py + uy * t1 };
      const d = this._distToSegment(p, s, en);
      let dist = d.dist;
      if (o.type === "door") {
        const hingeRight = normHinge(o.hinge) === "right";
        const H = hingeRight ? en : s;
        const J = hingeRight ? s : en;
        const R = Math.hypot(J.px - H.px, J.py - H.py) || 1;
        const hingeD = Math.hypot(p.px - H.px, p.py - H.py);
        if (hingeD <= R * 1.15) dist = Math.min(dist, hingeD * 0.65);
      }
      if (dist < bestD) {
        bestD = dist;
        best = o;
      }
    }
    return best;
  }

  /** Doors/windows beat wall segments when both are near the click — unless a corner is closer. */
  _pickOpeningOverEdge(kind, id, p) {
    const u = this._unitPerScreen();
    const cornerSnap = SNAP_SCREEN_PX * u * 2.5;
    const node = this._nodeNear(p, cornerSnap);
    if (node) {
      const nd = Math.hypot(node.px - p.px, node.py - p.py);
      const snap = SNAP_SCREEN_PX * u * 2;
      const opening = this._openingNear(p, snap);
      if (!opening) return { kind: "node", id: node.id };
      const edge = this.model.edges.find((e) => e.id === opening.edge);
      if (!edge) return { kind: "node", id: node.id };
      const A = this.model.nodes.get(String(edge.a));
      const B = this.model.nodes.get(String(edge.b));
      if (!A || !B) return { kind: "node", id: node.id };
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      const t0 = Math.max(0, Math.min(len, opening.t));
      const t1 = Math.max(0, Math.min(len, opening.t + opening.w));
      const od = this._distToSegment(p, { px: A.px + ux * t0, py: A.py + uy * t0 }, { px: A.px + ux * t1, py: A.py + uy * t1 }).dist;
      if (nd <= od) return { kind: "node", id: node.id };
    }

    const snap = SNAP_SCREEN_PX * u * 2;
    const opening = this._openingNear(p, snap);
    if (!opening) return { kind, id };
    if (kind === "opening" && id === opening.id) return { kind, id };
    if (kind === "node") return { kind, id };
    if (kind === "edge" || kind === "ohandle" || !kind) {
      return { kind: "opening", id: opening.id };
    }
    return { kind, id };
  }

  /** Remove one wall segment and any openings on it; drop unused corners. */
  _removeEdge(id) {
    this.model.edges = this.model.edges.filter((e) => e.id !== id);
    for (const op of this.model.openings.filter((o) => o.edge === id)) {
      this._removeOpeningFromScene(op);
    }
    this.model.openings = this.model.openings.filter((o) => o.edge !== id);
    this._dropOrphanNodes();
    if (this.selected?.kind === "edge" && this.selected.id === id) this.selected = null;
    this._status("Wall segment removed.");
    this._render();
    this._record();
    this._syncTopologyLive();
  }

  /** Delete the current selection (keyboard or toolbar). */
  _deleteSelection() {
    if (!this.model || !this.selected) return false;
    const { kind, id } = this.selected;
    if (kind === "furniture" && id != null) {
      this._removeFurniture(id);
      return true;
    }
    if (kind === "edge" && id != null) {
      this._removeEdge(id);
      return true;
    }
    this._deleteAt(kind, id, null);
    return true;
  }

  _distToSegment(p, A, B) {
    const vx = B.px - A.px;
    const vy = B.py - A.py;
    const len = Math.hypot(vx, vy) || 1;
    let t = ((p.px - A.px) * vx + (p.py - A.py) * vy) / (len * len);
    t = Math.max(0, Math.min(1, t));
    const cx = A.px + vx * t;
    const cy = A.py + vy * t;
    return { dist: Math.hypot(p.px - cx, p.py - cy), t: t * len, len };
  }

  /** Average of all corner positions — interior-side reference (matches door swing). */
  _layoutCentroidPx() {
    let cx = 0;
    let cy = 0;
    let n = 0;
    for (const [, node] of this.model?.nodes ?? []) {
      cx += node.px;
      cy += node.py;
      n++;
    }
    return n ? [cx / n, cy / n] : [0, 0];
  }

  /** Unit inward normal on segment AB at (cx, cy), pointing toward (refCx, refCy). */
  _inwardNormalOnSegment(A, B, cx, cy, refCx, refCy) {
    const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
    const ux = (B.px - A.px) / len;
    const uy = (B.py - A.py) / len;
    let nx = -uy;
    let ny = ux;
    if ((refCx - cx) * nx + (refCy - cy) * ny < 0) {
      nx = -nx;
      ny = -ny;
    }
    return { nx, ny, ux, uy };
  }

  /** Map a plan-pixel unit normal to world XZ (handles non-uniform px→meter scaling). */
  _pxNormalToWorld(nx, ny, px, py) {
    const a = this._pxToWorld(px, py);
    const b = this._pxToWorld(px + nx, py + ny);
    let wx = b.x - a.x;
    let wz = b.z - a.z;
    const len = Math.hypot(wx, wz) || 1;
    return { x: wx / len, z: wz / len };
  }

  /**
   * Snap a switch to the nearest wall of the room under the click (or nearest wall
   * when clicking near a segment). Returns wall point + inward normal toward room interior.
   */
  _snapSwitchToWall(p) {
    if (!this.model) return null;
    const face = this._faceAtPoint(p);
    const [refCx, refCy] = face ? face.centroid : this._layoutCentroidPx();
    const edges = face ? this.model.edges.filter((e) => face.edges.includes(e.id)) : this.model.edges;
    if (!edges.length) return null;

    let best = null;
    let bestDist = Infinity;
    for (const e of edges) {
      const A = this.model.nodes.get(String(e.a));
      const B = this.model.nodes.get(String(e.b));
      if (!A || !B) continue;
      const d = this._distToSegment(p, A, B);
      if (d.dist >= bestDist) continue;
      const len = d.len || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      const px = A.px + ux * d.t;
      const py = A.py + uy * d.t;
      const { nx, ny } = this._inwardNormalOnSegment(A, B, px, py, refCx, refCy);
      bestDist = d.dist;
      best = { px, py, nx, ny, edge: e.id, ux, uy };
    }

    if (!best) return null;
    if (!face) {
      const snap = Math.max(SNAP_SCREEN_PX * this._unitPerScreen() * 4, 24 * this._unitPerScreen());
      if (bestDist > snap) return null;
    }
    return best;
  }

  /** Re-snap an existing switch onto its wall when missing or off-wall. */
  _ensureSwitchPlacement(sw) {
    const snapped = this._snapSwitchToWall({ px: sw.px, py: sw.py });
    if (!snapped) return false;
    const tol = this._unitPerScreen() * 0.75;
    const moved = Math.hypot(sw.px - snapped.px, sw.py - snapped.py) > tol;
    const badNormal = !Number.isFinite(sw.nx) || !Number.isFinite(sw.ny);
    if (!moved && !badNormal) return false;
    sw.px = snapped.px;
    sw.py = snapped.py;
    sw.nx = snapped.nx;
    sw.ny = snapped.ny;
    sw.edge = snapped.edge;
    return true;
  }

  _addOpeningAt(p, type) {
    const snap = Math.max(SNAP_SCREEN_PX * this._unitPerScreen(), 14 * this._unitPerScreen());
    const hit = this._edgeNear(p, snap);
    if (!hit) {
      this._status("Click directly on a wall to add a " + type + ".", true);
      return;
    }
    const defW = (type === "window" ? 1.4 : 0.9) / (this.model.metersPerPixel || 1);
    const w = Math.min(defW, hit.len - 4);
    const t = Math.max(w / 2, Math.min(hit.len - w / 2, hit.t));
    const id = this.model.nextOpening++;
    this.model.openings.push({
      id,
      sceneId: `pe_op_${id}`,
      edge: hit.edge.id,
      type,
      t: t - w / 2, // store distance from start node to the opening's START
      w,
      sill: type === "window" ? 0.9 : 0,
      ...(type === "window" ? { height: 1.46, curtain: true } : {}),
      ...(type === "door" ? { hinge: "left", swing: "in" } : {}),
    });
    this.selected = { kind: "opening", id };
    this._status("");
    this._render();
    this._record();
    this._bindEdgesToSceneWalls();
    this._syncOpeningById(id);
    try {
      sessionStorage.setItem(PLAN_OPENINGS_DRAFT_KEY, JSON.stringify(this.model.openings));
    } catch {
      /* ignore */
    }
  }

  /** Drop a ceiling light at the clicked point (must be inside a room). */
  _addLightAt(p) {
    const face = this._faceAtPoint(p);
    if (!face) {
      this._status("Click inside a room to mount a ceiling light.", true);
      return;
    }
    const id = this.model.nextLight++;
    const fixture = this._lightFixture || DEFAULT_LIGHT;
    this.model.lights.push({ id, px: p.px, py: p.py, fixture });
    this.selected = { kind: "light", id };
    const label = LIGHT_TYPES.find((l) => l.id === fixture)?.label ?? fixture;
    this._status(`${label} light placed — drag to reposition, or click ✕ to remove.`);
    this._render();
    this._record();
  }

  /** Drop a wall/room light switch at the clicked point (anywhere in a room). */
  _addSwitchAt(p) {
    const id = this.model.nextSwitch++;
    this.model.switches.push({ id, px: p.px, py: p.py, on: true });
    this.selected = { kind: "switch", id };
    this._status("Switch placed — it toggles the lights of the room it sits in.");
    this._render();
    this._record();
  }

  /** Move a free marker (light/switch) to follow the pointer. */
  _moveMarker(list, id, p) {
    const it = list.find((x) => x.id === id);
    if (!it) return;
    it.px = p.px;
    it.py = p.py;
  }

  _slideOpening(id, p) {
    const op = this.model.openings.find((o) => o.id === id);
    if (!op) return;
    const edge = this.model.edges.find((e) => e.id === op.edge);
    if (!edge) return;
    const A = this.model.nodes.get(String(edge.a));
    const B = this.model.nodes.get(String(edge.b));
    const d = this._distToSegment(p, A, B);
    op.t = Math.max(0, Math.min(d.len - op.w, d.t - op.w / 2));
  }

  _resizeOpening(id, role, p) {
    const op = this.model.openings.find((o) => o.id === id);
    if (!op) return;
    const edge = this.model.edges.find((e) => e.id === op.edge);
    if (!edge) return;
    const A = this.model.nodes.get(String(edge.a));
    const B = this.model.nodes.get(String(edge.b));
    const d = this._distToSegment(p, A, B);
    const minW = 6;
    if (role === "start") {
      const end = op.t + op.w;
      const start = Math.max(0, Math.min(end - minW, d.t));
      op.t = start;
      op.w = end - start;
    } else {
      const end = Math.max(op.t + minW, Math.min(d.len, d.t));
      op.w = end - op.t;
    }
  }

  _deleteAt(kind, id, p) {
    if ((!kind || id == null) && p) {
      const opening = this._openingNear(p, SNAP_SCREEN_PX * this._unitPerScreen() * 2);
      if (opening) {
        kind = "opening";
        id = opening.id;
      } else {
        const pick = this._pickEdgeOrNode(p);
        if (pick) {
          kind = pick.kind;
          id = pick.id;
        }
      }
    } else if (p) {
      ({ kind, id } = this._pickOpeningOverEdge(kind, id, p));
    }
    if (kind === "light" && id != null) {
      this.model.lights = this.model.lights.filter((l) => l.id !== id);
    } else if (kind === "switch" && id != null) {
      this.model.switches = this.model.switches.filter((s) => s.id !== id);
    } else if (kind === "furniture" && id != null) {
      this._removeFurniture(id);
      return;
    } else if (kind === "node" && id != null) {
      this.model.nodes.delete(String(id));
      const removed = new Set();
      this.model.edges = this.model.edges.filter((e) => {
        const keep = e.a !== id && e.b !== id;
        if (!keep) removed.add(e.id);
        return keep;
      });
      this.model.openings = this.model.openings.filter((o) => !removed.has(o.edge));
      this._dropOrphanNodes();
      this._status("Corner removed.");
    } else if (kind === "opening" && id != null) {
      const doomed = this.model.openings.find((o) => o.id === id);
      if (doomed) this._removeOpeningFromScene(doomed);
      this.model.openings = this.model.openings.filter((o) => o.id !== id);
      this._status("Opening removed.");
    } else if (kind === "ohandle" && id != null) {
      const doomed = this.model.openings.find((o) => o.id === id);
      if (doomed) this._removeOpeningFromScene(doomed);
      this.model.openings = this.model.openings.filter((o) => o.id !== id);
      this._status("Opening removed.");
    } else if (kind === "edge" && id != null) {
      this._removeEdge(id);
      return;
    } else {
      this._status("Click a wall line or item to delete.", true);
      return;
    }
    this.selected = null;
    this._render();
    this._record();
  }

  // ---- calibration ----------------------------------------------------------

  _calibrateClick(p) {
    const snap = SNAP_SCREEN_PX * this._unitPerScreen();
    const near = this._nodeNear(p, snap);
    const pt = near ? { px: near.px, py: near.py } : { px: p.px, py: p.py };
    if (!this._calib || this._calib.b) {
      this._calib = { a: pt, b: null };
    } else {
      this._calib.b = pt;
      const dpx = Math.hypot(this._calib.b.px - this._calib.a.px, this._calib.b.py - this._calib.a.py);
      if (this.elCalibPx) this.elCalibPx.textContent = `${Math.round(dpx)} px`;
      this.elCalibM?.focus();
    }
    this._render();
  }

  _applyCalibration() {
    if (!this._calib?.a || !this._calib?.b) {
      this._status("Draw a line across a known dimension first.", true);
      return;
    }
    const meters = Number(this.elCalibM?.value);
    if (!(meters > 0)) {
      this._status("Enter the real length in meters.", true);
      return;
    }
    const dpx = Math.hypot(this._calib.b.px - this._calib.a.px, this._calib.b.py - this._calib.a.py);
    if (dpx < 1) return;
    this.model.metersPerPixel = meters / dpx;
    this.model.graphMeterBounds = null;
    this._calib = null;
    this._status(`Scale set: 1 px ≈ ${this.model.metersPerPixel.toFixed(4)} m`);
    this._render();
    this._record();
  }

  // ---- rendering ------------------------------------------------------------

  /** Large red ✕ remove chip — shared by walls, openings, lights, switches, furniture. */
  _deleteBtnSvg(kind, id, cx, cy, u, thinW) {
    const r = 11 * u;
    const pad = 5 * u;
    const hitR = r + pad;
    const xLen = r * 0.42;
    const sw = Math.max(thinW * 1.6, 2.2 * u);
    return (
      `<g data-kind="${kind}" data-id="${id}" style="cursor:pointer">` +
      `<title>Remove</title>` +
      `<circle cx="${cx}" cy="${cy}" r="${hitR}" fill="transparent"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${r + 2 * u}" fill="rgba(239,68,68,0.3)" style="pointer-events:none"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${r}" fill="#dc2626" stroke="rgba(255,255,255,0.95)" stroke-width="${thinW * 1.25}" style="pointer-events:none"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${r + thinW * 0.4}" fill="none" stroke="#0b1220" stroke-width="${thinW * 0.5}" opacity="0.4" style="pointer-events:none"/>` +
      `<path d="M ${cx - xLen} ${cy - xLen} L ${cx + xLen} ${cy + xLen} M ${cx + xLen} ${cy - xLen} L ${cx - xLen} ${cy + xLen}" ` +
      `stroke="#fff" stroke-width="${sw}" stroke-linecap="round" style="pointer-events:none"/>` +
      `</g>`
    );
  }

  /** Glass-style door control chip (hinge / swing toggles). */
  _doorControlChipSvg(kind, id, title, subtitle, cx, cy, u) {
    const cw = 96 * u;
    const ch = 44 * u;
    const titleFs = 12 * u;
    const subFs = 9 * u;
    const pad = 6 * u;
    const rx = 10 * u;
    const x0 = cx - cw / 2;
    const y0 = cy - ch / 2;
    return (
      `<g data-kind="${kind}" data-id="${id}" style="cursor:pointer">` +
      `<rect x="${x0 - pad}" y="${y0 - pad}" width="${cw + pad * 2}" height="${ch + pad * 2}" rx="${rx + pad}" fill="transparent"/>` +
      `<rect x="${x0 + u * 0.6}" y="${y0 + u * 1.4}" width="${cw}" height="${ch}" rx="${rx}" fill="rgba(15,23,42,0.1)" style="pointer-events:none"/>` +
      `<rect x="${x0}" y="${y0}" width="${cw}" height="${ch}" rx="${rx}" fill="rgba(248,250,252,0.96)" stroke="rgba(100,116,139,0.35)" stroke-width="${1.6 * u}"/>` +
      `<rect x="${x0 + 2 * u}" y="${y0 + 2 * u}" width="${cw - 4 * u}" height="${3.5 * u}" rx="${2 * u}" fill="rgba(255,255,255,0.85)" style="pointer-events:none"/>` +
      `<text x="${cx}" y="${cy - 2 * u}" font-size="${titleFs}" font-weight="600" fill="#0f172a" text-anchor="middle" ` +
      `style="pointer-events:none;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont">${this._svgEsc(title)}</text>` +
      `<text x="${cx}" y="${cy + titleFs * 0.82}" font-size="${subFs}" fill="#64748b" text-anchor="middle" ` +
      `style="pointer-events:none;font-family:ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont">${this._svgEsc(subtitle)}</text>` +
      `</g>`
    );
  }

  /** Door-style info chip — reused for selected light fixture labels. */
  _infoChipSvg(kind, id, label, cx, cy, u, thinW, stroke = "#fbbf24") {
    const cw = Math.max(38 * u, label.length * 5.8 * u + 12 * u);
    const ch = 20 * u;
    const fs = 10 * u;
    return (
      `<g data-kind="${kind}" data-id="${id}" style="cursor:pointer">` +
      `<rect x="${cx - cw / 2}" y="${cy - ch / 2}" width="${cw}" height="${ch}" rx="${4 * u}" ` +
      `fill="#0b1220" stroke="${stroke}" stroke-width="${thinW}"/>` +
      `<text x="${cx}" y="${cy + fs * 0.34}" font-size="${fs}" fill="#e2f7ff" text-anchor="middle" ` +
      `font-family="ui-sans-serif,system-ui" style="pointer-events:none">${this._svgEsc(label)}</text>` +
      `</g>`
    );
  }

  /** Read-only label chip — selected window dimensions (no pointer interaction). */
  _labelChipSvg(label, cx, cy, u, thinW, stroke = "#38bdf8") {
    const cw = Math.max(34 * u, label.length * 5.8 * u + 12 * u);
    const ch = 20 * u;
    const fs = 10 * u;
    return (
      `<g style="pointer-events:none">` +
      `<rect x="${cx - cw / 2}" y="${cy - ch / 2}" width="${cw}" height="${ch}" rx="${4 * u}" ` +
      `fill="#0b1220" stroke="${stroke}" stroke-width="${thinW}"/>` +
      `<text x="${cx}" y="${cy + fs * 0.34}" font-size="${fs}" fill="#e2f7ff" text-anchor="middle" ` +
      `font-family="ui-sans-serif,system-ui">${this._svgEsc(label)}</text>` +
      `</g>`
    );
  }

  /** Top-down ceiling-fixture glyph — distinct shape per LIGHT_TYPES id. */
  _lightGlyphSvg(l, u, thinW, sel) {
    const px = l.px;
    const py = l.py;
    const fixture = l.fixture || DEFAULT_LIGHT;
    const r = (sel ? 11 : 8) * u;
    const hitR = r * 2.15;
    const col = sel ? "#fde68a" : "#fbbf24";
    const colDim = sel ? "#f59e0b" : "#d97706";
    const ink = "#0b1220";
    const sw = sel ? Math.max(thinW * 1.35, 2 * u) : thinW;
    const pe = `style="pointer-events:none"`;

    let body = "";
    switch (fixture) {
      case "pendant":
        body =
          `<line x1="${px}" y1="${py - r * 1.35}" x2="${px}" y2="${py - r * 0.55}" stroke="${ink}" stroke-width="${sw}" ${pe}/>` +
          `<circle cx="${px}" cy="${py - r * 1.35}" r="${1.8 * u}" fill="${colDim}" stroke="${ink}" stroke-width="${sw * 0.7}" ${pe}/>` +
          `<circle cx="${px}" cy="${py}" r="${r * 0.72}" fill="${col}" stroke="${ink}" stroke-width="${sw}" ${pe}/>` +
          `<circle cx="${px}" cy="${py}" r="${r * 0.38}" fill="${colDim}" opacity="0.55" ${pe}/>`;
        break;
      case "spot":
        body =
          `<circle cx="${px}" cy="${py}" r="${r * 0.95}" fill="none" stroke="${colDim}" stroke-width="${sw * 0.85}" stroke-dasharray="${3 * u} ${2.2 * u}" ${pe}/>` +
          `<circle cx="${px}" cy="${py}" r="${r * 0.55}" fill="${col}" stroke="${ink}" stroke-width="${sw}" ${pe}/>` +
          `<circle cx="${px}" cy="${py}" r="${r * 0.22}" fill="#fffbeb" opacity="0.9" ${pe}/>` +
          [0, 45, 90, 135].map((deg) => {
            const rad = (deg * Math.PI) / 180;
            const x1 = px + Math.cos(rad) * r * 0.62;
            const y1 = py + Math.sin(rad) * r * 0.62;
            const x2 = px + Math.cos(rad) * r * 1.05;
            const y2 = py + Math.sin(rad) * r * 1.05;
            return `<line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}" stroke="${col}" stroke-width="${sw * 0.75}" stroke-linecap="round" opacity="0.85" ${pe}/>`;
          }).join("");
        break;
      case "chandelier":
        body =
          `<circle cx="${px}" cy="${py}" r="${r * 0.28}" fill="${colDim}" stroke="${ink}" stroke-width="${sw * 0.8}" ${pe}/>` +
          [0, 60, 120, 180, 240, 300].map((deg) => {
            const rad = (deg * Math.PI) / 180;
            const bx = px + Math.cos(rad) * r * 0.72;
            const by = py + Math.sin(rad) * r * 0.72;
            return (
              `<line x1="${px}" y1="${py}" x2="${bx}" y2="${by}" stroke="${ink}" stroke-width="${sw * 0.7}" ${pe}/>` +
              `<circle cx="${bx}" cy="${by}" r="${2.4 * u}" fill="${col}" stroke="${ink}" stroke-width="${sw * 0.65}" ${pe}/>`
            );
          }).join("");
        break;
      default:
        body =
          `<circle cx="${px}" cy="${py}" r="${r * 0.92}" fill="${col}" stroke="${ink}" stroke-width="${sw}" ${pe}/>` +
          `<circle cx="${px}" cy="${py}" r="${r * 0.58}" fill="none" stroke="${ink}" stroke-width="${sw * 0.75}" opacity="0.55" ${pe}/>` +
          `<path d="M ${px - r * 0.42} ${py} L ${px + r * 0.42} ${py} M ${px} ${py - r * 0.42} L ${px} ${py + r * 0.42}" ` +
          `stroke="${ink}" stroke-width="${sw * 0.85}" opacity="0.7" ${pe}/>`;
        break;
    }

    return (
      `<g style="cursor:pointer">` +
      `<circle data-kind="light" data-id="${l.id}" cx="${px}" cy="${py}" r="${hitR}" fill="transparent"/>` +
      `<circle cx="${px}" cy="${py}" r="${r * (sel ? 2.15 : 1.75)}" fill="rgba(251,191,36,${sel ? 0.32 : 0.1})" ${pe}/>` +
      (sel
        ? `<circle cx="${px}" cy="${py}" r="${r + 4 * u}" fill="none" stroke="#fbbf24" stroke-width="${2.2 * u}" ` +
          `stroke-dasharray="${5 * u} ${3.5 * u}" opacity="0.95" ${pe}/>`
        : "") +
      body +
      `</g>`
    );
  }

  /** Wall-switch rocker plate — larger hit target, ON/OFF rocker offset. */
  _switchGlyphSvg(sw, u, thinW, sel) {
    const w = (sel ? 16 : 13) * u;
    const h = (sel ? 22 : 18) * u;
    const on = sw.on !== false;
    const borderCol = sel ? "#fbbf24" : on ? "#34d399" : "#64748b";
    const rockerCol = on ? (sel ? "#6ee7b7" : "#34d399") : "#475569";
    const rockerY = sw.py + (on ? -h * 0.14 : h * 0.14);
    const rw = w * 0.46;
    const rh = h * 0.48;
    const pe = `style="pointer-events:none"`;

    return (
      `<g style="cursor:pointer">` +
      `<rect data-kind="switch" data-id="${sw.id}" x="${sw.px - w * 0.72}" y="${sw.py - h * 0.72}" ` +
      `width="${w * 1.44}" height="${h * 1.44}" fill="transparent"/>` +
      (sel ? `<rect x="${sw.px - w / 2 - 3 * u}" y="${sw.py - h / 2 - 3 * u}" width="${w + 6 * u}" height="${h + 6 * u}" ` +
        `rx="${5 * u}" fill="rgba(251,191,36,0.14)" stroke="#fbbf24" stroke-width="${thinW}" stroke-dasharray="${4 * u} ${3 * u}" ${pe}/>` : "") +
      `<rect data-kind="switch" data-id="${sw.id}" x="${sw.px - w / 2}" y="${sw.py - h / 2}" width="${w}" height="${h}" rx="${3.5 * u}" ` +
      `fill="#0b1220" stroke="${borderCol}" stroke-width="${sel ? thinW * 1.35 : thinW * 1.1}"/>` +
      `<rect x="${sw.px - w / 2 + 1.2 * u}" y="${sw.py - h / 2 + 1.2 * u}" width="${w - 2.4 * u}" height="${h - 2.4 * u}" ` +
      `rx="${2.5 * u}" fill="none" stroke="${borderCol}" stroke-width="${thinW * 0.55}" opacity="0.45" ${pe}/>` +
      `<rect x="${sw.px - rw / 2}" y="${rockerY - rh / 2}" width="${rw}" height="${rh}" rx="${2.2 * u}" fill="${rockerCol}" ${pe}/>` +
      `<line x1="${sw.px - rw * 0.28}" y1="${rockerY}" x2="${sw.px + rw * 0.28}" y2="${rockerY}" ` +
      `stroke="${on ? "#064e3b" : "#1e293b"}" stroke-width="${thinW * 0.65}" stroke-linecap="round" opacity="0.55" ${pe}/>` +
      `</g>`
    );
  }

  /** Model-pixel margin around the plan so door/window chips are not clipped. */
  _renderPad(u) {
    const m = this.model;
    if (!m) return 80;
    const chipClear = 58 * u + 22 * u; // wall offset + half chip height
    const ratioPad = Math.min(m.naturalW, m.naturalH) * 0.11;
    let pad = Math.max(chipClear, ratioPad, 72 * u);
    if (this._floorExpand) {
      const b = this._modelContentBounds();
      const span = Math.max(b.maxX - b.minX, b.maxY - b.minY, 120);
      pad = Math.max(pad, span * 0.35);
    }
    return pad;
  }

  /** Expand canvas + viewBox so overlays can sit in the margin; keep image aligned. */
  _syncCanvasLayout(vb) {
    const m = this.model;
    if (!m || !this.elCanvas || !this.elImg || !vb) return;
    const vw = vb.w;
    const vh = vb.h;
    const left = ((0 - vb.x) / vw) * 100;
    const top = ((0 - vb.y) / vh) * 100;
    const width = (m.naturalW / vw) * 100;
    const height = (m.naturalH / vh) * 100;

    this.elCanvas.style.aspectRatio = `${vw} / ${vh}`;
    this.elImg.style.position = "absolute";
    this.elImg.style.left = `${left}%`;
    this.elImg.style.top = `${top}%`;
    this.elImg.style.width = `${width}%`;
    this.elImg.style.height = `${height}%`;
    this.elImg.style.objectFit = "fill";
    if (this.elSvg) {
      this.elSvg.style.overflow = "visible";
    }
    this._syncFloorImageClip(vb);
  }

  /** Clip the plan render to the editable floor polygon so it matches the corner sticks. */
  _syncFloorImageClip(vb = this._viewBox) {
    if (!this.elImg) return;
    if (!this._isFloorOnlyMode() || !this.model?.floor || !vb) {
      this.elImg.style.clipPath = "";
      return;
    }
    const mpp = this.model.metersPerPixel > 0 ? this.model.metersPerPixel : 0.02;
    const fillPoints = planFloorFillPoints(this.model.floor, mpp);
    const nw = this.model.naturalW;
    const nh = this.model.naturalH;
    if (fillPoints.length < 3 || !(nw > 0) || !(nh > 0)) {
      this.elImg.style.clipPath = "";
      return;
    }
    const pts = fillPoints
      .map(([px, py]) => {
        const ix = (px / nw) * 100;
        const iy = (py / nh) * 100;
        return `${ix}% ${iy}%`;
      })
      .join(", ");
    this.elImg.style.clipPath = `polygon(${pts})`;
  }

  _render() {
    if (!this.model || !this.elSvg) return;
    const m = this.model;
    const estU = this._unitPerScreen();
    const pad = this._renderPad(estU);
    if (this._floorExpand?.viewBox) {
      this._viewBox = { ...this._floorExpand.viewBox };
    } else if (this._drag?.type === "floorCorner" && this._drag.viewBox) {
      this._viewBox = { ...this._drag.viewBox };
    } else {
      this._viewBox = this._computeViewBox(pad);
    }
    this._syncCanvasLayout(this._viewBox);
    this.elSvg.setAttribute("viewBox", `${this._viewBox.x} ${this._viewBox.y} ${this._viewBox.w} ${this._viewBox.h}`);
    const u = this._unitPerScreen();
    const node_r = 6 * u;
    const wallW = 3 * u;
    const thinW = 1.5 * u;
    const fontSz = 13 * u;

    const parts = [];
    const deleteParts = [];
    const controlParts = [];
    const nodeParts = [];
    const vbPick = this._viewBox;
    const freeClickTool = this.tool === "floorExpand" || this.tool === "calibrate" || this.tool === "wall";
    parts.push(
      `<rect data-kind="canvas-bg" x="${vbPick.x}" y="${vbPick.y}" width="${vbPick.w}" height="${vbPick.h}" ` +
        `fill="rgba(0,0,0,0.001)" style="pointer-events:all;cursor:${freeClickTool ? "crosshair" : "default"}"/>`,
    );
    const roomFaces = this._computeFaces();
    const mpp = this.model.metersPerPixel > 0 ? this.model.metersPerPixel : 0;

    // Balcony areas — always visible so the user sees which zone opens to a view.
    for (const f of roomFaces) {
      if (!isBalconyType(f.type)) continue;
      const pts = f.polygon.map(([x, y]) => `${x},${y}`).join(" ");
      const areaM2 = mpp > 0 ? f.areaPx * mpp * mpp : 0;
      parts.push(
        `<polygon data-kind="roomfill" points="${pts}" fill="rgba(251,191,36,0.22)" ` +
          `stroke="#fbbf24" stroke-width="${1.8 * u}" stroke-dasharray="${6 * u} ${4 * u}" />`,
      );
      if (f.centroid) {
        const label = mpp > 0 ? `Balcony · ${areaM2.toFixed(1)} m²` : "Balcony";
        parts.push(
          `<text x="${f.centroid[0]}" y="${f.centroid[1] - 8 * u}" fill="#fbbf24" font-size="${12 * u}" ` +
            `text-anchor="middle" dominant-baseline="middle" style="font-weight:700;paint-order:stroke;stroke:#0b1220;stroke-width:${3 * u}">${label}</text>`,
        );
      }
    }

    // Floor-only mode — editable envelope when all walls are cleared.
    if (this._isFloorOnlyMode()) {
      this._ensureFloorCorners(m);
      const fl = m.floor;
      const floorStroke = "#22c55e";
      const { lines: floorLines, arcs: floorArcs, pathD: floorPathD } = planFloorStrokes(fl, mpp > 0 ? mpp : 0.02);
      if (floorPathD) {
        parts.push(
          `<path d="${floorPathD}" fill="#243028" fill-rule="nonzero" stroke="transparent" style="pointer-events:none"/>`,
        );
        parts.push(
          `<path d="${floorPathD}" fill="rgba(34,197,94,0.1)" fill-rule="nonzero" stroke="${floorStroke}" stroke-width="${wallW}" ` +
            `stroke-linejoin="round" stroke-linecap="round" opacity="0.95" style="pointer-events:none"/>`,
        );
      }
      for (const ln of floorLines) {
        if (this._showDims && mpp > 0) {
          const len = Math.hypot(ln.x2 - ln.x1, ln.y2 - ln.y1) * mpp;
          const mx = (ln.x1 + ln.x2) / 2;
          const my = (ln.y1 + ln.y2) / 2;
          parts.push(
            `<text x="${mx}" y="${my - 4 * u}" font-size="${fontSz}" fill="#bbf7d0" text-anchor="middle" ` +
              `paint-order="stroke" stroke="#0b1220" stroke-width="${3 * u}" style="pointer-events:none">${len.toFixed(2)}m</text>`,
          );
        }
      }
      for (const arc of floorArcs) {
        const sel = this.selected?.kind === "floorCorner" && this.selected.id === arc.cornerId;
        if (!sel) continue;
        const r = arc.rPx;
        const sweep =
          (arc.t2.px - arc.t1.px) * (arc.center.py - arc.t1.py) - (arc.t2.py - arc.t1.py) * (arc.center.px - arc.t1.px) < 0
            ? 1
            : 0;
        parts.push(
          `<path data-kind="floor-corner-arc" data-id="${arc.cornerId}" fill="none" stroke="#fbbf24" stroke-width="${wallW * 2.4}" stroke-linecap="round" opacity="1" ` +
            `d="M ${arc.t1.px} ${arc.t1.py} A ${r} ${r} 0 0 ${sweep} ${arc.t2.px} ${arc.t2.py}"/>`,
        );
      }
      for (const c of this._floorCorners()) {
        if (this._floorExpand) continue;
        const sel = this.selected?.kind === "floorCorner" && this.selected.id === c.id;
        const hr = node_r * 1.15;
        nodeParts.push(
          `<rect data-kind="floor-corner" data-id="${c.id}" x="${c.px - hr}" y="${c.py - hr}" width="${hr * 2}" height="${hr * 2}" fill="transparent" style="cursor:grab"/>`,
          `<rect data-kind="floor-corner" data-id="${c.id}" x="${c.px - hr * 0.85}" y="${c.py - hr * 0.85}" width="${hr * 1.7}" height="${hr * 1.7}" ` +
            `fill="${sel ? "#fbbf24" : "#0b1220"}" stroke="#fbbf24" stroke-width="${thinW}" style="cursor:grab"/>`,
        );
      }
      if (this._floorExpand) {
        const ex = this._floorExpand;
        const chainPts = [{ px: ex.start.px, py: ex.start.py }, ...ex.points];
        const closePt = this._floorExpandHover;
        const cursor = this._floorExpandCursor;
        for (let i = 1; i < chainPts.length; i++) {
          const a = chainPts[i - 1];
          const b = chainPts[i];
          parts.push(
            `<line x1="${a.px}" y1="${a.py}" x2="${b.px}" y2="${b.py}" stroke="#fbbf24" stroke-width="${wallW}" stroke-dasharray="${6 * u} ${4 * u}" opacity="0.95"/>`,
          );
        }
        if (cursor) {
          const last = chainPts[chainPts.length - 1];
          parts.push(
            `<line x1="${last.px}" y1="${last.py}" x2="${cursor.px}" y2="${cursor.py}" stroke="#fbbf24" stroke-width="${wallW * 0.85}" stroke-dasharray="${4 * u} ${4 * u}" opacity="0.7"/>`,
          );
        }
        if (closePt && ex.points.length >= 1) {
          const last = chainPts[chainPts.length - 1];
          parts.push(
            `<line x1="${last.px}" y1="${last.py}" x2="${closePt.px}" y2="${closePt.py}" stroke="#fbbf24" stroke-width="${wallW * 0.9}" stroke-dasharray="${4 * u} ${3 * u}" opacity="0.85"/>`,
          );
          const preview = [...chainPts, { px: closePt.px, py: closePt.py }];
          const fillPts = preview.map((pt) => `${pt.px},${pt.py}`).join(" ");
          parts.push(
            `<polygon points="${fillPts}" fill="rgba(251,191,36,0.18)" stroke="#fbbf24" stroke-width="${thinW}" stroke-dasharray="${4 * u} ${3 * u}"/>`,
          );
          nodeParts.push(
            `<circle cx="${closePt.px}" cy="${closePt.py}" r="${node_r * 0.9}" fill="none" stroke="#fbbf24" stroke-width="${thinW * 1.2}"/>`,
          );
        }
        const pinTol = node_r * 0.35;
        const pins = [];
        for (const pt of chainPts) {
          if (!pins.some((q) => Math.hypot(q.px - pt.px, q.py - pt.py) < pinTol)) pins.push(pt);
        }
        for (const pt of pins) {
          nodeParts.push(
            `<circle cx="${pt.px}" cy="${pt.py}" r="${node_r * 0.75}" fill="#fbbf24" stroke="#0b1220" stroke-width="${thinW}"/>`,
          );
        }
      }
    }

    // Room tool: faint fills for all rooms + highlight on the selected block.
    if (this.tool === "room") {
      for (const f of roomFaces) {
        if (isBalconyType(f.type)) continue;
        const pts = f.polygon.map(([x, y]) => `${x},${y}`).join(" ");
        parts.push(`<polygon data-kind="roomfill" points="${pts}" fill="rgba(34,211,238,0.07)" stroke="transparent" />`);
      }
      if (this.selectedRoom?.loop?.length) {
        const pts = this.selectedRoom.loop
          .map((id) => {
            const n = m.nodes.get(String(id));
            return n ? `${n.px},${n.py}` : "";
          })
          .filter(Boolean)
          .join(" ");
        if (pts) parts.push(`<polygon points="${pts}" fill="rgba(34,211,238,0.30)" stroke="#22d3ee" stroke-width="${2 * u}" />`);
      }
    }

    // Walls (straight segments + rounded corner arcs)
    const { lines: wallLines, arcs: wallArcs } = planWallStrokes(m);
    for (const ln of wallLines) {
      const sel = this.selected?.kind === "edge" && this.selected.id === ln.edgeId;
      const stroke = sel ? "#fbbf24" : "#22d3ee";
      const w = sel ? wallW * 2.4 : wallW;
      parts.push(
        `<line data-kind="edge" data-id="${ln.edgeId}" x1="${ln.x1}" y1="${ln.y1}" x2="${ln.x2}" y2="${ln.y2}" ` +
          `stroke="${stroke}" stroke-width="${w}" stroke-linecap="round" opacity="${sel ? 1 : 0.95}"/>`,
      );
      if (this._showDims && this.model.metersPerPixel > 0) {
        const len = Math.hypot(ln.x2 - ln.x1, ln.y2 - ln.y1) * this.model.metersPerPixel;
        const mx = (ln.x1 + ln.x2) / 2;
        const my = (ln.y1 + ln.y2) / 2;
        parts.push(
          `<text x="${mx}" y="${my - 4 * u}" font-size="${fontSz}" fill="#e2f7ff" text-anchor="middle" ` +
            `paint-order="stroke" stroke="#0b1220" stroke-width="${3 * u}" style="pointer-events:none">${len.toFixed(2)}m</text>`,
        );
      }
    }
    for (const arc of wallArcs) {
      const sel = this.selected?.kind === "node" && this.selected.id === arc.nodeId;
      const stroke = sel ? "#fbbf24" : "#22d3ee";
      const w = sel ? wallW * 2.4 : wallW;
      const r = arc.rPx;
      const sweep =
        (arc.t2.px - arc.t1.px) * (arc.center.py - arc.t1.py) - (arc.t2.py - arc.t1.py) * (arc.center.px - arc.t1.px) < 0
          ? 1
          : 0;
      parts.push(
        `<path data-kind="corner-arc" data-id="${arc.nodeId}" fill="none" stroke="${stroke}" stroke-width="${w}" ` +
          `stroke-linecap="round" opacity="${sel ? 1 : 0.95}" ` +
          `d="M ${arc.t1.px} ${arc.t1.py} A ${r} ${r} 0 0 ${sweep} ${arc.t2.px} ${arc.t2.py}"/>`,
      );
    }

    // Wall pick layer (under openings) — wide hit targets along each segment.
    if (this.tool === "select" || this.tool === "delete") {
      for (const e of m.edges) {
        const A = m.nodes.get(String(e.a));
        const B = m.nodes.get(String(e.b));
        if (!A || !B) continue;
        const sel = this.selected?.kind === "edge" && this.selected.id === e.id;
        parts.push(
          `<line data-kind="edge" data-id="${e.id}" x1="${A.px}" y1="${A.py}" x2="${B.px}" y2="${B.py}" ` +
            `stroke="${sel ? "rgba(251,191,36,0.18)" : "transparent"}" stroke-width="${20 * u}" style="cursor:pointer"/>`,
        );
        const showX = this.tool === "delete" || sel;
        if (showX) {
          const mx = (A.px + B.px) / 2;
          const my = (A.py + B.py) / 2;
          const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
          const ux = (B.px - A.px) / len;
          const uy = (B.py - A.py) / len;
          const dx = mx - uy * (14 * u);
          const dy = my + ux * (14 * u);
          deleteParts.push(this._deleteBtnSvg("edelete", e.id, dx, dy, u, thinW));
        }
      }
    }

    // Layout centroid (pixel space) — the "interior" side reference. Mirrors the
    // server's inward-normal convention (normal flipped to point at the envelope
    // center) so a door's "swing: in" arc here matches which way it opens in 3D.
    let cenX = 0;
    let cenY = 0;
    let cenN = 0;
    for (const [, n] of m.nodes) {
      cenX += n.px;
      cenY += n.py;
      cenN++;
    }
    if (cenN) {
      cenX /= cenN;
      cenY /= cenN;
    }

    // Openings
    for (const o of m.openings) {
      const edge = m.edges.find((e) => e.id === o.edge);
      if (!edge) continue;
      const A = m.nodes.get(String(edge.a));
      const B = m.nodes.get(String(edge.b));
      if (!A || !B) continue;
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      const t0 = Math.max(0, Math.min(len, o.t));
      const t1 = Math.max(0, Math.min(len, o.t + o.w));
      const s = { px: A.px + ux * t0, py: A.py + uy * t0 };
      const en = { px: A.px + ux * t1, py: A.py + uy * t1 };
      const isWindow = o.type === "window";
      const color = isWindow ? "#38bdf8" : "#f59e0b";
      const sel = this.selected?.kind === "opening" && this.selected.id === o.id;
      const mx2 = (s.px + en.px) / 2;
      const my2 = (s.py + en.py) / 2;
      // Inward normal (toward centroid) — interior side for glass/sill/chips.
      let inx = -uy;
      let iny = ux;
      if ((cenX - mx2) * inx + (cenY - my2) * iny < 0) {
        inx = -inx;
        iny = -iny;
      }

      if (isWindow) {
        const frameW = sel ? 2.4 * u : 2 * u;
        const frameOff = 2.2 * u;
        const paneDepth = sel ? 9 * u : 7 * u;
        const glass = sel ? "rgba(125,211,252,0.42)" : "rgba(125,211,252,0.28)";
        const stroke = sel ? "#7dd3fc" : color;

        if (sel) {
          parts.push(
            `<line x1="${s.px}" y1="${s.py}" x2="${en.px}" y2="${en.py}" ` +
              `stroke="rgba(56,189,248,0.35)" stroke-width="${14 * u}" stroke-linecap="butt" style="pointer-events:none"/>`,
          );
        }

        // Glass pane strip into the room (reads clearly unlike a door leaf).
        const g0 = { px: s.px + inx * 2.5 * u, py: s.py + iny * 2.5 * u };
        const g1 = { px: en.px + inx * 2.5 * u, py: en.py + iny * 2.5 * u };
        const g2 = { px: en.px + inx * paneDepth, py: en.py + iny * paneDepth };
        const g3 = { px: s.px + inx * paneDepth, py: s.py + iny * paneDepth };
        parts.push(
          `<polygon points="${g0.px},${g0.py} ${g1.px},${g1.py} ${g2.px},${g2.py} ${g3.px},${g3.py}" ` +
            `fill="${glass}" stroke="${stroke}" stroke-width="${thinW * 0.8}" style="pointer-events:none"/>`,
        );

        // Double frame rails along the wall opening.
        for (const sign of [-1, 1]) {
          const ox = -iny * frameOff * sign;
          const oy = inx * frameOff * sign;
          parts.push(
            `<line x1="${s.px + ox}" y1="${s.py + oy}" x2="${en.px + ox}" y2="${en.py + oy}" ` +
              `stroke="${stroke}" stroke-width="${frameW}" stroke-linecap="butt" style="pointer-events:none"/>`,
          );
        }

        // Center mullion + sill bar on the interior edge.
        const mullLen = paneDepth - 2 * u;
        parts.push(
          `<line x1="${mx2}" y1="${my2}" x2="${mx2 + inx * mullLen}" y2="${my2 + iny * mullLen}" ` +
            `stroke="${stroke}" stroke-width="${1.6 * u}" opacity="0.9" style="pointer-events:none"/>`,
          `<line x1="${g3.px}" y1="${g3.py}" x2="${g2.px}" y2="${g2.py}" ` +
            `stroke="${stroke}" stroke-width="${3.2 * u}" stroke-linecap="round" style="pointer-events:none"/>`,
        );

        // Invisible pick target along the wall segment.
        parts.push(
          `<line data-kind="opening" data-id="${o.id}" x1="${s.px}" y1="${s.py}" x2="${en.px}" y2="${en.py}" ` +
            `stroke="transparent" stroke-width="${8 * u}" stroke-linecap="butt"/>`,
        );
      } else {
        parts.push(
          `<line data-kind="opening" data-id="${o.id}" x1="${s.px}" y1="${s.py}" x2="${en.px}" y2="${en.py}" ` +
            `stroke="${color}" stroke-width="${5 * u}" stroke-linecap="butt" opacity="0.98"/>`,
        );
      }

      // Door swing symbol (leaf line + quarter-circle arc) so the user can see and
      // match how the door is hinged/opened in the original drawing.
      if (o.type === "door") {
        const hingeRight = normHinge(o.hinge) === "right";
        const H = hingeRight ? en : s; // hinge jamb
        const J = hingeRight ? s : en; // far jamb (closed-leaf tip)
        const R = Math.hypot(J.px - H.px, J.py - H.py) || 1;
        // Inward normal (toward centroid), then flip for "swing: out".
        let nx = inx;
        let ny = iny;
        if (normSwing(o.swing) === "out") {
          nx = -nx;
          ny = -ny;
        }
        const a0 = Math.atan2((J.py - H.py) / R, (J.px - H.px) / R); // closed dir
        const a1 = Math.atan2(ny, nx); // open dir
        let d = a1 - a0;
        while (d > Math.PI) d -= 2 * Math.PI;
        while (d < -Math.PI) d += 2 * Math.PI;
        const arc = [];
        for (let k = 0; k <= 10; k++) {
          const th = a0 + (d * k) / 10;
          arc.push(`${(H.px + R * Math.cos(th)).toFixed(1)},${(H.py + R * Math.sin(th)).toFixed(1)}`);
        }
        const tipX = H.px + nx * R;
        const tipY = H.py + ny * R;
        parts.push(
          `<line x1="${H.px}" y1="${H.py}" x2="${tipX}" y2="${tipY}" stroke="${color}" stroke-width="${1.6 * u}" opacity="0.9" style="pointer-events:none"/>`,
          `<polyline points="${arc.join(" ")}" fill="none" stroke="${color}" stroke-width="${1.4 * u}" stroke-dasharray="${4 * u} ${3 * u}" opacity="0.85" style="pointer-events:none"/>`,
          `<circle cx="${H.px}" cy="${H.py}" r="${2.8 * u}" fill="${color}" stroke="#fff" stroke-width="${thinW * 0.8}" style="pointer-events:none"/>`,
        );

        // Selected-door controls — two glass chips on the exterior side (outside the wall).
        if (sel) {
          const chipH = 44 * u;
          let outx = -inx;
          let outy = -iny;
          const chipW = 96 * u;
          const btnGap = 10 * u;
          const wallOff = 52 * u;
          let panelCx = mx2 + outx * wallOff;
          let panelCy = my2 + outy * wallOff;
          const span = chipW + btnGap;
          let hingeCx = panelCx - ux * (span / 2);
          let hingeCy = panelCy - uy * (span / 2);
          let swingCx = panelCx + ux * (span / 2);
          let swingCy = panelCy + uy * (span / 2);

          // If exterior side still clips the margin, fall back to interior.
          const minX = Math.min(hingeCx, swingCx) - chipW / 2;
          const maxX = Math.max(hingeCx, swingCx) + chipW / 2;
          const minY = Math.min(hingeCy, swingCy) - chipH / 2;
          const maxY = Math.max(hingeCy, swingCy) + chipH / 2;
          const margin = pad * 0.92;
          if (
            minX < -margin ||
            minY < -margin ||
            maxX > m.naturalW + margin ||
            maxY > m.naturalH + margin
          ) {
            outx = inx;
            outy = iny;
            panelCx = mx2 + outx * wallOff;
            panelCy = my2 + outy * wallOff;
            hingeCx = panelCx - ux * (span / 2);
            hingeCy = panelCy - uy * (span / 2);
            swingCx = panelCx + ux * (span / 2);
            swingCy = panelCy + uy * (span / 2);
          }

          const hingeLeft = normHinge(o.hinge) === "left";
          const swingIn = normSwing(o.swing) === "in";
          const hingeTitle = hingeLeft ? "Left hinge ◄" : "► Right hinge";
          const swingTitle = swingIn ? "Opens inward" : "Opens outward";

          controlParts.push(
            this._doorControlChipSvg("dhinge", o.id, hingeTitle, "Tap to flip hinge", hingeCx, hingeCy, u),
            this._doorControlChipSvg("dswing", o.id, swingTitle, "Tap to flip swing", swingCx, swingCy, u),
          );
        }
      }
      if (sel) {
        const hr = isWindow ? node_r * 1.25 : node_r;
        const handleStroke = isWindow ? (sel ? "#7dd3fc" : color) : color;
        for (const [pt, role] of [
          [s, "start"],
          [en, "end"],
        ]) {
          parts.push(
            `<rect data-kind="ohandle" data-id="${o.id}" data-role="${role}" x="${pt.px - hr}" y="${pt.py - hr}" ` +
              `width="${hr * 2}" height="${hr * 2}" rx="${isWindow ? 2 * u : 0}" fill="#fff" stroke="${handleStroke}" stroke-width="${thinW * (isWindow ? 1.4 : 1)}"/>`,
          );
        }
      }

      // Red ✕ remove handle — shown for the matching tool (or when selected).
      const showX =
        this.tool === "delete" ||
        sel ||
        (this.tool === "door" && o.type === "door") ||
        (this.tool === "window" && o.type === "window");
      if (showX) {
        const mx = (s.px + en.px) / 2 - uy * (12 * u);
        const my = (s.py + en.py) / 2 + ux * (12 * u);
        deleteParts.push(this._deleteBtnSvg("odelete", o.id, mx, my, u, thinW));
      }

      // Selected-window info chips: type label + width + sill + curtain toggle.
      if (sel && isWindow) {
        const mcx = mx2 + inx * (18 * u);
        const mcy = my2 + iny * (18 * u);
        const widthCm = mpp > 0 ? Math.round(o.w * mpp * 100) : null;
        const sillCm = Math.round((Number(o.sill) || 0.9) * 100);
        const widthLabel = widthCm != null ? `${widthCm} cm` : "—";
        const winColor = "#38bdf8";
        const chipOff = 38 * u / 2 + 2 * u;
        const hasCurtain = windowHasCurtain(o);
        // Curtain chip on the exterior side (same placement language as door hinge/swing).
        let outx = -inx;
        let outy = -iny;
        const wallOff = 52 * u;
        let curtainCx = mx2 + outx * wallOff;
        let curtainCy = my2 + outy * wallOff;
        const chipW = 96 * u;
        const chipH = 44 * u;
        const margin = pad * 0.92;
        if (
          curtainCx - chipW / 2 < -margin ||
          curtainCy - chipH / 2 < -margin ||
          curtainCx + chipW / 2 > m.naturalW + margin ||
          curtainCy + chipH / 2 > m.naturalH + margin
        ) {
          outx = inx;
          outy = iny;
          curtainCx = mx2 + outx * wallOff;
          curtainCy = my2 + outy * wallOff;
        }
        controlParts.push(
          this._labelChipSvg("Window", mcx, mcy - uy * (23 * u), u, thinW, winColor),
          this._labelChipSvg(widthLabel, mcx - ux * chipOff, mcy - uy * chipOff, u, thinW, winColor),
          this._labelChipSvg(`sill ${sillCm} cm`, mcx + ux * chipOff, mcy + uy * chipOff, u, thinW, winColor),
          this._doorControlChipSvg(
            "wcurtain",
            o.id,
            hasCurtain ? "Curtains on" : "Curtains off",
            hasCurtain ? "Tap to remove" : "Tap to add",
            curtainCx,
            curtainCy,
            u,
          ),
        );
      }
    }

    // Ceiling lights — amber bulb glyphs the user drops into rooms. Editable with
    // the Move/Light/Delete tools; the ✕ removes one.
    for (const l of m.lights ?? []) {
      const sel = this.selected?.kind === "light" && this.selected.id === l.id;
      const r = 9 * u;
      const col = sel ? "#fde68a" : "#fbbf24";
      // Soft glow + bulb body + filament cross so the fixture reads as a light.
      parts.push(
        `<g style="cursor:pointer">` +
          `<circle data-kind="light" data-id="${l.id}" cx="${l.px}" cy="${l.py}" r="${r * 1.9}" fill="rgba(251,191,36,0.14)" stroke="transparent"/>` +
          `<circle data-kind="light" data-id="${l.id}" cx="${l.px}" cy="${l.py}" r="${r}" fill="${col}" stroke="#0b1220" stroke-width="${thinW}"/>` +
          `<path d="M ${l.px - r * 0.5} ${l.py} L ${l.px + r * 0.5} ${l.py} M ${l.px} ${l.py - r * 0.5} L ${l.px} ${l.py + r * 0.5}" stroke="#0b1220" stroke-width="${thinW}" style="pointer-events:none"/>` +
          `</g>`,
      );
      const flabel = (LIGHT_TYPES.find((t) => t.id === l.fixture)?.label ?? "Ceiling");
      parts.push(
        `<text x="${l.px}" y="${l.py + r * 2.6}" font-size="${11 * u}" fill="#fbbf24" text-anchor="middle" ` +
          `paint-order="stroke" stroke="#0b1220" stroke-width="${3 * u}" style="pointer-events:none">${flabel}</text>`,
      );
      if (this.tool === "delete" || this.tool === "light" || sel) {
        const dx = l.px + r * 1.4;
        const dy = l.py - r * 1.4;
        deleteParts.push(this._deleteBtnSvg("ldelete", l.id, dx, dy, u, thinW));
      }
    }

    // Light switches — rocker plates snapped to walls, arrow shows interior side.
    for (const sw of m.switches ?? []) {
      this._ensureSwitchPlacement(sw);
      const sel = this.selected?.kind === "switch" && this.selected.id === sw.id;
      const nx = sw.nx ?? 0;
      const ny = sw.ny ?? -1;
      const tx = -ny;
      const ty = nx;
      const ang = (Math.atan2(ty, tx) * 180) / Math.PI;
      const plateW = (sel ? 18 : 15) * u;
      const plateD = (sel ? 5.5 : 4.5) * u;
      const stroke = sel ? "#6ee7b7" : "#34d399";
      const swW = sel ? 2.4 * u : 1.8 * u;
      const rockerW = plateW * 0.42;
      const rockerD = plateD * 0.55;
      parts.push(
        `<g transform="translate(${sw.px},${sw.py}) rotate(${ang})" style="cursor:pointer">` +
          (sel
            ? `<rect x="${-plateW / 2 - 2.5 * u}" y="${-plateD / 2 - 2.5 * u}" width="${plateW + 5 * u}" height="${plateD + 5 * u}" ` +
              `rx="${3 * u}" fill="rgba(52,211,153,0.14)" style="pointer-events:none"/>`
            : "") +
          `<rect data-kind="switch" data-id="${sw.id}" x="${-plateW / 2}" y="${-plateD / 2}" width="${plateW}" height="${plateD}" rx="${2.5 * u}" ` +
          `fill="#0b1220" stroke="${stroke}" stroke-width="${swW}"/>` +
          `<rect x="${-rockerW / 2}" y="${plateD * 0.02}" width="${rockerW}" height="${rockerD}" rx="${1.8 * u}" ` +
          `fill="${stroke}" opacity="${sw.on === false ? 0.45 : 0.95}" style="pointer-events:none"/>` +
          `<path d="M 0 ${plateD * 0.72} L ${-3.5 * u} ${plateD * 0.38} M 0 ${plateD * 0.72} L ${3.5 * u} ${plateD * 0.38}" ` +
          `stroke="${stroke}" stroke-width="${1.6 * u}" stroke-linecap="round" fill="none" style="pointer-events:none"/>` +
          `</g>`,
      );
      if (this.tool === "delete" || this.tool === "switch" || sel) {
        const dx = sw.px + nx * 14 * u + tx * 10 * u;
        const dy = sw.py + ny * 14 * u + ty * 10 * u;
        deleteParts.push(this._deleteBtnSvg("swdelete", sw.id, dx, dy, u, thinW));
      }
    }

    // Catalog furniture — top-down footprint with item thumbnail + label.
    const drawFurniture = (f, { ghost = false } = {}) => {
      const meta = this._catalogMeta(f.asset_id) ?? { name: f.asset_id, footprint: { w: 1, d: 1 } };
      const thumb = itemThumb(meta);
      const fp = meta.footprint ?? { w: 1, d: 1 };
      const { wPx, dPx } = this._footprintPx(fp);
      const sel = !ghost && this.selected?.kind === "furniture" && this.selected.id === f.id;
      const rot = -(f.rotY ?? 0);
      const stroke = ghost ? "#38bdf8" : sel ? "#fbbf24" : "#38bdf8";
      const fill = ghost ? "rgba(56,189,248,0.12)" : "rgba(56,189,248,0.22)";
      const dash = ghost ? ` stroke-dasharray="${5 * u} ${4 * u}"` : "";
      parts.push(
        `<g data-kind="${ghost ? "furniture-ghost" : "furniture"}" data-id="${f.id ?? 0}" ` +
          `transform="translate(${f.px},${f.py}) rotate(${rot})" style="cursor:${ghost ? "crosshair" : "grab"}">` +
          `<rect data-kind="furniture" data-id="${f.id}" x="${-wPx / 2}" y="${-dPx / 2}" width="${wPx}" height="${dPx}" rx="${2 * u}" fill="${fill}" stroke="${stroke}" stroke-width="${thinW}"${dash}/>` +
          `<image href="${thumb}" x="${-wPx / 2}" y="${-dPx / 2}" width="${wPx}" height="${dPx}" preserveAspectRatio="xMidYMid meet" opacity="${ghost ? 0.55 : 0.92}" style="pointer-events:none"/>` +
          `<text x="0" y="${dPx / 2 + 14 * u}" text-anchor="middle" font-size="${10 * u}" fill="#e0f2fe" ` +
          `paint-order="stroke" stroke="#0b1220" stroke-width="${2 * u}" style="pointer-events:none">${this._svgEsc(meta.name)}</text>` +
          (sel
            ? `<line x1="0" y1="${-dPx / 2}" x2="0" y2="${-dPx / 2 - 14 * u}" stroke="#fbbf24" stroke-width="${thinW}" style="pointer-events:none"/>` +
              `<circle data-kind="frotate" data-id="${f.id}" cx="0" cy="${-dPx / 2 - 14 * u}" r="${6 * u}" fill="#fbbf24" stroke="#0b1220" stroke-width="${thinW}" style="cursor:grab"/>`
            : "") +
          `</g>`,
      );
      if (!ghost && (this.tool === "delete" || sel)) {
        const dx = f.px + wPx * 0.35;
        const dy = f.py - dPx * 0.35;
        deleteParts.push(this._deleteBtnSvg("fdelete", f.id, dx, dy, u, thinW));
      }
    };
    for (const f of m.furniture ?? []) drawFurniture(f);
    if (this._placingCatalog && this._furnitureGhostPos) {
      drawFurniture(
        {
          id: 0,
          asset_id: this._placingCatalog.asset_id,
          px: this._furnitureGhostPos.px,
          py: this._furnitureGhostPos.py,
          rotY: this._placingCatalog.rotY ?? 0,
        },
        { ghost: true },
      );
    }

    // Nodes (corner handles) — rendered above opening picks so corners win clicks.
    for (const [, n] of m.nodes) {
      const sel = this.selected?.kind === "node" && this.selected.id === n.id;
      const chain = this._wallChain === n.id;
      nodeParts.push(
        `<circle data-kind="node" data-id="${n.id}" cx="${n.px}" cy="${n.py}" r="${node_r * 2.4}" fill="transparent" style="cursor:grab"/>`,
      );
      nodeParts.push(
        `<circle data-kind="node" data-id="${n.id}" cx="${n.px}" cy="${n.py}" r="${node_r}" ` +
          `fill="${sel || chain ? "#22d3ee" : "#0b1220"}" stroke="#22d3ee" stroke-width="${thinW}" style="cursor:grab"/>`,
      );
    }

    // Calibration line
    if (this._calib?.a) {
      const a = this._calib.a;
      const b = this._calib.b ?? a;
      parts.push(
        `<line x1="${a.px}" y1="${a.py}" x2="${b.px}" y2="${b.py}" stroke="#facc15" stroke-width="${2 * u}" stroke-dasharray="${6 * u} ${4 * u}" style="pointer-events:none"/>`,
      );
      parts.push(`<circle cx="${a.px}" cy="${a.py}" r="${4 * u}" fill="#facc15" style="pointer-events:none"/>`);
      if (this._calib.b) parts.push(`<circle cx="${b.px}" cy="${b.py}" r="${4 * u}" fill="#facc15" style="pointer-events:none"/>`);
    }

    // Opening pick layer — doors/windows beat wall lines when clicked (not corners).
    for (const o of m.openings) {
      const edge = m.edges.find((e) => e.id === o.edge);
      if (!edge) continue;
      const A = m.nodes.get(String(edge.a));
      const B = m.nodes.get(String(edge.b));
      if (!A || !B) continue;
      const len = Math.hypot(B.px - A.px, B.py - A.py) || 1;
      const ux = (B.px - A.px) / len;
      const uy = (B.py - A.py) / len;
      const t0 = Math.max(0, Math.min(len, o.t));
      const t1 = Math.max(0, Math.min(len, o.t + o.w));
      const s = { px: A.px + ux * t0, py: A.py + uy * t0 };
      const en = { px: A.px + ux * t1, py: A.py + uy * t1 };
      const sel = this.selected?.kind === "opening" && this.selected.id === o.id;
      parts.push(
        `<line data-kind="opening" data-id="${o.id}" x1="${s.px}" y1="${s.py}" x2="${en.px}" y2="${en.py}" ` +
          `stroke="transparent" stroke-width="${22 * u}" stroke-linecap="round" style="cursor:pointer"/>`,
      );
      const mx = (s.px + en.px) / 2;
      const my = (s.py + en.py) / 2;
      parts.push(
        `<circle data-kind="opening" data-id="${o.id}" cx="${mx}" cy="${my}" r="${12 * u}" fill="transparent" style="cursor:pointer"/>`,
      );
    }

    parts.push(...nodeParts);
    parts.push(...controlParts);

    // Delete handles on top — must beat opening/wall pick layers so ✕ clicks register.
    parts.push(...deleteParts);

    this.elSvg.innerHTML = parts.join("");
    requestAnimationFrame(() => this._syncStageScrollbar());
  }

  // ---- commit ---------------------------------------------------------------

  /**
   * Trace planar minimal cycles (interior faces). Returns one entry per room
   * with its node loop, edge loop, signed pixel area and centroid. Used at
   * commit-time for room derivation.
   */
  _computeFaces() {
    const edges = this.model?.edges ?? [];
    if (edges.length < 3) return [];

    // Build adjacency with angles.
    const adj = new Map(); // nodeId -> [{edgeId, to, ang}]
    const addAdj = (from, to, edgeId) => {
      const A = this.model.nodes.get(String(from));
      const B = this.model.nodes.get(String(to));
      if (!A || !B) return;
      const ang = Math.atan2(B.py - A.py, B.px - A.px);
      if (!adj.has(from)) adj.set(from, []);
      adj.get(from).push({ edgeId, to, ang });
    };
    for (const e of edges) {
      addAdj(e.a, e.b, e.id);
      addAdj(e.b, e.a, e.id);
    }
    for (const [, arr] of adj) arr.sort((p, q) => p.ang - q.ang);

    const nextHalf = (from, to) => {
      // Arrive at `to` from `from`; pick the next edge clockwise-most.
      const arr = adj.get(to) ?? [];
      let idx = arr.findIndex((x) => x.to === from);
      if (idx < 0) idx = 0;
      return arr[(idx - 1 + arr.length) % arr.length];
    };

    const visited = new Set(); // "from->to"
    const faces = [];
    for (const e of edges) {
      for (const [from, to] of [
        [e.a, e.b],
        [e.b, e.a],
      ]) {
        if (visited.has(`${from}->${to}`)) continue;
        const faceNodes = [];
        const faceEdges = [];
        let cf = from;
        let ct = to;
        let guard = 0;
        while (guard++ < edges.length * 2 + 5) {
          const k = `${cf}->${ct}`;
          if (visited.has(k)) break;
          visited.add(k);
          faceNodes.push(cf);
          const eRec = edges.find((x) => (x.a === cf && x.b === ct) || (x.a === ct && x.b === cf));
          if (eRec) faceEdges.push(eRec.id);
          const nh = nextHalf(cf, ct);
          if (!nh) break;
          cf = ct;
          ct = nh.to;
          if (cf === from && ct === to) break;
        }
        if (faceNodes.length >= 3) faces.push({ nodes: faceNodes, edges: faceEdges });
      }
    }

    const signedArea = (face) => {
      let a = 0;
      for (let i = 0; i < face.nodes.length; i++) {
        const A = this.model.nodes.get(String(face.nodes[i]));
        const B = this.model.nodes.get(String(face.nodes[(i + 1) % face.nodes.length]));
        if (!A || !B) return 0;
        a += A.px * B.py - B.px * A.py;
      }
      return a / 2;
    };
    const withArea = faces.map((f) => ({ ...f, area: signedArea(f) })).filter((f) => Math.abs(f.area) > 1);
    if (!withArea.length) return [];

    // Outer boundary = the face with the largest absolute area; drop it.
    let outerIdx = 0;
    for (let i = 1; i < withArea.length; i++) {
      if (Math.abs(withArea[i].area) > Math.abs(withArea[outerIdx].area)) outerIdx = i;
    }
    const interior = withArea.filter((_, i) => i !== outerIdx);

    // De-dup faces with the same edge set (both traversal directions trace it).
    const seen = new Set();
    const rooms = [];
    for (const f of interior) {
      const key = [...f.edges].sort((a, b) => a - b).join(",");
      if (seen.has(key)) continue;
      seen.add(key);
      let cx = 0;
      let cy = 0;
      const polygon = [];
      for (const nid of f.nodes) {
        const n = this.model.nodes.get(String(nid));
        cx += n.px;
        cy += n.py;
        polygon.push([n.px, n.py]);
      }
      cx /= f.nodes.length;
      cy /= f.nodes.length;
      const room = {
        key,
        nodes: f.nodes,
        edges: f.edges,
        polygon,
        areaPx: Math.abs(f.area),
        centroid: [cx, cy],
      };
      const meta = this._typeForFace(room);
      room.type = meta.type;
      room.viewLen = meta.viewLen;
      rooms.push(room);
    }
    return rooms;
  }

  /** Commit-time: faces → room loops in committed graph-edge ids (carry type/view). */
  _deriveRooms(idMapToGraph) {
    return this._computeFaces().map((f, i) => ({
      id: `R${i + 1}`,
      edges: f.edges.map((eid) => idMapToGraph.get(eid)).filter(Boolean),
      ...(isBalconyType(f.type) ? { type: "balcony", viewLength: f.viewLen || DEFAULT_VIEW_LEN } : {}),
    }));
  }

  // ---- room-type tags (kept by face key + centroid so they survive recomputes) -----

  _faceKeyFromEdges(edgeIds) {
    return [...edgeIds].sort((a, b) => a - b).join(",");
  }

  _edgeFaceMap(faces) {
    const map = new Map();
    for (const face of faces ?? []) {
      for (const eid of face.edges ?? []) {
        if (!map.has(eid)) map.set(eid, []);
        map.get(eid).push(face);
      }
    }
    return map;
  }

  _isExteriorSceneWall(wall, borderingFaces) {
    const id = String(wall?.id ?? "");
    if (id.startsWith("w_ext") || /^w_(south|north|east|west)$/.test(id)) return true;
    return (borderingFaces?.length ?? 0) === 1;
  }

  /** Geometric fallback: map a live scene wall back to a planner edge id. */
  _matchPlannerEdgeForWall(wall) {
    if (!wall || !this.model?.edges?.length) return null;
    for (const e of this.model.edges) {
      if (e.sceneWallId === wall.id) return e.id;
    }
    const ax = wall.start?.[0];
    const az = wall.start?.[1];
    const bx = wall.end?.[0];
    const bz = wall.end?.[1];
    if (![ax, az, bx, bz].every(Number.isFinite)) return null;
    let best = null;
    let bestScore = Infinity;
    for (const e of this.model.edges) {
      const A = this.model.nodes.get(String(e.a));
      const B = this.model.nodes.get(String(e.b));
      if (!A || !B) continue;
      const wa = this._pxToWorld(A.px, A.py);
      const wb = this._pxToWorld(B.px, B.py);
      const elen = Math.hypot(wb.x - wa.x, wb.z - wa.z) || 1;
      const wlen = Math.hypot(bx - ax, bz - az) || 1;
      if (Math.abs(wlen - elen) > Math.max(0.35, wlen * 0.12)) continue;
      const d1 = Math.hypot(ax - wa.x, az - wa.z) + Math.hypot(bx - wb.x, bz - wb.z);
      const d2 = Math.hypot(ax - wb.x, az - wb.z) + Math.hypot(bx - wa.x, bz - wa.z);
      const score = Math.min(d1, d2);
      if (score < bestScore) {
        bestScore = score;
        best = e;
      }
    }
    if (!best || bestScore >= 0.45) return null;
    best.sceneWallId = wall.id;
    if (!wall.edgeId) wall.edgeId = `E${best.id}`;
    return best.id;
  }

  /** Keep the wood floor slab visible after balcony wall patches. */
  _ensureSceneFloor() {
    const room = this.scene?.room;
    const roomRoot = this.scene?.roomRoot;
    if (!room?.bounds || !roomRoot || !this.app) return;
    const floor = roomRoot.findByName?.("floor") ?? roomRoot.children.find((c) => c.name === "floor");
    const ok =
      floor?.render?.enabled &&
      (floor.render.meshInstances?.length > 0 || Boolean(floor.render.type));
    if (!ok) refreshEnvelope(this.app, room, roomRoot);
  }

  /** Map graph edge ids (E12) → planner edge ids used in _computeFaces(). */
  _graphEdgeToPlanner() {
    const map = new Map();
    for (const e of this.model?.edges ?? []) map.set(`E${e.id}`, e.id);
    return map;
  }

  _roomCentroidPx(nodeIds) {
    let cx = 0;
    let cy = 0;
    let n = 0;
    for (const id of nodeIds) {
      const nd = this.model?.nodes.get(String(id));
      if (!nd) continue;
      cx += nd.px;
      cy += nd.py;
      n++;
    }
    return n ? [cx / n, cy / n] : null;
  }

  /** Tag tolerance in model px — tight so only the intended room face matches. */
  _roomTagTol() {
    return Math.max(18, Math.min(this.model?.naturalW ?? 1000, this.model?.naturalH ?? 1000) * 0.025);
  }

  _findRoomMeta(centroid) {
    if (!centroid || !this._roomMeta?.length) return null;
    const face = this._faceAtPoint({ px: centroid[0], py: centroid[1] });
    if (face) return this._findRoomMetaForFace(face);
    const tol = this._roomTagTol();
    let best = null;
    let bestD = tol;
    for (const m of this._roomMeta) {
      const d = Math.hypot(m.cx - centroid[0], m.cy - centroid[1]);
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }

  /** Resolve a balcony tag for one traced room face (keyed by its wall loop). */
  _findRoomMetaForFace(face) {
    if (!face || !this._roomMeta?.length) return null;
    const key = face.key ?? this._faceKeyFromEdges(face.edges ?? []);
    const byKey = this._roomMeta.find((m) => m.faceKey === key);
    if (byKey) return byKey;
    const tol = this._roomTagTol();
    let best = null;
    let bestD = tol;
    for (const m of this._roomMeta) {
      if (!this._pointInPoly({ px: m.cx, py: m.cy }, face.polygon)) continue;
      const d = Math.hypot(m.cx - face.centroid[0], m.cy - face.centroid[1]);
      if (d < bestD) {
        bestD = d;
        best = m;
      }
    }
    return best;
  }

  /** Type + view length the given face currently carries (default ordinary room). */
  _typeForFace(face) {
    const meta = this._findRoomMetaForFace(face);
    return { type: meta?.type ?? "room", viewLen: meta?.viewLen ?? DEFAULT_VIEW_LEN };
  }

  /** Upsert a tag for the room face at a centroid; ordinary "room" clears the tag. */
  _setRoomTagAt(centroid, type, viewLen) {
    if (!centroid) return;
    const face = this._faceAtPoint({ px: centroid[0], py: centroid[1] });
    if (!face) return;
    if (!this._roomMeta) this._roomMeta = [];
    const faceKey = face.key ?? this._faceKeyFromEdges(face.edges ?? []);
    const existing = this._findRoomMetaForFace(face);
    if (!isBalconyType(type)) {
      if (existing) this._roomMeta = this._roomMeta.filter((m) => m !== existing);
      return;
    }
    if (existing) {
      existing.faceKey = faceKey;
      existing.cx = face.centroid[0];
      existing.cy = face.centroid[1];
      existing.type = "balcony";
      if (viewLen != null) existing.viewLen = viewLen;
    } else {
      this._roomMeta.push({
        faceKey,
        cx: face.centroid[0],
        cy: face.centroid[1],
        type: "balcony",
        viewLen: viewLen ?? DEFAULT_VIEW_LEN,
      });
    }
  }

  /** Apply a type change to the currently-selected room block. */
  _setSelectedRoomType(type) {
    if (!this.selectedRoom) return;
    const c = this._roomCentroidPx(this._selectedRoomNodeIds());
    const prev = this._findRoomMeta(c);
    this._setRoomTagAt(c, type, prev?.viewLen);
    this._status(isBalconyType(type) ? "Room set to Balcony — its outer walls open to a view." : "Room set to ordinary room.");
    this._syncRoomCtl();
    this._syncBalconyMetaLive();
    this._render();
    this._record();
  }

  /** Set the balcony view length (meters) for the selected room. */
  _setSelectedRoomViewLen(meters) {
    if (!this.selectedRoom) return;
    const v = Math.max(1, Math.min(40, Number(meters) || DEFAULT_VIEW_LEN));
    const c = this._roomCentroidPx(this._selectedRoomNodeIds());
    this._setRoomTagAt(c, "balcony", v);
    this._syncRoomCtl();
    this._syncBalconyMetaLive();
    this._render();
    this._record();
  }

  /** Update balcony railings in the live scene without a full room rebuild. */
  _syncBalconyMetaLive() {
    const room = this.scene?.room;
    if (!room?.walls?.length) return;
    const faces = this._computeFaces();
    const edgeFaces = this._edgeFaceMap(faces);
    const graphToPlanner = this._graphEdgeToPlanner();
    const plannerBySceneWall = new Map();
    for (const e of this.model?.edges ?? []) {
      if (e.sceneWallId) plannerBySceneWall.set(e.sceneWallId, e.id);
    }

    for (const w of room.walls) {
      delete w.view;
      delete w.viewLength;
    }

    const wallIds = [];
    for (const w of room.walls) {
      let plannerEid = graphToPlanner.get(String(w.edgeId ?? ""));
      if (plannerEid == null) plannerEid = plannerBySceneWall.get(w.id);
      if (plannerEid == null) plannerEid = this._matchPlannerEdgeForWall(w);
      if (plannerEid == null) continue;
      const bordering = edgeFaces.get(plannerEid) ?? [];
      if (!this._isExteriorSceneWall(w, bordering)) continue;

      let isView = false;
      let vl = DEFAULT_VIEW_LEN;
      for (const face of bordering) {
        if (!isBalconyType(face.type)) continue;
        isView = true;
        vl = Math.max(vl, face.viewLen ?? DEFAULT_VIEW_LEN);
      }
      if (!isView) continue;
      w.view = true;
      w.viewLength = vl;
      wallIds.push(w.id);
    }

    if (room.rooms?.length) {
      for (const r of room.rooms) {
        delete r.type;
        delete r.viewLength;
      }
      for (const face of faces) {
        if (!isBalconyType(face.type) || !face.centroid) continue;
        const { x, z } = this._pxToWorld(face.centroid[0], face.centroid[1]);
        let best = null;
        let bestD = 2.5;
        for (const r of room.rooms) {
          const rcx = (r.x ?? 0) + (r.width ?? 0) / 2;
          const rcz = (r.z ?? 0) + (r.depth ?? 0) / 2;
          const d = Math.hypot(x - rcx, z - rcz);
          if (d < bestD) {
            bestD = d;
            best = r;
          }
        }
        if (best) {
          best.type = "balcony";
          best.viewLength = face.viewLen ?? DEFAULT_VIEW_LEN;
        }
      }
    }

    if (wallIds.length) {
      const ids = [...new Set(wallIds)];
      this.app.fire("scene:openings-patched", { wallIds: ids });
    }
    this._ensureSceneFloor();
  }

  /** Reflect the selected room's type + view length in the room-control panel. */
  _syncRoomCtl() {
    if (!this._roomTypePicker) return;
    const has = Boolean(this.selectedRoom);
    this._roomTypePicker.disabled = !has;
    let type = "room";
    let viewLen = DEFAULT_VIEW_LEN;
    if (has) {
      const meta = this._findRoomMeta(this._roomCentroidPx(this._selectedRoomNodeIds()));
      type = meta?.type ?? "room";
      viewLen = meta?.viewLen ?? DEFAULT_VIEW_LEN;
    }
    this._roomTypePicker.value = isBalconyType(type) ? "balcony" : "room";
    this.elRoomViewWrap?.classList.toggle("hidden", !(has && isBalconyType(type)));
    if (this.elRoomViewLen) this.elRoomViewLen.value = String(Math.round(viewLen));
  }

  _syncCornerSliderUi(value = Number(this.elCornerR?.value ?? 0)) {
    const v = Math.min(1.5, Math.max(0, value));
    this.elCornerRWrap?.style.setProperty("--range-pct", `${(v / 1.5) * 100}%`);
    if (this.elCornerRLabel) this.elCornerRLabel.textContent = `${v.toFixed(2)}m`;
  }

  /** Show corner-curve slider when a wall or floor corner is selected. */
  _syncCornerCtl() {
    const show =
      this.tool === "select" && (this.selected?.kind === "node" || this.selected?.kind === "floorCorner");
    this.elCornerCtl?.classList.toggle("hidden", !show);
    if (!show) return;
    const r =
      this.selected?.kind === "floorCorner"
        ? this._getFloorCornerFillet(this.selected.id)
        : Number(this.model?.nodes.get(String(this.selected.id))?.filletRadius) || 0;
    if (this.elCornerR) this.elCornerR.value = String(r);
    this._syncCornerSliderUi(r);
  }

  _buildGraph() {
    const m = this.model;
    if (!m) return null;
    // Re-join room blocks the user moved next to each other, then split any
    // T-junctions, so room loops + interior/exterior walls come out correct.
    this._weldCoincidentNodes();
    this._normalizeTopology();
    this._refreshWorldMapping();
    const mpp = m.metersPerPixel > 0 ? m.metersPerPixel : 0.02;

    // Only keep nodes referenced by at least one edge.
    const used = new Set();
    for (const e of m.edges) {
      used.add(e.a);
      used.add(e.b);
    }
    const nodeIdToGraph = new Map();
    const nodes = [];
    for (const [, n] of m.nodes) {
      if (!used.has(n.id)) continue;
      const gid = `N${n.id}`;
      nodeIdToGraph.set(n.id, gid);
      const abs = this._pxToGraphMeters(n.px, n.py);
      nodes.push({
        id: gid,
        px: Math.round(n.px),
        py: Math.round(n.py),
        x: abs.x,
        y: abs.y,
        ...(Number(n.filletRadius) > 0 ? { fillet_radius: Math.round(Number(n.filletRadius) * 1000) / 1000 } : {}),
      });
    }

    const edgeIdToGraph = new Map();
    const edges = [];
    for (const e of m.edges) {
      const a = nodeIdToGraph.get(e.a);
      const b = nodeIdToGraph.get(e.b);
      if (!a || !b) continue;
      const gid = `E${e.id}`;
      edgeIdToGraph.set(e.id, gid);
      edges.push({ id: gid, node_start: a, node_end: b });
    }

    const openings = [];
    for (const o of m.openings) {
      const eg = edgeIdToGraph.get(o.edge);
      if (!eg) continue;
      openings.push({
        id: o.sceneId ?? `O${o.id}`,
        edge_id: eg,
        type: o.type === "window" ? "window" : "door_single",
        distance_from_start_node_pixels: Math.max(0, Math.round(o.t)),
        width_pixels: Math.max(1, Math.round(o.w)),
        ...(o.type === "window"
          ? { sill_meters: Number(o.sill) || 0.9, curtain: windowHasCurtain(o) }
          : { hinge: normHinge(o.hinge), swing: normSwing(o.swing) }),
      });
    }

    const rooms = this._deriveRooms(edgeIdToGraph);

    // Lights + switches. Each is a free point (not bound to an edge). Group them
    // by the room face they sit in so a switch toggles the lights in its room.
    const lights = (m.lights ?? []).map((l) => {
      const abs = this._pxToGraphMeters(l.px, l.py);
      return {
        id: `L${l.id}`,
        fixture: l.fixture || DEFAULT_LIGHT,
        group: this._groupAt(l.px, l.py),
        px: Math.round(l.px),
        py: Math.round(l.py),
        x: abs.x,
        y: abs.y,
      };
    });
    const switches = (m.switches ?? []).map((s) => {
      this._ensureSwitchPlacement(s);
      const abs = this._pxToGraphMeters(s.px, s.py);
      const wn = this._pxNormalToWorld(s.nx, s.ny, s.px, s.py);
      const round3 = (v) => Math.round(v * 1000) / 1000;
      return {
        id: `S${s.id}`,
        on: s.on !== false,
        group: this._groupAt(s.px, s.py, s.edge),
        px: Math.round(s.px),
        py: Math.round(s.py),
        x: abs.x,
        y: abs.y,
        nx: round3(s.nx),
        ny: round3(s.ny),
        normal: [round3(wn.x), round3(wn.z)],
      };
    });

    return {
      nodes,
      edges,
      openings,
      rooms,
      lights,
      switches,
      metersPerPixel: mpp,
      height: m.height || 2.7,
      ...(m.floor
        ? (() => {
            this._ensureFloorCorners(m);
            this._syncFloorRectFromCorners(m);
            const c = m.floor.corners ?? {};
            const verts = floorVertexRing(m.floor);
            const corner = (id, fbPx, fbPy) => ({
              px: round2(c[id]?.px ?? fbPx),
              py: round2(c[id]?.py ?? fbPy),
            });
            return {
              floor_px: { x0: m.floor.x0, y0: m.floor.y0, x1: m.floor.x1, y1: m.floor.y1 },
              floor_corners: {
                nw: corner("nw", m.floor.x0, m.floor.y0),
                ne: corner("ne", m.floor.x1, m.floor.y0),
                se: corner("se", m.floor.x1, m.floor.y1),
                sw: corner("sw", m.floor.x0, m.floor.y1),
              },
              floor_vertices: verts.map((v) => ({ id: v.id, px: round2(v.px), py: round2(v.py) })),
              floor_fillet: { ...(m.floor.fillet ?? { nw: 0, ne: 0, se: 0, sw: 0 }) },
              floor_polygon: floorPolygonMeters(m.floor, (px, py) => this._pxToWorld(px, py), mpp),
              envelope: this._floorBoundsMeters(m),
              planNaturalW: m.naturalW,
              planNaturalH: m.naturalH,
            };
          })()
        : {}),
    };
  }

  /** True when walls/nodes/edges or lights/switches changed since the editor opened. */
  _structureDirty() {
    return this._wallsDirty() || this._lightsDirty();
  }

  /** Push final furniture poses to the live scene + server (no room rebuild). */
  _commitFurnitureTransforms() {
    for (const f of this.model?.furniture ?? []) {
      const payload = this._applyFurnitureToScene(f);
      if (payload) this._sendFurnitureNet(payload);
    }
  }

  _canSyncGraph(graph) {
    return Boolean(graph && graph.nodes?.length >= 3 && graph.edges?.length >= 3);
  }

  /** True when the user cleared all traced walls (valid empty slate). */
  _graphIsEmpty(graph) {
    return Boolean(graph && (graph.edges?.length ?? 0) === 0 && (graph.nodes?.length ?? 0) === 0);
  }

  /** Mirror the edited graph into browser localStorage (orchestrator offline fallback). */
  _persistGraphToLocalStore(graph, builtOpenings = null) {
    if (!graph || !Array.isArray(graph.nodes) || !Array.isArray(graph.edges)) return false;
    try {
      const saved = loadSavedPlan() ?? { spec: {} };
      const empty = (graph.nodes?.length ?? 0) === 0 && (graph.edges?.length ?? 0) === 0;
      const envW = Number(graph.envelope?.width);
      const envD = Number(graph.envelope?.depth);
      const openingsPayload = {};
      if (Array.isArray(builtOpenings) && builtOpenings.length) {
        const byId = new Map((saved.spec?.builtOpenings ?? []).map((o) => [o.id, o]));
        for (const op of builtOpenings) {
          if (!op?.id) continue;
          byId.set(op.id, {
            id: op.id,
            type: op.type === "window" ? "window" : "door",
            wall: op.wall,
            center: op.center,
            width: op.width,
            height: op.height,
            ...(op.type === "window"
              ? { sill: op.sill ?? 0.9, curtain: op.curtain !== false }
              : { hinge: op.hinge ?? "left", swing: op.swing ?? "in" }),
          });
        }
        openingsPayload.builtOpenings = [...byId.values()];
      }
      saveSavedPlan({
        ...saved,
        spec: {
          ...saved.spec,
          graph: { ...graph, height: Number(graph.height) || saved.spec?.height || 2.7 },
          height: Number(graph.height) || saved.spec?.height || 2.7,
          layoutMode: empty ? "cleared" : "trace",
          ...(empty ? { rooms: [], builtOpenings: [], openings: [] } : {}),
          ...(empty && envW > 0 && envD > 0 ? { width: envW, depth: envD } : {}),
          ...openingsPayload,
        },
        source: saved.source ?? "plan-editor",
      });
      return true;
    } catch {
      return false;
    }
  }

  async _persistGraphRemote(graph, patch = { upsert: [], remove: [] }) {
    if (!this.planAnalyzer) return true;
    const ok = await this.planAnalyzer.patchTopology({
      upsert: patch.upsert ?? [],
      remove: patch.remove ?? [],
      graph,
    });
    if (ok) {
      this._persistGraphToLocalStore(graph);
      return true;
    }
    if (this._persistGraphToLocalStore(graph)) {
      this._status("Saved locally — start the orchestrator to persist to disk.", false);
      return true;
    }
    return false;
  }

  _revertEditorSession() {
    this._restoreSceneSnapshot();
    if (this._sessionModelBaseline) this._restoreModel(this._sessionModelBaseline);
  }

  async _apply() {
    if (this._applying || !this.open) return;
    this._applying = true;
    this._setPlanEditorLoading(true);
    this._status("Applying…");
    let topologyCommitted = false;
    try {
      const graph = this._buildGraph();
      const wallsDirty = this._wallsDirty();
      const lightsDirty = this._lightsDirty();
      const openingsDirty = this._openingsDirty();
      const empty = this._graphIsEmpty(graph);

      if ((wallsDirty || lightsDirty) && !this._canSyncGraph(graph) && !empty) {
        this._status("Draw at least a closed shape (3+ walls) before applying.", true);
        return;
      }

      if (wallsDirty || (empty && this._isFloorOnlyMode())) {
        const ok = await this._persistTopology();
        if (!ok) {
          this._revertEditorSession();
          this._status("Could not save structure changes.", true);
          return;
        }
        topologyCommitted = true;
      } else if (lightsDirty) {
        const ok = await this._persistLightsAndSwitchesOnly();
        if (!ok) {
          this._revertEditorSession();
          this._status("Could not save light changes.", true);
          return;
        }
      }
      if (this._roomMetaDirty()) {
        this._syncBalconyMetaLive();
        if (this._canSyncGraph(graph) || empty) {
          const ok = await this._persistGraphRemote(graph);
          if (!ok && !topologyCommitted) {
            this._status("Balcony applied in 3D — could not save to disk.", true);
          }
        }
        this._roomMetaBaseline = JSON.stringify(this._roomMeta ?? []);
      }
      if (openingsDirty || empty) {
        const ok = await this._persistOpenings(graph);
        if (!ok) {
          if (!topologyCommitted) this._revertEditorSession();
          else this._status("Structure saved — could not sync openings to disk.", true);
          return;
        }
      }

      this._commitFurnitureTransforms();
      this._topologyBaseline = this._wallsStructuralFingerprint();
      this._lightsBaseline = this._lightsFingerprint();
      this._openingsBaseline = this._openingsSnapshot();
      this._sessionModelBaseline = this._serializeModel();
      this._captureSceneSnapshot();
      this.closeEditor({ applied: true });
    } catch (err) {
      console.error("[plan-editor] apply failed", err);
      this._status(err?.message || "Apply failed.", true);
    } finally {
      this._applying = false;
      if (this.open) this._setPlanEditorLoading(false);
    }
  }

  destroy() {
    this._cancelFurniturePlace(false);
    if (this._boundQeKeyDown) window.removeEventListener("keydown", this._boundQeKeyDown, true);
    if (this._boundQeKeyUp) window.removeEventListener("keyup", this._boundQeKeyUp, true);
    if (this._onDocClickClearMenu) document.removeEventListener("click", this._onDocClickClearMenu);
    this.root?.remove();
  }
}

export default PlanEditor;
